/* tslint:disable */
/* eslint-disable */
/**
 * TaskHub Api 1.0
 * Task Hub provides federated task management platform for use across ServiceTitan
 *
 * The version of the OpenAPI document: 1.0
 * Contact: #task-hub
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction, replaceWithSerializableTypeIfNeeded } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Represents a Task Type permitted for creation on Boards of a given Board Type.
 */
export interface AllowedTaskType {
    /**
     * The unique identifier for the Task Type.
     */
    'taskTypeId': string;
}
/**
 * Represents an Attachment download.
 */
export interface AttachmentDownload {
    /**
     * The link to download the Attachment.
     */
    'uri': string;
}
/**
 * Represents a created Attachment
 */
export interface AttachmentResponse {
    /**
     * Unique identifier for the Attachment
     */
    'attachmentId': string;
    /**
     * Unique identifier for the Workspace the Attachment belongs to
     */
    'workspaceId': string;
    /**
     * Attachment\'s current status. Possible values are valid
     */
    'status': string;
}
/**
 * Data object for info related to the user context.
 */
export interface AuthInfo {
    /**
     * The user\'s titan GUID.
     */
    'titanGuid': string;
    /**
     * The user\'s tenant ID.
     */
    'tenantId': number;
    /**
     * The user\'s org ID.
     */
    'orgId': string;
    /**
     * The user\'s username.
     */
    'userName'?: string | null;
    /**
     * The user\'s display name.
     */
    'displayName'?: string | null;
    /**
     * The org\'s cohort tier (`Lowest`, `Low`, `Medium`, `VIP`), or null when it can\'t be resolved. Send it as the `cohortTier` attribute on the LaunchDarkly `org` context.
     */
    'cohortTier'?: string | null;
}
/**
 * Configuration settings for a Board, including field overrides.
 */
export interface BoardConfiguration {
    /**
     * A set of field overrides for the Board.
     */
    'fieldOverrides': Set<BoardConfigurationFieldOverridesInner>;
    /**
     * A set of task type overrides for the Board.
     */
    'taskTypeOverrides'?: Set<TaskTypeOverride>;
    /**
     * When true, task dependencies are soft-blocking — tasks can still change status even when blocked.
     */
    'softBlockingDependencies'?: boolean | null;
}
/**
 * @type BoardConfigurationFieldOverridesInner
 * Represents the schema for a custom field in a Task Type
 */
export type BoardConfigurationFieldOverridesInner = ComputedFieldOverride | DropdownFieldOverride | ExternalEntityPropertyFieldOverride | ExternalEntityTypeFieldOverride | FieldOverride | StatusFieldOverride;

/**
 * A request to retrieve metrics for tasks in a board based on specified filters.
 */
export interface BoardMetricsRequest {
    /**
     * Collection of filtering rules to apply when calculating metrics. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<TaskFilter> | null;
}
/**
 * Represents a Board, container for Tasks
 */
export interface BoardResponse {
    /**
     * Unique identifier for the Board
     */
    'boardId'?: string;
    /**
     * Unique identifier for the Workspace the Board belongs to
     */
    'workspaceId'?: string;
    /**
     * Namespace for the Board
     */
    'namespace': string;
    /**
     * Indicates if the Board is scoped
     */
    'scoped'?: boolean;
    /**
     * A list of Labels associated with this Board
     */
    'labels'?: Array<LabelReference>;
    /**
     * A color associated with this Board
     */
    'color'?: string | null;
    /**
     * A photo associated with this Board
     */
    'photo'?: Photo | null;
    /**
     * Represents a list of External Entities associated with this Board
     */
    'externalEntities'?: Array<ExternalEntityReference>;
    /**
     * Represents a list of correlation ids associated with this Board.
     */
    'correlationIds'?: Set<string>;
    /**
     * The optional Key of the Board.
     */
    'boardKey'?: string | null;
    /**
     * Reference to a global board type that defines shared configuration.
     */
    'boardTypeId': string;
    /**
     * The configuration for the Board, including field overrides.
     */
    'configuration': BoardConfiguration;
    /**
     * The Task Types permitted for creation on this Board, resolved from its Board Type. An empty list means no restriction -- any Task Type may be created. It does not mean that no Task Types are allowed. Entries are returned as configured and are not checked against existing Task Types, so a listed Task Type that does not exist or is disabled is still rejected on creation.
     */
    'allowedTaskTypes'?: Set<AllowedTaskType>;
}
/**
 * Represents a paginated response containing a page of items
 */
export interface BoardResponseIPaginatedResponse {
    /**
     * The zero-based page index
     */
    'pageIndex'?: number;
    /**
     * Need to return page size as the server may disallow passing an arbitrarily large value
     */
    'pageSize'?: number;
    /**
     * Whether there is a previous page
     */
    'hasPreviousPage'?: boolean;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<BoardResponse>;
    /**
     * Total count of items across pages. Only returned when requested.
     */
    'totalCount'?: number | null;
}
/**
 * A filter query to search Tasks in a board, paginated by continuation token. Suited to iterating every Task that matches a query; use ServiceTitan.TaskHub.Client.Contracts.BoardTasksQuery to jump to an arbitrary page.
 */
export interface BoardTasksContinuationQuery {
    /**
     * Collection of filtering rules. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<TaskFilter> | null;
    /**
     * The opaque token returned by the previous page\'s response. Omit to request the first page. A token is only valid with the same filters, sort, and search term that produced it; otherwise the request is rejected. Formula filters are re-evaluated on every request, so prefer literal values when walking every page.
     */
    'continuationToken'?: string | null;
    /**
     * The size of the page of Tasks to return. Every page but the last contains exactly this many Tasks.
     */
    'pageSize'?: number | null;
    /**
     * Details on how the query should sort results
     */
    'sort'?: TaskQuerySort | null;
    /**
     * Optional secondary sorting criteria for the query. Requires DB support for this feature. Multiple sorting is only supported for \'sys.due_date\' / \'sys.created_at\' fields (both ASC or both DESC) and for \'sys.priority\' DESC / \'sys.created_at\' ASC.
     */
    'secondarySort'?: TaskQuerySort | null;
    /**
     * Optional free-text search term. When provided, results are narrowed to tasks whose searchable field content matches the term.
     */
    'searchTerm'?: string | null;
}
/**
 * A filter query to search and filter Tasks count by Filters.
 */
export interface BoardTasksCountQuery {
    /**
     * Collection of filtering rules. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<TaskFilter> | null;
    /**
     * Optional free-text search term. When provided, the count is narrowed to tasks whose searchable field content matches the term.
     */
    'searchTerm'?: string | null;
}
/**
 * A filter query to search and filter Tasks by Org ID, Status Definition Ids, and associated External Entities.
 */
export interface BoardTasksQuery {
    /**
     * Collection of filtering rules. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<TaskFilter> | null;
    /**
     * Whether to include to the total count in the response
     */
    'includeTotalCount'?: boolean;
    /**
     * The index of the page of Tasks to return.
     */
    'pageIndex'?: number;
    /**
     * The size of the page of Tasks to return.
     */
    'pageSize'?: number | null;
    /**
     * Details on how the query should sort results
     */
    'sort'?: TaskQuerySort | null;
    /**
     * Optional secondary sorting criteria for the query. Requires DB support for this feature. Multiple sorting is only supported for \'sys.due_date\' / \'sys.created_at\' fields (both ASC or both DESC) and for \'sys.priority\' DESC / \'sys.created_at\' ASC.
     */
    'secondarySort'?: TaskQuerySort | null;
    /**
     * Optional free-text search term. When provided, results are narrowed to tasks whose searchable field content matches the term.
     */
    'searchTerm'?: string | null;
}
/**
 * Request for the top-task-per-group search endpoint. Returns one ServiceTitan.TaskHub.Client.Contracts.GroupedTaskResult per ServiceTitan.TaskHub.Client.Contracts.TaskGroupBy.Values entry — the single top task in that group, or null when no task matches.
 */
export interface BoardTasksTopPerGroupQuery {
    /**
     * Collection of filtering rules applied alongside filtering based on the supplied group. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<TaskFilter> | null;
    /**
     * Sort direction and field used to select the top task per group. Required.
     */
    'sort': TaskQuerySort;
    /**
     * Required group definition.
     */
    'groupBy': TaskGroupBy;
}
/**
 * Represents a Board Type, a metadata entity that defines shared configuration for Boards
 */
export interface BoardTypeResponse {
    /**
     * Unique identifier for the Workspace the Board Type belongs to
     */
    'workspaceId'?: string;
    /**
     * Unique identifier for the Board Type
     */
    'id': string;
    /**
     * Namespace for the Board Type
     */
    'namespace': string;
    /**
     * The name of the Board Type
     */
    'name': string;
    /**
     * Indicates whether the Board Type is disabled
     */
    'disabled'?: boolean;
    /**
     * Whether boards inheriting from this Board Type are scoped (one per workspace).
     */
    'scoped'?: boolean;
    /**
     * When true, accessing an unresolved boardKey no longer auto-provisions a new board instance for this Board Type — instances must be created explicitly.
     */
    'disableAutoProvision'?: boolean;
    /**
     * When true, a board instance\'s own configuration is not merged in — the Board Type\'s configuration is authoritative.
     */
    'isLocked'?: boolean;
    /**
     * States whether this Board Type\'s tasks belong in a workspace-wide, board-less query result: `false` (the default) means they participate, `true` means they are excluded unless the query names the board. Retroactive, not an access control, and does not affect a query that names the board.
     */
    'includedOnlyWhenRequested'?: boolean;
    /**
     * The optional configuration for the Board Type, including field and task type overrides.
     */
    'configuration'?: BoardConfiguration | null;
    /**
     * The Task Types permitted for creation on Boards of this Board Type. An empty list means unrestricted -- any Task Type may be used.
     */
    'allowedTaskTypes'?: Set<AllowedTaskType>;
}
/**
 * Represents a request to bulk archive a set of Tasks on a single board.
 */
export interface BulkArchiveTasksRequest {
    /**
     * Collection of Task IDs to archive (maximum 50).
     */
    'taskIds': Array<string>;
}
/**
 * A single task update within a bulk update request.
 */
export interface BulkUpdateIndividualTaskItem {
    /**
     * The unique identifier for the Task to update.
     */
    'taskId': string;
    /**
     * The ETag of the task for optimistic concurrency control.
     */
    'eTag': string;
    /**
     * The update details to apply to this task.
     */
    'update': UpdateTaskDetailsRequest;
}
/**
 * Request to bulk update multiple tasks, each with their own update properties.
 */
export interface BulkUpdateIndividualTasksRequest {
    /**
     * Collection of task updates (maximum 50). Each item specifies a task ID, ETag, and update payload.
     */
    'tasks': Array<BulkUpdateIndividualTaskItem>;
}
/**
 * Represents a request to bulk update multiple Tasks.
 */
export interface BulkUpdateTasksRequest {
    /**
     * Collection of Task IDs to update (maximum 50).
     */
    'taskIds': Array<string>;
    /**
     * The update request details to apply to all tasks.
     */
    'update': UpdateTaskDetailsRequest;
}
/**
 * Represents a field of type \"checkbox\".
 */
export interface CheckboxField extends Field {
}
/**
 * Represents a checkbox field value.
 */
export interface CheckboxFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': boolean;
}
/**
 * Represents a Checklist with a list of items
 */
export interface Checklist {
    /**
     * Unique identifier of the Checklist
     */
    'id': string;
    /**
     * Name of the Checklist
     */
    'name': string;
    /**
     * Checklist items
     */
    'items': Array<ChecklistItem>;
}
/**
 * A single item in a Checklist
 */
export interface ChecklistItem {
    /**
     * Unique identifier of the Checklist item
     */
    'id': string;
    /**
     * The text for the Checklist item
     */
    'description': string;
    /**
     * If the item is marked as complete
     */
    'isChecked': boolean;
}
/**
 * Represents a field of type \"computed\", used to display fields derived from other fields.
 */
export interface ComputedField extends Field {
    /**
     * The json schema used to conform computed template output.
     */
    'outputSchema': JsonSchema;
    /**
     * The template used to compute the fields value.
     */
    'template': string;
    /**
     * The evaluation behavior of the computed field.
     */
    'evaluationMode': ComputedFieldEvaluationModeEnum;
}

export const ComputedFieldEvaluationModeEnum = {
    Runtime: 'Runtime',
    Stored: 'Stored',
} as const;

export type ComputedFieldEvaluationModeEnum = typeof ComputedFieldEvaluationModeEnum[keyof typeof ComputedFieldEvaluationModeEnum];

/**
 * Represents the configuration of a Computed field for a Task Type.
 */
export interface ComputedFieldOverride extends FieldOverride {
    /**
     * Gets or sets a value that, if set, overrides the template defined on the Field.
     */
    'template'?: string | null;
}
/**
 * Represents a computed value.
 */
export interface ComputedFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': any;
}
/**
 * Represents a Context Configuration
 */
export interface ContextConfiguration {
    /**
     * Indicates whether the Context Configuration is disabled
     */
    'disabled': boolean;
    /**
     * Board identifier the Context Configuration is associated with (single-board surface). Optional and mutually exclusive with BoardKeys; carries cardinality only - see BoardMode for how the board set is determined, including what \"neither BoardKey nor BoardKeys\" means.
     */
    'boardKey'?: string | null;
    /**
     * Known set of board identifiers the Context Configuration is scoped to (multi-board surface). Optional and mutually exclusive with the single-board BoardKey; carries cardinality only - see BoardMode for how the board set is determined, including what \"neither BoardKey nor BoardKeys\" means. Setting both BoardKey and BoardKeys, or an empty BoardKeys set, is rejected at upsert.
     */
    'boardKeys'?: Array<string> | null;
    /**
     * How the board set is determined: \"explicit\" (BoardKey/BoardKeys), \"dynamic\" (a board supplied by the host at load, no keys on the config), or \"all_boards\" (no board set at all, the workspace-wide query). Optional - when omitted, resolves to \"explicit\" if keys are present, otherwise \"dynamic\". \"all_boards\" is never inferred; it is reachable only by stating it.
     */
    'boardMode'?: string | null;
    /**
     * Task Types associated with the Context Configuration
     */
    'taskTypeIds': Array<string>;
    /**
     * Filters applied to all pages in the context configuration
     */
    'filters': Array<TaskFilter>;
    /**
     * Context Configuration identifier, unique across the Workspace + Namespace
     */
    'id': string;
    /**
     * Workspace identifier the Context Configuration is associated with
     */
    'workspaceId'?: string;
    /**
     * Context Configuration title. Optional - when null, the context configuration header is hidden in the UI.
     */
    'title'?: string | null;
    /**
     * User-specific filters with fully resolved display names
     */
    'userFilters': UserContextConfigurationFilter;
    /**
     * Pages within the context configuration
     */
    'pages': Array<ContextConfigurationPagesInner>;
}
/**
 * Represents a paginated response containing a page of items
 */
export interface ContextConfigurationIPaginatedResponse {
    /**
     * The zero-based page index
     */
    'pageIndex'?: number;
    /**
     * Need to return page size as the server may disallow passing an arbitrarily large value
     */
    'pageSize'?: number;
    /**
     * Whether there is a previous page
     */
    'hasPreviousPage'?: boolean;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<ContextConfiguration>;
    /**
     * Total count of items across pages. Only returned when requested.
     */
    'totalCount'?: number | null;
}
/**
 * Configuration for mobile list items with status-specific layouts
 */
export interface ContextConfigurationMobileListItem {
    /**
     * Default layout applied when a status-specific layout is not defined
     */
    'default': MobileListItemLayout;
    /**
     * Status-specific layouts, keyed by status string (e.g., \"to_do\", \"in_progress\")
     */
    'statusLayouts'?: { [key: string]: MobileListItemLayout; };
}
/**
 * A page within a Context Configuration, which can be Grid, MasterDetail, or MobileList layout
 */
export interface ContextConfigurationPage {
    'type': string;
    /**
     * Page identifier, unique within the Context Configuration
     */
    'id': string;
    /**
     * Title of the page
     */
    'title': string;
    /**
     * Filters applied to this specific page
     */
    'filters': Array<TaskFilter>;
    /**
     * Metrics displayed on this page
     */
    'metrics'?: ContextConfigurationPageMetrics | null;
    /**
     * Whether free-text search is enabled for this page. When true, the frontend renders a search input on the grid/list and passes the typed term as the task search query\'s free-text term. When false (the default), no search input is shown and no free-text term is included in queries.
     */
    'searchEnabled'?: boolean;
    /**
     * User-selectable filters with fully resolved display names
     */
    'userFilters'?: UserContextConfigurationFilter | null;
}
/**
 * Default metric to apply when the page is first loaded
 */
export interface ContextConfigurationPageDefaultMetric {
    /**
     * Metric identifier
     */
    'metricId': string;
    /**
     * Metric value identifier
     */
    'metricValueId': string;
}
/**
 * Configuration for a grid layout, including responsive widths
 */
export interface ContextConfigurationPageGrid {
    /**
     * Columns within the grid
     */
    'columns': Array<ContextConfigurationPageGridColumn>;
    /**
     * Sorting configurations for the grid
     */
    'sorting': ContextConfigurationPageGridSorting;
    /**
     * Whether this grid supports bulk edit mode (checkbox column and row selection). Defaults to false when absent. Independent of row-level flags at storage time; the frontend only enters bulk edit mode when this is true.
     */
    'allowsBulkEdit'?: boolean;
}
/**
 * Configuration for a column within a grid layout
 */
export interface ContextConfigurationPageGridColumn {
    /**
     * Field associated with the column
     */
    'rows': Array<ContextConfigurationPageGridRow>;
    /**
     * Whether this column is hidden by default in the table. Defaults to false when absent. Users can reveal it via the column customizer; this is a per-board config default.
     */
    'hiddenByDefault'?: boolean;
    /**
     * Display name of the column. Always resolved from the first row field\'s display name if not explicitly set.
     */
    'title': string;
}
/**
 * Configuration for a column within a grid layout used in upsert requests where the title is optional
 */
export interface ContextConfigurationPageGridColumnInput {
    /**
     * Field associated with the column
     */
    'rows': Array<ContextConfigurationPageGridRow>;
    /**
     * Whether this column is hidden by default in the table. Defaults to false when absent. Users can reveal it via the column customizer; this is a per-board config default.
     */
    'hiddenByDefault'?: boolean;
    /**
     * Display name of the column. When null, defaults to the first row field\'s display name.
     */
    'title'?: string | null;
}
/**
 * Configuration for a grid layout used in upsert requests
 */
export interface ContextConfigurationPageGridInput {
    /**
     * Columns within the grid
     */
    'columns': Array<ContextConfigurationPageGridColumnInput>;
    /**
     * Sorting configurations for the grid
     */
    'sorting': ContextConfigurationPageGridSorting;
    /**
     * Whether this grid supports bulk edit mode (checkbox column and row selection). Defaults to false when absent. Independent of row-level flags at storage time; the frontend only enters bulk edit mode when this is true.
     */
    'allowsBulkEdit'?: boolean;
}
/**
 * Configuration for a row within a grid column
 */
export interface ContextConfigurationPageGridRow {
    /**
     * Field associated with the row
     */
    'fieldId': string;
    /**
     * Optional display type for the row (e.g., \"Date\", \"DateTime\", \"Link\", \"Text\")
     */
    'displayType'?: string | null;
    /**
     * Optional display value for the row (e.g., \"id\", \"name\")
     */
    'displayValue'?: string | null;
    /**
     * Whether this field exposes a bulk editor when the grid is in bulk edit mode and the row is selected. Defaults to false when absent. Requires ServiceTitan.TaskHub.Client.Contracts.ContextConfigurations.ContextConfigurationPageGrid.AllowsBulkEdit to be true at runtime; row flags are persisted even when the grid flag is false.
     */
    'allowsBulkEdit'?: boolean;
}
/**
 * Configuration for sorting within a grid layout
 */
export interface ContextConfigurationPageGridSorting {
    /**
     * Default sorting configuration
     */
    'default': FieldSorting;
    /**
     * Sortable fields available in the grid
     */
    'fields': Array<string>;
}
/**
 * A page within a Context Configuration used in upsert requests
 */
export interface ContextConfigurationPageInput {
    'type': string;
    /**
     * Page identifier, unique within the Context Configuration
     */
    'id': string;
    /**
     * Title of the page
     */
    'title': string;
    /**
     * Filters applied to this specific page
     */
    'filters': Array<TaskFilter>;
    /**
     * Metrics displayed on this page
     */
    'metrics'?: ContextConfigurationPageMetrics | null;
    /**
     * Whether free-text search is enabled for this page. When true, the frontend renders a search input on the grid/list and passes the typed term as the task search query\'s free-text term. When false (the default), no search input is shown and no free-text term is included in queries.
     */
    'searchEnabled'?: boolean;
    /**
     * Page-scoped user filters (all / quickAccess / initial) layered on top of the context configuration\'s root ServiceTitan.TaskHub.Client.Contracts.ContextConfigurations.UpsertContextConfigurationRequest.UserFilters. Optional - omit when the page adds no filters of its own.
     */
    'userFilters'?: UpsertUserContextConfigurationFilter | null;
}
/**
 * Configuration for a metric displayed on a Context Configuration page
 */
export interface ContextConfigurationPageMetric {
    /**
     * Metric identifier, unique within the Context Configuration
     */
    'id': string;
    /**
     * Default width of the metric in number of columns (out of 12)
     */
    'defaultWidth': number;
    /**
     * Kind of metric (e.g., \"metrics\", \"stages\")
     */
    'kind': string;
    'widths': ContextConfigurationPageMetricWidths;
}
/**
 * Widths of the metric for different screen resolutions
 */
export interface ContextConfigurationPageMetricWidths {
    'Mobile'?: number;
    'Tablet'?: number;
    'Desktop'?: number;
    'DesktopXl'?: number;
    'DesktopXxl'?: number;
}
/**
 * Configuration for metrics displayed on a Context Configuration page
 */
export interface ContextConfigurationPageMetrics {
    /**
     * All metrics displayed on the page
     */
    'all'?: Array<ContextConfigurationPageMetric>;
    /**
     * Optional default metric to apply when the page is first loaded
     */
    'default'?: ContextConfigurationPageDefaultMetric | null;
}
/**
 * @type ContextConfigurationPagesInner
 * A page within a Context Configuration, which can be Grid, MasterDetail, or MobileList layout
 */
export type ContextConfigurationPagesInner = ContextConfigurationPage | GridConfigurationPage | MasterDetailConfigurationPage | MobileListConfigurationPage;

/**
 * A Comm Hub external entity — includes the title/subtitle and correlation metadata that the Comm Hub MFE needs to display and correlate conversations.
 */
export interface ConversationExternalEntity {
    /**
     * Entity ID.
     */
    'id': string;
    /**
     * Entity type (e.g. `th.task`, `jpm.job`).
     */
    'type': string;
    /**
     * Whether this entity is correlated to the conversation.
     */
    'correlate'?: boolean;
    /**
     * Whether this entity provides contextual information for the conversation.
     */
    'isContextual'?: boolean;
}
/**
 * Represents a request to create a new Board
 */
export interface CreateBoardRequest {
    /**
     * Namespace for the Board
     */
    'namespace': string;
    /**
     * Indicates if the Board is scoped
     */
    'scoped'?: boolean;
    /**
     * Reference to a global board type that defines shared configuration.
     */
    'boardTypeId': string;
    /**
     * Optional organization ID to associate with this board. When specified, the board will be associated with the given organization. When null, the board uses the workspace\'s organization (backward compatible).
     */
    'boardOrgId'?: string | null;
    /**
     * A list of Labels associated with this Board
     */
    'labels'?: Set<string>;
    /**
     * A color associated with this Board
     */
    'color'?: string | null;
    /**
     * A photo associated with this Board
     */
    'photo'?: Photo | null;
    /**
     * Represents a list of External Entities that should be associated with this Board
     */
    'externalEntities'?: Array<ExternalEntityReference>;
    /**
     * Represents a list of correlation ids that should be associated with this Board.
     */
    'correlationIds'?: Set<string>;
}
/**
 * Represents a request to add a new Attachment to a Task.
 */
export interface CreateTaskAttachmentRequest {
    /**
     * ID of the Attachment upload.
     */
    'attachmentId': string;
    /**
     * File name of the Attachment.
     */
    'fileName': string;
}
/**
 * Represents a request to create a new Task.
 */
export interface CreateTaskRequest {
    /**
     * Gets or sets the field values associated with the Task.
     */
    'fieldValues'?: { [key: string]: NullableFieldValue; };
    /**
     * Gets or sets the labels associated with the Task.
     */
    'labels'?: Set<string>;
    /**
     * Gets or sets the external entities associated with the Task.
     */
    'externalEntities'?: Array<ExternalEntityReference>;
    /**
     * Gets or sets the relations of the Task with another Tasks.
     */
    'relations'?: Set<Relation>;
    /**
     * Gets or sets the checklists associated with the Task.
     */
    'checklists'?: Array<Checklist>;
    /**
     * Gets or sets the attachments associated with the Task.
     */
    'attachments'?: Array<TaskAttachment>;
    /**
     * Gets or sets the correlation IDs associated with the Task. Must be unique within a board.
     */
    'correlationIds'?: Set<string>;
}
/**
 * Represents a request to create new Workspace, correlated to OrgId
 */
export interface CreateWorkspaceRequest {
    /**
     * Represents a list of External Entities that should be associated with this Workspace
     */
    'externalEntities'?: Array<ExternalEntityReference>;
}
/**
 * Represents a field in the data platform schema.
 */
export interface DataPlatformField {
    /**
     * The name of the source column.
     */
    'source_column_name': string;
    /**
     * The name of the target column, when it differs from the source column name.
     */
    'target_column_name'?: string | null;
    /**
     * The data type of the source column.
     */
    'source_column_data_type': string;
    /**
     * The maximum length of the source column data.
     */
    'source_column_data_maximum_length'?: number | null;
    /**
     * The precision of the source column data.
     */
    'source_column_data_precision'?: number | null;
    /**
     * The scale of the source column data.
     */
    'source_column_data_scale'?: number | null;
    /**
     * Whether the source column is nullable.
     */
    'source_column_is_nullable': string;
    /**
     * The key order for primary key columns.
     */
    'key_order'?: number | null;
    /**
     * The ordinal position of the column.
     */
    'ordinal_position': number;
    /**
     * The default expression for the source column.
     */
    'source_column_default_expression'?: string | null;
}
/**
 * Represents a data platform schema that describes the structure of a table.
 */
export interface DataPlatformSchema {
    /**
     * The name of the source table.
     */
    'source_table_name': string;
    /**
     * The fields in the schema.
     */
    'fields': Array<DataPlatformField>;
    /**
     * The source service name.
     */
    'source_service': string;
    /**
     * The source database software.
     */
    'source_database_software': string;
    /**
     * The source server name.
     */
    'source_server': string;
    /**
     * The source database name.
     */
    'source_database': string;
    /**
     * The source schema name.
     */
    'source_schema': string;
}
/**
 * Represents a field of type \"date\".
 */
export interface DateField extends Field {
}
/**
 * Represents a date field value.
 */
export interface DateFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': string;
}
/**
 * Configuration for the detail view in a Master-Detail layout
 */
export interface DetailsConfiguration {
    /**
     * Configuration for the header section of the detail view
     */
    'header': HeaderConfiguration;
}
/**
 * Represents a field of type \"dropdown\", allowing selection from a list of options.
 */
export interface DropdownField extends Field {
    /**
     * Gets or sets the list of selectable options for the dropdown field.
     */
    'valueOptions'?: Array<FieldValueOption>;
    /**
     * Gets or sets a value indicating whether multiple options can be selected for this dropdown field.
     */
    'allowMultiChoice'?: boolean;
}
/**
 * Represents the configuration of a Dropdown field for a Task Type.
 */
export interface DropdownFieldOverride extends FieldOverride {
    /**
     * Gets or sets a value indicating whether multiple options can be selected for this dropdown field.
     */
    'allowMultiChoice'?: boolean | null;
    /**
     * The list of selectable options for the dropdown field.
     */
    'valueOptions'?: Array<FieldValueOption> | null;
}
/**
 * Represents additional details of a specific field of \"dropdown\" type.
 */
export interface DropdownFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': Array<DropdownFieldValueItem>;
}
/**
 * Represents a selected item value in a dropdown field.
 */
export interface DropdownFieldValueItem {
    /**
     * The field value option identifier.
     */
    'id'?: string;
    /**
     * The field value option name.
     */
    'displayName'?: string;
    /**
     * The field value option sort order.
     */
    'sortOrder'?: number | null;
    /**
     * The color associated with this field value option (e.g., \"#FF0000\" or \"#F00\")
     */
    'color'?: string | null;
}
/**
 * Response for the ensure-conversation endpoint. Both Desktop and Mobile FE clients use this before rendering the Comm Hub MFE.
 */
export interface EnsureConversationResponse {
    /**
     * The Comm Hub conversation ID. An empty string indicates that Comm Hub is unavailable in this environment (graceful degradation). Callers must check for an empty value before passing this ID to the Comm Hub MFE.
     */
    'conversationId': string;
    /**
     * Formatted conversation name derived from the task and its linked job or project.
     */
    'name': string;
    /**
     * The external entities associated with this conversation. Pass these directly into the Comm Hub MFE (and the FMA app\'s go-to-comms call).
     */
    'externalEntities': Array<ConversationExternalEntity>;
}
/**
 * Request to execute a task template.
 */
export interface ExecuteTaskTemplateRequest {
    /**
     * Gets or sets the variables to substitute in the template. Variables not included will use their defaultValue if available.
     */
    'variables'?: { [key: string]: any; };
    /**
     * Gets or sets the board key where tasks should be created. Required if template doesn\'t specify a board.
     */
    'boardKey'?: string | null;
    /**
     * Gets or sets an optional idempotency key used to prevent duplicate executions. This value only needs to be unique for the same template and board combination.
     */
    'idempotencyKey'?: string | null;
    /**
     * Gets or sets an optional value that can be used to relate multiple executions.
     */
    'reference'?: string | null;
}
/**
 * Represents an External Entity response that contains External Entity Properties.
 */
export interface ExternalEntity {
    /**
     * External Entity ID.
     */
    'id': string;
    /**
     * External Entity name.
     */
    'name': string;
    /**
     * External Entity type.
     */
    'type': string;
    /**
     * External Entity data.
     */
    'data': { [key: string]: any; };
}
/**
 * Represents information about a property an external entity.
 */
export interface ExternalEntityFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': string;
    /**
     * Gets or sets the link associated with the field value, if any.
     */
    'link'?: string | null;
    /**
     * Gets or sets the display name associated with the field value, if any.
     */
    'displayName'?: string | null;
    /**
     * Gets or sets whether the field value can be set by the user.
     */
    'isReadOnly'?: boolean;
}
/**
 * Represents a source for obtaining values from an external entity field and optional templates for display and linking.
 */
export interface ExternalEntityFieldValueSource {
    /**
     * The Id of the external entity field that will contain the property to use.
     */
    'externalEntityFieldId': string;
    /**
     * The property of the external entity field to use.
     */
    'property': string;
    /**
     * Gets the template used to format the display value.
     */
    'displayTemplate'?: string | null;
    /**
     * Gets or sets the template used to generate links.
     */
    'linkTemplate'?: string | null;
}
/**
 * Represents a single filtering rule applied to an External Entity.
 */
export interface ExternalEntityFilter {
    /**
     * The name of External Entity property to filter by.
     */
    'propertyName': string;
    /**
     * The comparison operator. Possible values are eq|in|neq|lt|lte|gt|gte.
     */
    'operator': string;
    /**
     * The values to compare External Entity property against. At most 100 values. A longer list returns HTTP 400 `API_LIMIT_EXCEEDED` (`filter_values`).
     */
    'values': Array<any>;
}
/**
 * Represents a paginated response containing a page of items
 */
export interface ExternalEntityIPaginatedResponse {
    /**
     * The zero-based page index
     */
    'pageIndex'?: number;
    /**
     * Need to return page size as the server may disallow passing an arbitrarily large value
     */
    'pageSize'?: number;
    /**
     * Whether there is a previous page
     */
    'hasPreviousPage'?: boolean;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<ExternalEntity>;
    /**
     * Total count of items across pages. Only returned when requested.
     */
    'totalCount'?: number | null;
}
/**
 * Identifies one external entity to invalidate.
 */
export interface ExternalEntityInvalidationItem {
    /**
     * The external entity type identifier, e.g. `jpm.job`.
     */
    'externalEntityTypeId': string;
    /**
     * The external entity identifier.
     */
    'externalEntityId': string;
}
/**
 * Represents a field of type \"external_entity_property\", used to reference a property of an external entity.
 */
export interface ExternalEntityPropertyField extends Field {
    /**
     * Gets or sets the collection of external entity fields and their properties to use as values for this field in order of priority.
     */
    'valueSources'?: Array<ExternalEntityFieldValueSource>;
    /**
     * Gets or sets whether the value of the field is an array.
     */
    'valueIsArray'?: boolean;
}
/**
 * Represents the configuration of an ExternalEntityProperty field for a Task Type.
 */
export interface ExternalEntityPropertyFieldOverride extends FieldOverride {
    /**
     * Gets or sets the collection of external entity fields and their properties to use as values for this field in order of priority.
     */
    'valueSources'?: Array<ExternalEntityFieldValueSource> | null;
}
/**
 * Represents information about a property an external entity.
 */
export interface ExternalEntityPropertyFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': any;
    /**
     * Gets or sets the link associated with the field value, if any.
     */
    'link'?: string | null;
    /**
     * Gets or sets the display name associated with the field value, if any.
     */
    'displayName'?: string | null;
}
/**
 * Represents an External Entity reference that can be associated with Workspaces, Boards, and Tasks
 */
export interface ExternalEntityReference {
    /**
     * Entity ID
     */
    'id': string;
    /**
     * Entity name
     */
    'name'?: string;
    /**
     * Entity predefined type
     */
    'type'?: string;
    /**
     * Entity icon, can contain Uri or Glyph
     */
    'icon'?: Icon | null;
    /**
     * Indicates the External Entity is correlated to this specific Task Hub entity. This External Entity cannot be correlated to another Task Hub entity of the same type (Workspace, Board, Task)
     */
    'correlate'?: boolean;
    /**
     * Defines an optional scoping for External Entities marked as Correlate. If set, this External Entity cannot be correlated to another Task Hub entity of the same type (Workspace, Board, Task) when using the same CorrelationScope
     */
    'correlationScope'?: string | null;
}
/**
 * Represents an External Entity reference that can be used to filter Tasks and Boards associated with it.
 */
export interface ExternalEntityReferenceFilter {
    /**
     * The Entity\'s Type, in form of a namespace.
     */
    'type'?: string;
    /**
     * The Entity\'s ID.
     */
    'id'?: string;
    /**
     * The Entity\'s optional Correlation scope.
     */
    'correlationScope'?: string | null;
}
/**
 * Represents an External Entity Type response that contains External Entity Properties
 */
export interface ExternalEntityType {
    /**
     * External Entity Type ID
     */
    'id': string;
    /**
     * External Entity Type name
     */
    'name': string;
    /**
     * External Entity Type schema
     */
    'schema': ExternalEntityTypeSchema;
    /**
     * External Entity Type provider
     */
    'provider'?: ExternalEntityTypeProvider | null;
    /**
     * Indicates whether the External Entity Type is disabled
     */
    'disabled': boolean;
    /**
     * Scriban template for the link to an External Entity of this type. Variables: entity, monolithBaseUri.
     */
    'linkTemplate'?: string | null;
}
/**
 * Represents a field of type \"external_entity\", used to reference an external entity type.
 */
export interface ExternalEntityTypeField extends Field {
    /**
     * Gets or sets the external entity type associated with this field when the field type is \"external_entity\".
     */
    'entityType'?: string;
    /**
     * Gets or sets the collection of external entity fields and their properties to use as values for this field in order of priority.
     */
    'valueSources'?: Array<ExternalEntityFieldValueSource> | null;
}
/**
 * Represents the configuration of an ExternalEntityType field for a Task Type.
 */
export interface ExternalEntityTypeFieldOverride extends FieldOverride {
    /**
     * Gets or sets the collection of external entity fields and their properties to use as values for this field in order of priority.
     */
    'valueSources'?: Array<ExternalEntityFieldValueSource> | null;
}
/**
 * Represents External Entity Type refresh parameters
 */
export interface ExternalEntityTypeFreshness {
    /**
     * Defines how often External Entity Type should be refreshed
     */
    'checkInterval'?: string;
    /**
     * Defines how long External Entity Properties should stay available in cache
     */
    'cacheTtl'?: string;
    /**
     * Defines maximum age of data before forcing a refresh
     */
    'maxAge'?: string;
}
/**
 * Represents External Entity Type properties provider
 */
export interface ExternalEntityTypeProvider {
    'type': string;
    /**
     * Defines External Entity Type refresh parameters
     */
    'freshness': ExternalEntityTypeFreshness;
    /**
     * Defines how External Entity changes reach Task Hub: \"lazy_poll\" (default) or \"eager_fetch\"
     */
    'ingress'?: string | null;
}
/**
 * Represents External Entity Type properties
 */
export interface ExternalEntityTypeSchema {
    /**
     * Defines External Entity Type properties that Task may use in Fields
     */
    'properties': { [key: string]: ExternalEntityTypeSchemaProperty; };
    /**
     * Defines which Property should be used as ID
     */
    'idProperty': string;
    /**
     * Defines which Property should be used as Name
     */
    'nameProperty': string;
    /**
     * Defines which Property should be used as Freshness indicator. Required when RestExternalEntityTypeProvider specified
     */
    'lastUpdatedProperty'?: string | null;
}
/**
 * Represents External Entity Type property
 */
export interface ExternalEntityTypeSchemaProperty {
    /**
     * Property\'s Type
     */
    'type': string;
    /**
     * Gets or sets a value indicating whether the property represents an array of the specified ServiceTitan.TaskHub.Client.Contracts.ExternalEntityTypeSchemaProperty.Type.
     */
    'isArray'?: boolean;
}
/**
 * A single feature gate: a setting key, the expected value, and an optional fallback.
 */
export interface FeatureGate {
    /**
     * The setting key to look up (e.g. \"EnableExteriorsProjectsAndJobs\").
     */
    'key': string;
    /**
     * Which provider resolves the key. V1 only supports \"FeatureGate\" (MetaDB).
     */
    'source'?: string;
    /**
     * The value the setting must equal for the gate to be satisfied.
     */
    'expectedValue'?: boolean;
    /**
     * Fallback value when the provider cannot resolve the setting. Null means the gate is not satisfied.
     */
    'default'?: boolean | null;
}
/**
 * Declares feature gate requirements for a field. When present, the field is only visible to orgs whose settings satisfy the configured gates.
 */
export interface FeatureGateConfiguration {
    /**
     * How multiple gates are combined: \"All\" requires every gate to be satisfied; \"Any\" requires at least one.
     */
    'operator'?: string;
    /**
     * The list of individual gates to evaluate.
     */
    'gates': Array<FeatureGate>;
}
/**
 * Represents the definition of a field that can be attached to a Task.
 */
export interface Field {
    'type': string;
    /**
     * Gets or sets the identifier of the Workspace this field belongs to.
     */
    'workspaceId'?: string;
    /**
     * Gets or sets the unique identifier for the field within the Workspace and Namespace.
     */
    'id': string;
    /**
     * Gets or sets the display name of the field.
     */
    'name': string;
    /**
     * Gets or sets a value indicating whether this field is required on tasks that use it.
     */
    'required': boolean;
    /**
     * Gets or sets a value indicating whether this field is disabled.
     */
    'disabled': boolean;
    /**
     * Gets or sets the access level indicating who can set the value of this field.
     */
    'settableBy': string;
    /**
     * Sort method for the Field.
     */
    'sortMethod': string;
    /**
     * Optional feature gate configuration. When present, the field is only visible to orgs whose settings satisfy the configured gates.
     */
    'featureGates'?: FeatureGateConfiguration | null;
    /**
     * When `true`, this field is for internal system use only. Internal fields are excluded entirely from list and get-by-id responses for callers without the `st.task_hub.global.write` scope or system-caller status; unprivileged callers never observe this property set to `true`. Defaults to `false`.
     */
    'internal'?: boolean;
    /**
     * When `true`, this field\'s values are matched by task search queries on every task type that uses the field (a task type may override this via its field override). Defaults to `false`.
     */
    'searchable'?: boolean;
}
/**
 * Represents a paginated response containing a page of items
 */
export interface FieldIPaginatedResponse {
    /**
     * The zero-based page index
     */
    'pageIndex'?: number;
    /**
     * Need to return page size as the server may disallow passing an arbitrarily large value
     */
    'pageSize'?: number;
    /**
     * Whether there is a previous page
     */
    'hasPreviousPage'?: boolean;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<UpsertCustomField200Response>;
    /**
     * Total count of items across pages. Only returned when requested.
     */
    'totalCount'?: number | null;
}
/**
 * Represents the schema for a custom field in a Task Type
 */
export interface FieldOverride {
    'type': string;
    /**
     * Gets the unique identifier for the custom field.
     */
    'fieldId': string;
    /**
     * Gets or sets a value that, if set, overrides the name defined on the Field.
     */
    'name'?: string | null;
    /**
     * Gets or sets a value that, if set, overrides the required setting defined on the Field.
     */
    'required'?: boolean | null;
    /**
     * Gets or sets a value that, if set, overrides the Searchable setting defined on the Field for tasks of this task type. When the field is searchable (by definition or override), its values are matched by task search queries for those tasks.
     */
    'searchable'?: boolean | null;
    /**
     * Gets or sets a value that, if set, computes this field\'s value by aggregating member tasks scoped to an external entity, rather than accepting a direct write.
     */
    'rule'?: FieldRule | null;
}
/**
 * Represents a config-driven aggregation rule for a field, sourced from tasks sharing an external entity.
 */
export interface FieldRule {
    /**
     * The aggregation source. Only \"external_entity\" is supported today.
     */
    'from': string;
    /**
     * The external entity type whose member tasks are aggregated.
     */
    'externalEntityTypeId': string;
    /**
     * The field read from member tasks and aggregated.
     */
    'field': string;
    /**
     * The aggregation function. One of: distinct_values, max, min (sum, avg, count are reserved and always rejected).
     */
    'aggregation': string;
    /**
     * Optional filters applied to member tasks before aggregation.
     */
    'filters'?: Array<FieldRuleFilter> | null;
}
/**
 * A single filter condition applied to member tasks before aggregation.
 */
export interface FieldRuleFilter {
    /**
     * The field on member tasks to filter on.
     */
    'fieldId': string;
    /**
     * The comparison operator applied to the field value.
     */
    'operator': string;
    /**
     * The value compared against the field value.
     */
    'value': string;
}
/**
 * Configuration for sorting by a specific field
 */
export interface FieldSorting {
    /**
     * Field to sort by
     */
    'fieldId': string;
    /**
     * Sorting direction (\"asc\" or \"desc\")
     */
    'direction': string;
}
/**
 * @type FieldValue
 * Represents a value associated with a specific field.
 */
export type FieldValue = Array<FieldValueOneOfInner> | boolean | number | string;

/**
 * @type FieldValueOneOfInner
 */
export type FieldValueOneOfInner = boolean | number | string;

/**
 * Represents a custom Field custom value
 */
export interface FieldValueOption {
    /**
     * Field Value Option\'s name
     */
    'name': string;
    /**
     * Indicates whether the option is currently disabled
     */
    'disabled'?: boolean;
    /**
     * Defines the order in which this option appears in a dropdown or list
     */
    'sortOrder'?: number;
    /**
     * The color associated with this field value option (e.g., \"#FF0000\" or \"#F00\")
     */
    'color'?: string | null;
    /**
     * Field value identifier, unique within the Field
     */
    'id': string;
}
/**
 * Represents a custom Field Value Option details
 */
export interface FieldValueOptionDetails {
    /**
     * Field Value Option\'s name
     */
    'name': string;
    /**
     * Indicates whether the option is currently disabled
     */
    'disabled'?: boolean;
    /**
     * Defines the order in which this option appears in a dropdown or list
     */
    'sortOrder'?: number;
    /**
     * The color associated with this field value option (e.g., \"#FF0000\" or \"#F00\")
     */
    'color'?: string | null;
}
/**
 * A paginated request to search for Users in an organization.
 */
export interface FindUsersRequest {
    /**
     * Users IDs. At most 200 ids. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`user_ids`).
     */
    'userIds'?: Array<string>;
    'pageIndex'?: number;
    'pageSize'?: number | null;
}
/**
 * A grid layout page within a Context Configuration
 */
export interface GridConfigurationPage extends ContextConfigurationPage {
    /**
     * Configuration for the grid layout
     */
    'grid': ContextConfigurationPageGrid;
}
/**
 * A grid layout page used in upsert requests
 */
export interface GridConfigurationPageInput extends ContextConfigurationPageInput {
    /**
     * Configuration for the grid layout
     */
    'grid': ContextConfigurationPageGridInput;
}
/**
 * One result entry from the top-task-per-group search endpoint. ServiceTitan.TaskHub.Client.Contracts.GroupedTaskResult.Task is `null` when no task matched this group value.
 */
export interface GroupedTaskResult {
    /**
     * The group value this result corresponds to.
     */
    'groupValue': FieldValue;
    /**
     * The top task for this group, or `null` when no match.
     */
    'task': TaskResponse | null;
}
/**
 * Response for the top-task-per-group search endpoint. ServiceTitan.TaskHub.Client.Contracts.GroupedTasksResponse.Results is ordered to match the request ServiceTitan.TaskHub.Client.Contracts.TaskGroupBy.Values.
 */
export interface GroupedTasksResponse {
    /**
     * One entry per input group value, in input order.
     */
    'results': Array<GroupedTaskResult>;
}
/**
 * Configuration for the header section, including fields to display
 */
export interface HeaderConfiguration {
    /**
     * Fields to display in the header
     */
    'fields': Array<HeaderFieldConfiguration>;
}
/**
 * Configuration for a field displayed in the header section
 */
export interface HeaderFieldConfiguration {
    /**
     * Field identifier
     */
    'fieldId': string;
    /**
     * Field name
     */
    'name': string;
    /**
     * Optional icon associated with the field
     */
    'icon'?: string | null;
}
/**
 * Base class for all task change types in audit log responses. Represents a single change to a task property, field, or collection item.
 */
export interface ITaskChange {
    'type': string;
}
/**
 * Represents an Entity icon, can contain Uri or Glyph
 */
export interface Icon {
    /**
     * Icon Uri
     */
    'uri'?: string | null;
    /**
     * Icon Glyph
     */
    'glyph'?: string | null;
}
/**
 * A batch of external entities in one organization whose source data changed and should be refreshed.
 */
export interface InvalidateExternalEntitiesRequest {
    /**
     * The external entities to invalidate.
     */
    'items': Array<ExternalEntityInvalidationItem>;
}
/**
 * Represents a field of type \"json\", used to store structured JSON data.
 */
export interface JsonField extends Field {
    /**
     * MFE configuration for rendering this JSON field. If null, the field will not be displayed in the UI.
     */
    'drawerMfe'?: JsonFieldDrawerMfeConfig | null;
}
/**
 * Configuration for the MFE that renders a JSON field.
 */
export interface JsonFieldDrawerMfeConfig {
    /**
     * The URL of the MFE bundle (typically unpkg CDN).
     */
    'url': string;
}
/**
 * Represents a JSON field value.
 */
export interface JsonFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': any;
}
export interface JsonSchema {
    'documentPath'?: string | null;
    'isBinary'?: boolean;
    'inheritedSchema'?: JsonSchema | null;
    'inheritedTypeSchema'?: JsonSchema | null;
    'allInheritedSchemas'?: Array<JsonSchema>;
    'responsibleDiscriminatorObject'?: OpenApiDiscriminator | null;
    'hasActualProperties'?: boolean;
    'actualProperties'?: { [key: string]: JsonSchemaProperty; };
    'schemaVersion'?: string | null;
    'id'?: string | null;
    'title'?: string | null;
    'hasTypeNameTitle'?: boolean;
    'description'?: string | null;
    'type'?: JsonSchemaTypeEnum;
    'parentSchema'?: JsonSchema | null;
    'parent'?: any;
    'format'?: string | null;
    'default'?: any;
    'multipleOf'?: number | null;
    'maximum'?: number | null;
    'exclusiveMaximum'?: number | null;
    'isExclusiveMaximum'?: boolean;
    'minimum'?: number | null;
    'exclusiveMinimum'?: number | null;
    'isExclusiveMinimum'?: boolean;
    'maxLength'?: number | null;
    'minLength'?: number | null;
    'pattern'?: string | null;
    'maxItems'?: number;
    'minItems'?: number;
    'uniqueItems'?: boolean;
    'maxProperties'?: number;
    'minProperties'?: number;
    'isDeprecated'?: boolean;
    'deprecatedMessage'?: string | null;
    'isAbstract'?: boolean;
    'isNullableRaw'?: boolean | null;
    'example'?: any;
    'isFlagEnumerable'?: boolean;
    'enumeration'?: Array<any>;
    'isEnumeration'?: boolean;
    'requiredProperties'?: Array<string>;
    'dictionaryKey'?: JsonSchema | null;
    'properties'?: { [key: string]: JsonSchemaProperty; };
    'xml'?: JsonXmlObject | null;
    'patternProperties'?: { [key: string]: JsonSchemaProperty; };
    'item'?: JsonSchema | null;
    'items'?: Array<JsonSchema>;
    'not'?: JsonSchema | null;
    'definitions'?: { [key: string]: JsonSchema; };
    'allOf'?: Array<JsonSchema>;
    'anyOf'?: Array<JsonSchema>;
    'oneOf'?: Array<JsonSchema>;
    'allowAdditionalItems'?: boolean;
    'additionalItemsSchema'?: JsonSchema | null;
    'allowAdditionalProperties'?: boolean;
    'additionalPropertiesSchema'?: JsonSchema | null;
    'isObject'?: boolean;
    'isArray'?: boolean;
    'isTuple'?: boolean;
    'isDictionary'?: boolean;
    'isAnyType'?: boolean;
    'actualSchema'?: JsonSchema;
    'actualTypeSchema'?: JsonSchema;
    'hasReference'?: boolean;
    'hasAllOfSchemaReference'?: boolean;
    'hasOneOfSchemaReference'?: boolean;
    'hasAnyOfSchemaReference'?: boolean;
    'reference'?: JsonSchema | null;
    'extensionData'?: { [key: string]: any; } | null;
    'actualDiscriminator'?: string | null;
    'discriminator'?: string | null;
    'actualDiscriminatorObject'?: OpenApiDiscriminator | null;
    'discriminatorObject'?: OpenApiDiscriminator | null;
    'enumerationNames'?: Array<string>;
    'enumerationDescriptions'?: Array<string>;
}

export const JsonSchemaTypeEnum = {
    None: 'None',
    Array: 'Array',
    Boolean: 'Boolean',
    Integer: 'Integer',
    Null: 'Null',
    Number: 'Number',
    Object: 'Object',
    String: 'String',
    File: 'File',
} as const;

export type JsonSchemaTypeEnum = typeof JsonSchemaTypeEnum[keyof typeof JsonSchemaTypeEnum];

export interface JsonSchemaProperty {
    'documentPath'?: string | null;
    'isBinary'?: boolean;
    'inheritedSchema'?: JsonSchema | null;
    'inheritedTypeSchema'?: JsonSchema | null;
    'allInheritedSchemas'?: Array<JsonSchema>;
    'responsibleDiscriminatorObject'?: OpenApiDiscriminator | null;
    'hasActualProperties'?: boolean;
    'actualProperties'?: { [key: string]: JsonSchemaProperty; };
    'schemaVersion'?: string | null;
    'id'?: string | null;
    'title'?: string | null;
    'hasTypeNameTitle'?: boolean;
    'description'?: string | null;
    'type'?: JsonSchemaPropertyTypeEnum;
    'parentSchema'?: JsonSchema | null;
    'format'?: string | null;
    'default'?: any;
    'multipleOf'?: number | null;
    'maximum'?: number | null;
    'exclusiveMaximum'?: number | null;
    'isExclusiveMaximum'?: boolean;
    'minimum'?: number | null;
    'exclusiveMinimum'?: number | null;
    'isExclusiveMinimum'?: boolean;
    'maxLength'?: number | null;
    'minLength'?: number | null;
    'pattern'?: string | null;
    'maxItems'?: number;
    'minItems'?: number;
    'uniqueItems'?: boolean;
    'maxProperties'?: number;
    'minProperties'?: number;
    'isDeprecated'?: boolean;
    'deprecatedMessage'?: string | null;
    'isAbstract'?: boolean;
    'isNullableRaw'?: boolean | null;
    'example'?: any;
    'isFlagEnumerable'?: boolean;
    'enumeration'?: Array<any>;
    'isEnumeration'?: boolean;
    'requiredProperties'?: Array<string>;
    'dictionaryKey'?: JsonSchema | null;
    'properties'?: { [key: string]: JsonSchemaProperty; };
    'xml'?: JsonXmlObject | null;
    'patternProperties'?: { [key: string]: JsonSchemaProperty; };
    'item'?: JsonSchema | null;
    'items'?: Array<JsonSchema>;
    'not'?: JsonSchema | null;
    'definitions'?: { [key: string]: JsonSchema; };
    'allOf'?: Array<JsonSchema>;
    'anyOf'?: Array<JsonSchema>;
    'oneOf'?: Array<JsonSchema>;
    'allowAdditionalItems'?: boolean;
    'additionalItemsSchema'?: JsonSchema | null;
    'allowAdditionalProperties'?: boolean;
    'additionalPropertiesSchema'?: JsonSchema | null;
    'isObject'?: boolean;
    'isArray'?: boolean;
    'isTuple'?: boolean;
    'isDictionary'?: boolean;
    'isAnyType'?: boolean;
    'actualSchema'?: JsonSchema;
    'actualTypeSchema'?: JsonSchema;
    'hasReference'?: boolean;
    'hasAllOfSchemaReference'?: boolean;
    'hasOneOfSchemaReference'?: boolean;
    'hasAnyOfSchemaReference'?: boolean;
    'reference'?: JsonSchema | null;
    'extensionData'?: { [key: string]: any; } | null;
    'actualDiscriminator'?: string | null;
    'discriminator'?: string | null;
    'actualDiscriminatorObject'?: OpenApiDiscriminator | null;
    'discriminatorObject'?: OpenApiDiscriminator | null;
    'enumerationNames'?: Array<string>;
    'enumerationDescriptions'?: Array<string>;
    'name'?: string;
    'parent'?: any;
    'isRequired'?: boolean;
    'isReadOnly'?: boolean;
    'isWriteOnly'?: boolean;
    'isInheritanceDiscriminator'?: boolean;
}

export const JsonSchemaPropertyTypeEnum = {
    None: 'None',
    Array: 'Array',
    Boolean: 'Boolean',
    Integer: 'Integer',
    Null: 'Null',
    Number: 'Number',
    Object: 'Object',
    String: 'String',
    File: 'File',
} as const;

export type JsonSchemaPropertyTypeEnum = typeof JsonSchemaPropertyTypeEnum[keyof typeof JsonSchemaPropertyTypeEnum];

export interface JsonXmlObject {
    'parentSchema'?: JsonSchema | null;
    'name'?: string | null;
    'wrapped'?: boolean;
    'namespace'?: string | null;
    'prefix'?: string | null;
    'attribute'?: boolean;
}
/**
 * Represents a custom Label
 */
export interface Label {
    /**
     * Label\'s name
     */
    'name': string;
    /**
     * A color associated with this Label
     */
    'color'?: string | null;
    /**
     * An icon associated with this Label
     */
    'icon'?: Icon | null;
    /**
     * Indicates whether the Label is disabled
     */
    'disabled': boolean;
    /**
     * Workspace identifier the Label is associated with
     */
    'workspaceId'?: string;
    /**
     * Label identifier, unique across the Workspace + Namespace
     */
    'id': string;
}
/**
 * Represents a paginated response containing a page of items
 */
export interface LabelIPaginatedResponse {
    /**
     * The zero-based page index
     */
    'pageIndex'?: number;
    /**
     * Need to return page size as the server may disallow passing an arbitrarily large value
     */
    'pageSize'?: number;
    /**
     * Whether there is a previous page
     */
    'hasPreviousPage'?: boolean;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<Label>;
    /**
     * Total count of items across pages. Only returned when requested.
     */
    'totalCount'?: number | null;
}
/**
 * Represents a custom embedded Label
 */
export interface LabelReference {
    /**
     * Label identifier, unique across the Workspace + Namespace
     */
    'id': string;
}
/**
 * A master-detail layout page within a Context Configuration
 */
export interface MasterDetailConfigurationPage extends ContextConfigurationPage {
    /**
     * Configuration for the grid layout
     */
    'grid': ContextConfigurationPageGrid;
    /**
     * Configuration for the detail view
     */
    'details': DetailsConfiguration;
}
/**
 * A master-detail layout page used in upsert requests
 */
export interface MasterDetailConfigurationPageInput extends ContextConfigurationPageInput {
    /**
     * Configuration for the grid layout
     */
    'grid': ContextConfigurationPageGridInput;
    /**
     * Configuration for the detail view
     */
    'details': DetailsConfiguration;
}
/**
 * Represents a single metric value with its associated filters.
 */
export interface MetricValue {
    /**
     * The identifier for this metric value.
     */
    'id': string;
    /**
     * The display name of the metric value.
     */
    'displayName': string;
    /**
     * The numeric value of the metric.
     */
    'value': number;
    /**
     * Collection of additional filters for this metric.
     */
    'filters'?: Array<TaskFilter>;
}
/**
 * Response containing metric values and their associated filters.
 */
export interface MetricsResponse {
    /**
     * Collection of metric values.
     */
    'values'?: Array<MetricValue>;
}
/**
 * A mobile list layout page within a Context Configuration
 */
export interface MobileListConfigurationPage extends ContextConfigurationPage {
    /**
     * Configuration for mobile list items
     */
    'listItems': ContextConfigurationMobileListItem;
    /**
     * Sorting configuration for the mobile list
     */
    'sorting'?: ContextConfigurationPageGridSorting | null;
}
/**
 * A mobile list layout page used in upsert requests
 */
export interface MobileListConfigurationPageInput extends ContextConfigurationPageInput {
    /**
     * Configuration for mobile list items
     */
    'listItems': ContextConfigurationMobileListItem;
    /**
     * Sorting configuration for the mobile list
     */
    'sorting'?: ContextConfigurationPageGridSorting | null;
}
/**
 * A field item in a mobile list
 */
export interface MobileListFieldItem extends MobileListItem {
    /**
     * Field identifier
     */
    'fieldId': string;
    /**
     * Optional prefix to display before the field value
     */
    'prefix'?: string | null;
    /**
     * Optional suffix to display after the field value
     */
    'suffix'?: string | null;
}
/**
 * A mobile list item that can be either a field or a separator
 */
export interface MobileListItem {
    'type': string;
}
/**
 * Layout configuration for a mobile list item
 */
export interface MobileListItemLayout {
    /**
     * Icon to display for the list item
     */
    'icon': string;
    /**
     * Eyebrow items displayed above the primary content
     */
    'eyebrow'?: Array<MobileListItemLayoutEyebrowInner>;
    /**
     * Primary field displayed on the right side
     */
    'primary'?: MobileListFieldItem | null;
    /**
     * Detail items displayed below the primary content
     */
    'details'?: Array<MobileListItemLayoutEyebrowInner>;
}
/**
 * @type MobileListItemLayoutEyebrowInner
 * A mobile list item that can be either a field or a separator
 */
export type MobileListItemLayoutEyebrowInner = MobileListFieldItem | MobileListItem | MobileListSeparatorItem;

/**
 * A separator item in a mobile list
 */
export interface MobileListSeparatorItem extends MobileListItem {
}
/**
 * A named filter that can be referenced by users
 */
export interface NamedFilter {
    /**
     * Field identifier the filter applies to
     */
    'fieldId': string;
    /**
     * Display name of the filter, always resolved from the field\'s display name if not explicitly set.
     */
    'name': string;
}
/**
 * A named filter used in upsert requests where the display name is optional
 */
export interface NamedFilterInput {
    /**
     * Field identifier the filter applies to
     */
    'fieldId': string;
    /**
     * Display name of the filter. When null, defaults to the field\'s display name.
     */
    'name'?: string | null;
}
/**
 * @type NullableFieldValue
 * A field value, or null to clear the field. Null is accepted for every field type.
 */
export type NullableFieldValue = Array<FieldValueOneOfInner> | boolean | number | string;

/**
 * Represents a field of type \"number\".
 */
export interface NumberField extends Field {
    /**
     * Gets or sets the numeric kind (default, percent).
     */
    'numericKind'?: string;
    /**
     * Gets or sets the desired decimal precision for display/UX (0 = integer-only, null = no preference).
     */
    'precision'?: number | null;
    /**
     * Gets or sets the minimum allowed value.
     */
    'min'?: number | null;
    /**
     * Gets or sets the maximum allowed value.
     */
    'max'?: number | null;
}
/**
 * Represents a number field value.
 */
export interface NumberFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': number;
}
export interface OpenApiDiscriminator {
    'propertyName'?: string | null;
    'mapping'?: { [key: string]: JsonSchema; };
    'jsonInheritanceConverter'?: any;
}
export interface OrgId {
    'value'?: string;
}
/**
 * Response for a single org setting lookup. Exposes the resolved value plus diagnostic state (MetaDB-enabled flag and resolved tenant id) so callers can tell whether a null value means \"MetaDB integration disabled\", \"tenant not mapped\", or \"property not set\".
 */
export interface OrgSettingResponse {
    /**
     * The setting key that was requested.
     */
    'key': string;
    /**
     * The resolved string value, or null if not set or unreachable.
     */
    'value'?: string | null;
    /**
     * Whether the MetaDB org-settings provider was used for this lookup.
     */
    'metaDbEnabled'?: boolean;
    /**
     * The tenant id resolved from the org, or null if resolution failed.
     */
    'resolvedTenantId'?: number | null;
}
/**
 * Response for a batch org settings lookup. Diagnostic state is reported once per org; values are keyed by the requested key.
 */
export interface OrgSettingsResponse {
    /**
     * Whether the MetaDB org-settings provider was used for this lookup.
     */
    'metaDbEnabled'?: boolean;
    /**
     * The tenant id resolved from the org, or null if resolution failed.
     */
    'resolvedTenantId'?: number | null;
    /**
     * Map from requested key to its resolved string value (null when not set or unreachable).
     */
    'values': { [key: string]: string | null; };
}
/**
 * Represents an optional Workspace Photo
 */
export interface Photo {
    /**
     * The URI of the photo
     */
    'uri': string;
}
export interface ProblemDetails {
    [key: string]: any;

    'type'?: string | null;
    'title'?: string | null;
    'status'?: number | null;
    'detail'?: string | null;
    'instance'?: string | null;
}
/**
 * Represents a field of type \"reference\", used to reference an internal entity.
 */
export interface ReferenceField extends Field {
}
/**
 * Represents a reference field value on a returned Task.
 */
export interface ReferenceFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': string;
    /**
     * Gets or sets the display name of the referenced entity.
     */
    'displayName': string;
}
/**
 * Represents a relationship between tasks.
 */
export interface Relation {
    /**
     * Type of the relation
     */
    'type': string;
    /**
     * TaskId of the related task
     */
    'taskId': string;
    /**
     * Board the related task lives on, when different from the board of the task this relation belongs to. Null when the related task is on the same board -- the common case.
     */
    'boardId'?: string | null;
}
/**
 * Represents a request to rename an Attachment associated with a Task.
 */
export interface RenameTaskAttachmentRequest {
    /**
     * New file name for the Attachment.
     */
    'fileName': string;
}
/**
 * Base type for all field values on a returned Task.
 */
export interface ResponseFieldValue {
    [key: string]: any;

    'type'?: string;
}
/**
 * Represents External Entity Type Provider that uses REST Api
 */
export interface RestExternalEntityTypeProvider extends ExternalEntityTypeProvider {
    /**
     * Defines REST Api Uri to get External Entity details from
     */
    'uri': string;
    /**
     * Defines the space separated scopes to use when requesting an access token for the REST Api.
     */
    'scopes'?: string | null;
}
/**
 * Represents a field of type \"richtext\".
 */
export interface RichTextField extends Field {
}
/**
 * Represents a rich text field value.
 */
export interface RichTextFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': string;
}
/**
 * Request for the member-filtered roll-up query: returns a page of roll-up tasks (e.g. `jpm.job` / `jpm.project`) whose member tasks (e.g. `jpm.job.task`) satisfy ServiceTitan.TaskHub.Client.Contracts.RollUpMemberFilterQuery.MemberFilters, additionally narrowed by ServiceTitan.TaskHub.Client.Contracts.RollUpMemberFilterQuery.RollUpFilters evaluated against the roll-up documents themselves. The member task types and group-by field are resolved server-side from ServiceTitan.TaskHub.Client.Contracts.RollUpMemberFilterQuery.RollUpTaskTypeId\'s auto-correlation configuration; the matching member→roll-up id set never crosses the wire.
 */
export interface RollUpMemberFilterQuery {
    /**
     * The roll-up task type identifying the grouping (e.g. `jpm.job`, `jpm.project`).
     */
    'rollUpTaskTypeId': string;
    /**
     * Filters evaluated against member tasks. A roll-up is included only if at least one member task satisfies all of these filters together (conjunctive-per-member). At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'memberFilters'?: Array<TaskFilter> | null;
    /**
     * Filters evaluated against the roll-up documents themselves (e.g. the hide-completed window on the roll-up\'s own materialized completion field). At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'rollUpFilters'?: Array<TaskFilter> | null;
    /**
     * The index of the page of roll-up tasks to return.
     */
    'pageIndex'?: number;
    /**
     * The size of the page of roll-up tasks to return.
     */
    'pageSize'?: number | null;
    /**
     * Details on how the query should sort the returned roll-up tasks.
     */
    'sort'?: TaskQuerySort | null;
}
/**
 * A paginated request to search for boards in an organization.
 */
export interface SearchBoardsRequest {
    /**
     * The Boards\' IDs. At most 100 keys. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`board_keys`).
     */
    'boardKeys'?: Array<string>;
    /**
     * The Boards\' labels. At most 50 labels. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`labels`).
     */
    'labels'?: Array<string>;
    /**
     * The Boards\' namespaces. At most 50 namespaces. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`namespaces`).
     */
    'namespaces'?: Array<string>;
    /**
     * External Entities associated with the Boards. At most 100 entities. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`external_entities`).
     */
    'externalEntities'?: Array<ExternalEntityReferenceFilter>;
    /**
     * The Organization ID to filter boards by. Only returns boards explicitly associated with this organization.
     */
    'orgId'?: string | null;
    'pageIndex'?: number;
    'pageSize'?: number | null;
}
/**
 * A request to search for external entities in an organization.
 */
export interface SearchExternalEntitiesRequest {
    /**
     * The text to search for in the entity Name.
     */
    'searchText'?: string | null;
    /**
     * Collection of filtering rules. At most 50 filters. Each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<ExternalEntityFilter> | null;
    'pageIndex'?: number;
    'pageSize'?: number | null;
}
/**
 * A paginated request to search the audit log of a task in an organization.
 */
export interface SearchTaskAuditLogRequest {
    /**
     * Filter by user ID who made the change.
     */
    'userId'?: string | null;
    /**
     * Filter by client application ID that made the change.
     */
    'clientId'?: string | null;
    /**
     * Filter by changes made on or after this date.
     */
    'changedOnFrom'?: string | null;
    /**
     * Filter by changes made on or before this date.
     */
    'changedOnTo'?: string | null;
    /**
     * Filter by action type (created, updated) — the same values the response emits.
     */
    'actionType'?: string | null;
    /**
     * Filter by update source (Default, LifecycleManagement, ExternalEntityRefresh, etc.).
     */
    'updateSource'?: string | null;
    'pageIndex'?: number;
    'pageSize'?: number | null;
}
/**
 * A filter query to search and filter Task Field Values.
 */
export interface SearchTaskFieldValuesQuery {
    /**
     * Collection of filtering rules. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<TaskFilter> | null;
    /**
     * Optional search text to filter the options by name.
     */
    'searchText'?: string | null;
}
/**
 * Response containing a collection of existing Task Field Values.
 */
export interface SearchTaskFieldValuesResponse {
    /**
     * Represents a list of existing Task Field Value Options for a specific field.
     */
    'fieldValues': Array<TaskFieldValueResponse>;
}
/**
 * A paginated request to search for task templates.
 */
export interface SearchTaskTemplatesRequest {
    /**
     * The template\'s applicable boards to include. At most 100 boards. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`applicable_boards`).
     */
    'applicableBoards'?: Array<string>;
    /**
     * The template\'s applicable entity types to include. At most 100 entity types. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`applicable_entity_types`).
     */
    'applicableEntityTypes'?: Array<string>;
    /**
     * The template\'s namespace.
     */
    'namespace'?: string | null;
    /**
     * Whether to include archived templates in the search results. Defaults to false.
     */
    'includeArchived'?: boolean;
    /**
     * Filter by archived status. null uses IncludeArchived behavior (backward compatible), true returns only archived, false returns only non-archived. Takes precedence over IncludeArchived when provided.
     */
    'isArchived'?: boolean | null;
    /**
     * Filter by disabled status. null returns all, true returns only disabled, false returns only active.
     */
    'isDisabled'?: boolean | null;
    /**
     * Case-insensitive search text to filter by name or description.
     */
    'searchText'?: string | null;
    'pageIndex'?: number;
    'pageSize'?: number | null;
}
/**
 * A request to search for users in an organization.
 */
export interface SearchUsersRequest {
    /**
     * The text to search for in the user\'s Name, Username, or E-mail address.
     */
    'searchText'?: string | null;
    'continuationToken'?: string | null;
    /**
     * Desired page size to return. At most 100. A larger page returns HTTP 400 `API_LIMIT_EXCEEDED` (`continuation_page_size`).
     */
    'pageSize'?: number | null;
}
/**
 * A filter query to search and filter Task Field Values across multiple boards. The field definition is resolved per board and the configured options are unioned, so board-override-only options (e.g. a board-specific Work Stage) are preserved.
 */
export interface SearchWorkspaceTaskFieldValuesQuery {
    /**
     * Collection of filtering rules. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<TaskFilter> | null;
    /**
     * Optional search text to filter the options by name.
     */
    'searchText'?: string | null;
    /**
     * Board keys whose field definitions are unioned to resolve the field\'s options. At most 100 keys. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`board_keys`).
     */
    'boardsKeys'?: Array<string>;
}
/**
 * Represents a field of type \"status\", allowing selection from a list of status options.
 */
export interface StatusField extends Field {
    /**
     * Gets or sets the list of selectable status options for the status field.
     */
    'statusValueOptions'?: Array<StatusFieldValueOption>;
    /**
     * The default value for this field on new Tasks of this type.
     */
    'defaultValue': string;
}
/**
 * Represents the configuration of a Status field for a Task Type.
 */
export interface StatusFieldOverride extends FieldOverride {
    /**
     * The allowed values for this field on Tasks of this type.
     */
    'allowedValues'?: Array<StatusFieldValueOptionOverride> | null;
    /**
     * The default value for this field on new Tasks of this type.
     */
    'defaultValue'?: string | null;
}
/**
 * Represents a status field value.
 */
export interface StatusFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': string;
}
/**
 * Represents a custom Status Field value option
 */
export interface StatusFieldValueOption {
    /**
     * Field Value Option\'s name
     */
    'name': string;
    /**
     * Indicates whether the option is currently disabled
     */
    'disabled'?: boolean;
    /**
     * Defines the order in which this option appears in a dropdown or list
     */
    'sortOrder'?: number;
    /**
     * The color associated with this field value option (e.g., \"#FF0000\" or \"#F00\")
     */
    'color'?: string | null;
    /**
     * The category associated with the status value option. Must be one of to_do, in_progress, or completed.
     */
    'category': string;
    /**
     * Field value identifier, unique within the Field
     */
    'id': string;
}
/**
 * Details about the allowed value for a Status field in a Task Type.
 */
export interface StatusFieldValueOptionOverride {
    /**
     * The identifier for this value.
     */
    'id': string;
}
/**
 * Represents an Attachment associated with a Task
 */
export interface TaskAttachment {
    /**
     * ID of the uploaded Attachment
     */
    'attachmentId'?: string;
    /**
     * File name of the uploaded Attachment
     */
    'fileName': string;
}
/**
 * Represents a change to task attachments (added, modified, or removed).
 */
export interface TaskAttachmentChangeResponse extends ITaskChange {
    /**
     * Gets or sets the type of change: \"added\", \"modified\", or \"removed\".
     */
    'changeType': string;
    /**
     * Gets or sets the attachment details before the change. Null for Add operations.
     */
    'oldValue'?: TaskAttachment | null;
    /**
     * Gets or sets the attachment details after the change. Null for Remove operations.
     */
    'newValue'?: TaskAttachment | null;
}
/**
 * Represents a response containing details of an Audit Log entry for a Task.
 */
export interface TaskAuditLogResponse {
    /**
     * Gets or sets the unique identifier for the Audit Log.
     */
    'id': string;
    /**
     * Gets or sets the type of change that was made. Possible values: \"created\", \"updated\".
     */
    'actionType': string;
    /**
     * Gets or sets the unique identifier for the Task.
     */
    'taskId': string;
    /**
     * Gets or sets the unique identifier for the Workspace the Task belongs to.
     */
    'workspaceId': string;
    /**
     * Gets or sets the unique identifier for the Board the Task belongs to.
     */
    'boardId': string;
    /**
     * Gets or sets the unique identifier for the client that made the change.
     */
    'changedByClientId'?: string | null;
    /**
     * Gets or sets the unique identifier for the user who made the change.
     */
    'changedByUserId'?: string | null;
    /**
     * Gets or sets the display name of the user who made the change. Null if the user was not found, if the change was made by the system, or if no user ID is available.
     */
    'changedByDisplayName'?: string | null;
    /**
     * Gets or sets the UTC date and time the change was made.
     */
    'changedOn': string;
    /**
     * Gets or sets the collection of changes made to the task. Each item represents a specific modification (field change, attachment added, etc.). Null for audit log entries created before this feature was implemented.
     */
    'changes'?: Array<TaskAuditLogResponseChangesInner> | null;
    /**
     * Gets or sets the source of the update (who/what initiated the change). Possible values: Default (user-initiated), RelationsHandler, AutoCorrelation, LifecycleManagement, ExternalEntityRefresh, JpmStage, Migration, UserMigration. Null for audit log entries created before this feature was implemented.
     */
    'updateSource'?: string | null;
}
/**
 * @type TaskAuditLogResponseChangesInner
 * Base class for all task change types in audit log responses. Represents a single change to a task property, field, or collection item.
 */
export type TaskAuditLogResponseChangesInner = ITaskChange | TaskAttachmentChangeResponse | TaskExternalEntityReferenceChangeResponse | TaskFieldChangeResponse | TaskPropertyChangeResponse | TaskRelationChangeResponse | TaskTriggerChangeResponse | TaskUserChangeResponse;

/**
 * Represents a paginated response containing a page of items
 */
export interface TaskAuditLogResponseIPaginatedResponse {
    /**
     * The zero-based page index
     */
    'pageIndex'?: number;
    /**
     * Need to return page size as the server may disallow passing an arbitrarily large value
     */
    'pageSize'?: number;
    /**
     * Whether there is a previous page
     */
    'hasPreviousPage'?: boolean;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<TaskAuditLogResponse>;
    /**
     * Total count of items across pages. Only returned when requested.
     */
    'totalCount'?: number | null;
}
/**
 * Represents a response containing details of how many tasks matching the query.
 */
export interface TaskCountResponse {
    /**
     * Returns a number of tasks matching the query.
     */
    'count': number;
}
/**
 * Represents a change to external entity references (added, modified, or removed).
 */
export interface TaskExternalEntityReferenceChangeResponse extends ITaskChange {
    /**
     * Gets or sets the type of change: \"added\", \"modified\", or \"removed\".
     */
    'changeType': string;
    /**
     * Gets or sets the external entity reference details before the change. Null for Add operations. Contains entityType, entityId properties.
     */
    'oldValue'?: ExternalEntityReference | null;
    /**
     * Gets or sets the external entity reference details after the change. Null for Remove operations. Contains entityType, entityId properties.
     */
    'newValue'?: ExternalEntityReference | null;
}
/**
 * Represents a field of type \"task\", used to reference a task entity.
 */
export interface TaskField extends Field {
}
/**
 * Represents a change to a custom field value in a task.
 */
export interface TaskFieldChangeResponse extends ITaskChange {
    /**
     * Gets or sets the unique identifier of the field that changed.
     */
    'fieldId': string;
    /**
     * Gets or sets the type of the field (e.g., \"text\", \"number\", \"date\", \"user\", \"dropdown\", \"rich_text\").
     */
    'fieldType': string;
    'oldValue'?: TaskFieldChangeResponseAllOfOldValue | null;
    'newValue'?: TaskFieldChangeResponseAllOfNewValue | null;
}
/**
 * @type TaskFieldChangeResponseAllOfNewValue
 * Gets or sets the value after the change. Null if the field value was removed. The structure depends on the FieldType.
 */
export type TaskFieldChangeResponseAllOfNewValue = CheckboxFieldValue | ComputedFieldValue | DateFieldValue | DropdownFieldValue | ExternalEntityFieldValue | ExternalEntityPropertyFieldValue | JsonFieldValue | NumberFieldValue | ReferenceFieldValue | ResponseFieldValue | RichTextFieldValue | StatusFieldValue | TaskFieldValue | TextFieldValue | TimestampFieldValue | UserFieldValue;

/**
 * @type TaskFieldChangeResponseAllOfOldValue
 * Gets or sets the value before the change. Null if this is a new field value. The structure depends on the FieldType.
 */
export type TaskFieldChangeResponseAllOfOldValue = CheckboxFieldValue | ComputedFieldValue | DateFieldValue | DropdownFieldValue | ExternalEntityFieldValue | ExternalEntityPropertyFieldValue | JsonFieldValue | NumberFieldValue | ReferenceFieldValue | ResponseFieldValue | RichTextFieldValue | StatusFieldValue | TaskFieldValue | TextFieldValue | TimestampFieldValue | UserFieldValue;

/**
 * Represents a request to preview a task\'s resolved field values prior to creation.
 */
export interface TaskFieldPreviewRequest {
    /**
     * Gets or sets the field values associated with the Task.
     */
    'fieldValues'?: { [key: string]: NullableFieldValue; };
}
/**
 * Represents the response with resolved field values for a task preview.
 */
export interface TaskFieldPreviewResponse {
    /**
     * Gets or sets the unique identifier for the Workspace the Task belongs to.
     */
    'workspaceId': string;
    /**
     * Gets or sets the unique identifier for the Board the Task belongs to.
     */
    'boardId': string;
    /**
     * Gets or sets the field values associated with the Task.
     */
    'fieldValues'?: { [key: string]: ResponseFieldValue; };
}
/**
 * Represents a task field value.
 */
export interface TaskFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': string;
}
/**
 * Represents a single Task Field Value Option.
 */
export interface TaskFieldValueResponse {
    /**
     * User-friendly identifier for the field value option.
     */
    'displayName': string;
    /**
     * Identifier of the field value option, or `null` for the \"Not set\" pseudo-option (a task whose field has no value).
     */
    'value': any;
}
/**
 * Represents a single filtering rule applied to a field.
 */
export interface TaskFilter {
    /**
     * The name of the field to filter or sort by.
     */
    'field': string;
    /**
     * The comparison operator. Possible values are eq|in|neq|lt|lte|gt|gte.
     */
    'operator': string;
    /**
     * The value to compare the field against.  Must be null when Formula is provided.
     */
    'value': any;
    /**
     * Formula property leverages the Scriban template engine to calculate dynamic values at query time.  Must be null when Value is provided.
     */
    'formula'?: string | null;
}
/**
 * Group dimension for ServiceTitan.TaskHub.Client.Contracts.BoardTasksTopPerGroupQuery.
 */
export interface TaskGroupBy {
    /**
     * The field id whose value defines each group. Must resolve to a field of type `ExternalEntity`.
     */
    'fieldId': string;
    /**
     * The set of group values to fetch the top task for. Must contain between 1 and 100 distinct, non-null entries.
     */
    'values': Array<FieldValue>;
}
/**
 * Represents a label applied to a Task.
 */
export interface TaskLabel {
    /**
     * Label identifier, unique across the Workspace + Namespace
     */
    'id': string;
    /**
     * Name of the label
     */
    'name': string;
    /**
     * Color of the label
     */
    'color'?: string | null;
    /**
     * Icon of the label
     */
    'icon'?: string | null;
    /**
     * Indicates if the label is a system label.  System labels cannot be added or removed by users.
     */
    'isSystem'?: boolean;
}
/**
 * Represents a change to a core task property (board, workspace, etc.).
 */
export interface TaskPropertyChangeResponse extends ITaskChange {
    /**
     * Gets or sets the name of the property that changed (e.g., \"BoardId\", \"WorkspaceId\").
     */
    'propertyName': string;
    /**
     * Gets or sets the value before the change.
     */
    'oldValue'?: any;
    /**
     * Gets or sets the value after the change.
     */
    'newValue'?: any;
}
/**
 * Represents the sorting direction used in KendoGrid.
 */
export interface TaskQuerySort {
    /**
     * The field to sort by.
     */
    'field': string;
    /**
     * The sorting direction (\"asc\" or \"desc\").
     */
    'dir': string;
}
/**
 * Represents a change to task relationships (added, modified, or removed).
 */
export interface TaskRelationChangeResponse extends ITaskChange {
    /**
     * Gets or sets the type of change: \"added\", \"modified\", or \"removed\".
     */
    'changeType': string;
    /**
     * Gets or sets the relation details before the change. Null for Add operations.
     */
    'oldValue'?: Relation | null;
    /**
     * Gets or sets the relation details after the change. Null for Remove operations.
     */
    'newValue'?: Relation | null;
}
/**
 * Represents a response containing details of a Task.
 */
export interface TaskResponse {
    /**
     * Gets or sets the unique identifier for the Workspace the Task belongs to.
     */
    'workspaceId': string;
    /**
     * Gets or sets the unique identifier for the Board the Task belongs to.
     */
    'boardId': string;
    /**
     * Gets or sets the unique identifier for the Task.
     */
    'taskId': string;
    /**
     * Gets or sets the namespace for the Task.
     */
    'namespace'?: string | null;
    /**
     * Gets or sets the field values associated with the Task.
     */
    'fieldValues'?: { [key: string]: ResponseFieldValue; };
    /**
     * Gets or sets the labels associated with the Task.
     */
    'labels'?: Array<TaskLabel>;
    /**
     * Gets or sets the external entities associated with the Task.
     */
    'externalEntities'?: Array<ExternalEntityReference>;
    /**
     * Gets or sets Users associated with the Task.
     */
    'users'?: Array<TaskUser>;
    /**
     * Gets or sets the Checklists associated with the Task.
     */
    'checklists'?: Array<Checklist>;
    /**
     * Gets or sets the attachments associated with the Task.
     */
    'attachments'?: Array<TaskAttachment>;
    /**
     * Gets the relations of the Task with another Tasks.
     */
    'relations'?: Array<Relation>;
    /**
     * Gets or sets the correlation IDs associated with the Task.
     */
    'correlationIds'?: Array<string>;
    /**
     * Gets or sets the entity tag for optimistic concurrency control. This value can be used in subsequent update requests to detect concurrent modifications.
     */
    'eTag'?: string | null;
}
/**
 * Represents a Continuation response containing a page of items
 */
export interface TaskResponseIContinuationResponse {
    /**
     * Continuation Token from the previous call of this request
     */
    'continuationToken'?: string | null;
    /**
     * Desired page size to return
     */
    'pageSize'?: number;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<TaskResponse>;
}
/**
 * Represents a paginated response containing a page of items
 */
export interface TaskResponseIPaginatedResponse {
    /**
     * The zero-based page index
     */
    'pageIndex'?: number;
    /**
     * Need to return page size as the server may disallow passing an arbitrarily large value
     */
    'pageSize'?: number;
    /**
     * Whether there is a previous page
     */
    'hasPreviousPage'?: boolean;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<TaskResponse>;
    /**
     * Total count of items across pages. Only returned when requested.
     */
    'totalCount'?: number | null;
}
/**
 * Represents a response containing details of a Task Template.
 */
export interface TaskTemplate {
    /**
     * Gets the unique identifier of the workspace associated with this instance.
     */
    'workspaceId': string;
    /**
     * Gets or sets the unique identifier for the Task Template.
     */
    'id': string;
    /**
     * Gets or sets the name of the Task Template.
     */
    'name': string;
    /**
     * Gets or sets the description of the Task Template.
     */
    'description': string;
    /**
     * Gets or sets the Board Key of the Task Template.
     */
    'applicableBoards'?: Array<string>;
    /**
     * Gets or sets the list of applicable entity types for the Task Template.
     */
    'applicableEntityTypes': Array<string>;
    /**
     * Gets or sets the list of applicable entity IDs for the Task Template.
     */
    'variables': Array<TaskTemplateVariablesInner>;
    /**
     * Gets or sets the list of tasks associated with the Task Template.
     */
    'tasks': Array<TaskTemplateTask>;
    /**
     * The UTC date and time the Task Template was updated.
     */
    'lastUpdatedAt': string;
    /**
     * Gets or sets a value indicating whether the Task Template has been archived.
     */
    'isArchived'?: boolean;
    /**
     * Gets or sets a value indicating whether the Task Template is disabled.
     */
    'disabled'?: boolean;
    /**
     * Gets or sets the identifiers of the workspace and task template from which this task template was copied from, if applicable.
     */
    'copiedFrom'?: TaskTemplateCopiedFromReference | null;
    /**
     * Indicates whether the Task Template is a system template.
     */
    'isSystem'?: boolean;
    /**
     * Gets or sets the display name of the user who last changed the Task Template.
     */
    'lastChangedByDisplayName'?: string | null;
}
/**
 * Represents a checkbox variable within a task template.
 */
export interface TaskTemplateCheckboxVariable extends TaskTemplateVariable {
}
/**
 * Represents a Checklist to be included in a task created from a Task Template.
 */
export interface TaskTemplateChecklist {
    /**
     * Name of the Checklist
     */
    'name': string;
    /**
     * Checklist items
     */
    'items': Array<TaskTemplateChecklistItem>;
}
/**
 * A single item in a ServiceTitan.TaskHub.Client.Contracts.TaskTemplateChecklist.
 */
export interface TaskTemplateChecklistItem {
    /**
     * The text for the Checklist item
     */
    'description': string;
    /**
     * If the item is marked as complete
     */
    'isChecked': boolean;
}
/**
 * Represents the identifiers of the workspace and task template from which a task template was copied.
 */
export interface TaskTemplateCopiedFromReference {
    /**
     * Gets or sets the Workspace ID from which the Task Template was copied.
     */
    'workspaceId': string;
    /**
     * Gets or sets the Task Template ID from which the Task Template was copied.
     */
    'taskTemplateId': string;
}
/**
 * Represents a date variable within a task template.
 */
export interface TaskTemplateDateVariable extends TaskTemplateVariable {
}
/**
 * Represents a dropdown variable within a task template.
 */
export interface TaskTemplateDropdownVariable extends TaskTemplateVariable {
}
/**
 * Represents a response containing details of a Task Template Execution.
 */
export interface TaskTemplateExecution {
    /**
     * Gets or sets the unique identifier for the Task Template Execution.
     */
    'id': string;
    /**
     * Gets or sets the ID of the task template that was executed.
     */
    'taskTemplateId': string;
    /**
     * Gets or sets the user who executed the template.
     */
    'executedBy': string;
    /**
     * Gets or sets the timestamp when the template was executed.
     */
    'executedOn': string;
    /**
     * Gets or sets the variables provided during execution.
     */
    'variables': { [key: string]: any; };
    /**
     * Gets or sets the status of the template execution. Possible values: \"success\", \"failed\", \"in_progress\"
     */
    'status': string;
    /**
     * Gets or sets the collection of errors if the execution failed.
     */
    'errors'?: Array<TaskTemplateExecutionError>;
    /**
     * Gets or sets the tasks created by the template execution, containing both task IDs and their corresponding template keys.
     */
    'tasks': Array<TaskTemplateExecutionTask>;
    /**
     * Gets or sets the optional idempotency key used to prevent duplicate executions.
     */
    'idempotencyKey'?: string | null;
    /**
     * Gets or sets the optional value used to relate multiple executions.
     */
    'reference'?: string | null;
}
/**
 * Represents an error that occurred during task template execution.
 */
export interface TaskTemplateExecutionError {
    /**
     * Gets or sets the error message.
     */
    'message': string;
}
/**
 * Represents a single task created during template execution.
 */
export interface TaskTemplateExecutionTask {
    /**
     * Gets or sets the unique identifier of the created task.
     */
    'taskId': string;
    /**
     * Gets or sets the key from the original TaskTemplateTask that was used to create this task.
     */
    'key': string;
}
/**
 * Represents an external entity variable used in a Task Template.
 */
export interface TaskTemplateExternalEntityVariable extends TaskTemplateVariable {
    /**
     * Gets or sets the type of the entity type.
     */
    'entityType': string;
}
/**
 * Represents a paginated response containing a page of items
 */
export interface TaskTemplateIPaginatedResponse {
    /**
     * The zero-based page index
     */
    'pageIndex'?: number;
    /**
     * Need to return page size as the server may disallow passing an arbitrarily large value
     */
    'pageSize'?: number;
    /**
     * Whether there is a previous page
     */
    'hasPreviousPage'?: boolean;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<TaskTemplate>;
    /**
     * Total count of items across pages. Only returned when requested.
     */
    'totalCount'?: number | null;
}
/**
 * Represents a task template variable that holds a numeric value.
 */
export interface TaskTemplateNumberVariable extends TaskTemplateVariable {
}
/**
 * Represents a relationship type for a task template.
 */
export interface TaskTemplateRelation {
    /**
     * Gets or sets the type for the relation.
     */
    'relationType': string;
    /**
     * Gets or sets the task for the relation.
     */
    'taskKey': string;
}
/**
 * Represents a rich text variable within a task template.
 */
export interface TaskTemplateRichTextVariable extends TaskTemplateVariable {
}
/**
 * Represents a status variable within a task template.
 */
export interface TaskTemplateStatusVariable extends TaskTemplateVariable {
}
/**
 * Represents a summary response containing lightweight information about a Task Template.
 */
export interface TaskTemplateSummary {
    /**
     * Gets or sets the unique identifier for the Task Template.
     */
    'id': string;
    /**
     * Gets or sets the name of the Task Template.
     */
    'name': string;
    /**
     * Gets or sets the description of the Task Template.
     */
    'description': string;
    /**
     * Gets or sets the Board Keys of the Task Template.
     */
    'applicableBoards'?: Array<string>;
    /**
     * Gets or sets the list of applicable entity types for the Task Template.
     */
    'applicableEntityTypes': Array<string>;
    /**
     * Gets or sets a value indicating whether the Task Template is disabled.
     */
    'disabled'?: boolean;
    /**
     * The UTC date and time the Task Template was last updated.
     */
    'lastUpdatedAt': string;
    /**
     * Gets or sets the display name of the user who last changed the Task Template.
     */
    'lastChangedByDisplayName'?: string | null;
    /**
     * Gets or sets the count of tasks in the Task Template.
     */
    'taskCount'?: number;
    /**
     * Indicates whether the Task Template is a system template.
     */
    'isSystem'?: boolean;
}
/**
 * Represents a paginated response containing a page of items
 */
export interface TaskTemplateSummaryIPaginatedResponse {
    /**
     * The zero-based page index
     */
    'pageIndex'?: number;
    /**
     * Need to return page size as the server may disallow passing an arbitrarily large value
     */
    'pageSize'?: number;
    /**
     * Whether there is a previous page
     */
    'hasPreviousPage'?: boolean;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<TaskTemplateSummary>;
    /**
     * Total count of items across pages. Only returned when requested.
     */
    'totalCount'?: number | null;
}
/**
 * Represents a task used in a Task Template.
 */
export interface TaskTemplateTask {
    /**
     * Gets or sets the unique identifier for the task.
     */
    'key': string;
    /**
     * Gets or sets the name of the task.
     */
    'name': string;
    /**
     * Gets or sets the description of the task.
     */
    'description'?: string | null;
    /**
     * Gets or sets the type of the task.
     */
    'type': string;
    /**
     * A dictionary of field IDs and their corresponding values.
     */
    'fields': { [key: string]: any; };
    /**
     * A collection of relationships associated with the task.
     */
    'relationships': Array<TaskTemplateRelation>;
    /**
     * An optional collection of checklists to include in the task.
     */
    'checklists': Array<TaskTemplateChecklist>;
    /**
     * A dictionary of field IDs and their corresponding triggers.
     */
    'triggers'?: { [key: string]: TaskTrigger; };
}
/**
 * Represents a variable to a specific task within a task template.
 */
export interface TaskTemplateTaskVariable extends TaskTemplateVariable {
}
/**
 * Represents a text variable within a task template.
 */
export interface TaskTemplateTextVariable extends TaskTemplateVariable {
}
/**
 * Represents a timestamp variable within a task template.
 */
export interface TaskTemplateTimestampVariable extends TaskTemplateVariable {
}
/**
 * Represents a user-defined variable within a task template.
 */
export interface TaskTemplateUserVariable extends TaskTemplateVariable {
}
/**
 * Represents a variable used in a Task Template.
 */
export interface TaskTemplateVariable {
    'type': string;
    /**
     * Gets or sets the unique identifier for the variable.
     */
    'name': string;
    /**
     * Gets or sets the description of the variable.
     */
    'description': string;
    /**
     * Gets or sets a value indicating whether the variable is required.
     */
    'isRequired': boolean;
    /**
     * Gets or sets the default value for the variable.
     */
    'defaultValue'?: any;
}
/**
 * @type TaskTemplateVariablesInner
 * Represents a variable used in a Task Template.
 */
export type TaskTemplateVariablesInner = TaskTemplateCheckboxVariable | TaskTemplateDateVariable | TaskTemplateDropdownVariable | TaskTemplateExternalEntityVariable | TaskTemplateNumberVariable | TaskTemplateRichTextVariable | TaskTemplateStatusVariable | TaskTemplateTaskVariable | TaskTemplateTextVariable | TaskTemplateTimestampVariable | TaskTemplateUserVariable | TaskTemplateVariable;

/**
 * Represents a field trigger that determines when a rule should be triggered.
 */
export interface TaskTrigger {
    /**
     * The template that determines the value to be set when the rule triggers.
     */
    'valueTemplate': string;
    /**
     * The template that determines if the rule should trigger.
     */
    'triggerTemplate': string;
}
/**
 * Represents a change to automation triggers (added, modified, or removed).
 */
export interface TaskTriggerChangeResponse extends ITaskChange {
    /**
     * Gets or sets the type of change: \"added\", \"modified\", or \"removed\".
     */
    'changeType': string;
    /**
     * Gets or sets the trigger details before the change. Null for Add operations. Contains triggerId, configuration properties.
     */
    'oldValue'?: TaskTrigger | null;
    /**
     * Gets or sets the trigger details after the change. Null for Remove operations. Contains triggerId, configuration properties.
     */
    'newValue'?: TaskTrigger | null;
}
/**
 * Represents a custom Task Type
 */
export interface TaskType {
    /**
     * Workspace identifier the Task Type is associated with
     */
    'workspaceId'?: string;
    /**
     * Task Type identifier, unique across the Workspace + Namespace
     */
    'id': string;
    /**
     * Task Type\'s name
     */
    'name': string;
    /**
     * Task Type\'s custom Fields
     */
    'fields': Set<UpsertCustomField200Response>;
    /**
     * Indicates whether the Task Type is disabled
     */
    'disabled': boolean;
    /**
     * The auto correlation configuration for the Task Type.
     */
    'autoCorrelation'?: TaskTypeAutoCorrelation | null;
    /**
     * The display configuration for the Task Type.
     */
    'display'?: TaskTypeDisplayConfiguration | null;
    /**
     * If set to true indicates that the Task Type doesn\'t have checklists
     */
    'disallowChecklists'?: boolean | null;
    /**
     * If set to true indicates that the Task Type doesn\'t have communication hub
     */
    'disallowComms'?: boolean | null;
    /**
     * If set to true indicates that the Task Type doesn\'t have attachments
     */
    'disallowAttachments'?: boolean | null;
    /**
     * When true, task dependencies are soft-blocking — tasks can still change status even when blocked.
     */
    'softBlockingDependencies'?: boolean | null;
}
/**
 * Represents a correlating configuration specifying the external entity type, relations, and mapping template details.
 */
export interface TaskTypeAutoCorrelation {
    /**
     * The external entity type to correlate with (e.g., jpm.job)
     */
    'externalEntityTypeId': string;
    /**
     * Template-driven field mapping using the field ID as the key and a template as the value. External entity properties may be inserted using the entity template variable (e.g. \"{{ entity.name }}\")
     */
    'fieldMaps': { [key: string]: string; };
    /**
     * The relations to the current task(s)
     */
    'relations': Array<TaskTypeRelation>;
}
/**
 * Represents the configuration for a Task Type
 */
export interface TaskTypeConfiguration {
    /**
     * Task Type\'s name
     */
    'name': string;
    /**
     * Key generation configuration for the Task Type.
     */
    'keyConfiguration'?: TaskTypeKeyConfiguration | null;
    /**
     * Task Type\'s custom Fields
     */
    'fields': Set<BoardConfigurationFieldOverridesInner>;
    /**
     * Indicates whether the Task Type is disabled
     */
    'disabled': boolean;
    /**
     * The auto correlation configuration for the Task Type.
     */
    'autoCorrelation'?: TaskTypeAutoCorrelation | null;
    /**
     * The display configuration for the Task Type.
     */
    'display'?: TaskTypeDisplayConfiguration | null;
    /**
     * If set to true indicates that the Task Type doesn\'t have checklists
     */
    'disallowChecklists'?: boolean | null;
    /**
     * If set to true indicates that the Task Type doesn\'t have communication hub
     */
    'disallowComms'?: boolean | null;
    /**
     * If set to true indicates that the Task Type doesn\'t have attachments
     */
    'disallowAttachments'?: boolean | null;
    /**
     * Workspace identifier the Task Type is associated with
     */
    'workspaceId'?: string;
    /**
     * Task Type identifier, unique across the Workspace
     */
    'id': string;
}
/**
 * Represents display configuration for a Task Type
 */
export interface TaskTypeDisplayConfiguration {
    /**
     * Default display configuration
     */
    'default': TaskTypeDisplayLayout;
    /**
     * Mobile display configuration
     */
    'mobile'?: TaskTypeDisplayLayout | null;
    /**
     * Display configuration for templates for this Task Type
     */
    'template'?: TaskTypeTemplateDisplayLayout | null;
}
/**
 * Represents a field to be displayed in a Task Type
 */
export interface TaskTypeDisplayField {
    /**
     * Field identifier. Mutually exclusive with ServiceTitan.TaskHub.Client.Contracts.TaskTypeDisplayField.Source.
     */
    'fieldId'?: string | null;
    /**
     * Optional icon for the field
     */
    'icon'?: string | null;
    /**
     * URL to a micro-frontend that renders a custom field display. Mutually exclusive with ServiceTitan.TaskHub.Client.Contracts.TaskTypeDisplayField.FieldId.
     */
    'source'?: string | null;
}
/**
 * Represents display layout configuration for a Task Type
 */
export interface TaskTypeDisplayLayout {
    /**
     * Heading zone configuration (fields only)
     */
    'heading'?: TaskTypeDisplayZone | null;
    /**
     * Main zone configuration (fields only)
     */
    'main'?: TaskTypeDisplayZone | null;
    /**
     * Secondary zone configuration (sections only)
     */
    'secondary'?: TaskTypeSectionedDisplayZone | null;
}
/**
 * Represents display layout overrides for a Task Type.
 */
export interface TaskTypeDisplayLayoutOverride {
    /**
     * The secondary sections to display for the Task Type.
     */
    'secondary'?: Array<TaskTypeDisplaySection> | null;
}
/**
 * Represents a section in a Task Type display configuration
 */
export interface TaskTypeDisplaySection {
    /**
     * Title of the section
     */
    'title'?: string | null;
    /**
     * Fields to display in this section
     */
    'fields'?: Array<TaskTypeDisplayField> | null;
    /**
     * URL or identifier for remote content (e.g., MFE) to render in this section. Mutually exclusive with Fields - a section can have either Fields or Source, but not both.
     */
    'source'?: string | null;
    /**
     * Subsections within this section
     */
    'subsections'?: Array<TaskTypeDisplaySection> | null;
}
/**
 * Represents a display zone containing only fields (no sections)
 */
export interface TaskTypeDisplayZone {
    /**
     * Fields to display in this zone
     */
    'fields'?: Array<TaskTypeDisplayField> | null;
}
/**
 * Represents a paginated response containing a page of items
 */
export interface TaskTypeIPaginatedResponse {
    /**
     * The zero-based page index
     */
    'pageIndex'?: number;
    /**
     * Need to return page size as the server may disallow passing an arbitrarily large value
     */
    'pageSize'?: number;
    /**
     * Whether there is a previous page
     */
    'hasPreviousPage'?: boolean;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<TaskType>;
    /**
     * Total count of items across pages. Only returned when requested.
     */
    'totalCount'?: number | null;
}
/**
 * Configures key generation for a task type.
 */
export interface TaskTypeKeyConfiguration {
    /**
     * True if key generation is enabled for this task type, false otherwise.
     */
    'enabled': boolean;
    /**
     * Represents the scope being used for task key generation. Required if key generation is enabled. Options are: Workspace - the key is unique within the workspace. Board - the key is unique within the board. Field - the key is unique within the scope of a specific field value. The field is configured via FieldId property.
     */
    'scope'?: string | null;
    /**
     * Field that is being used for key generation when Field scope is being used. Required if the scope is set to Field.
     */
    'fieldId'?: string | null;
    /**
     * String prefix being used for keys of the task type. Required if key generation is enabled.
     */
    'prefix'?: string | null;
}
/**
 * Represents overrides for a specific Task Type.
 */
export interface TaskTypeOverride {
    /**
     * The unique identifier for the Task Type.
     */
    'taskTypeId': string;
    /**
     * The unique identifier for the Task Type.
     */
    'nameOverride'?: string | null;
    /**
     * The set of field override configurations for this Task Type, keyed by field name.
     */
    'fieldOverrides': Set<BoardConfigurationFieldOverridesInner>;
    /**
     * The default display layout overrides for the Task Type.
     */
    'default'?: TaskTypeDisplayLayoutOverride | null;
    /**
     * The mobile display layout overrides for the Task Type.
     */
    'mobile'?: TaskTypeDisplayLayoutOverride | null;
}
/**
 * Represents a relation for a correlating with a given task type.
 */
export interface TaskTypeRelation {
    /**
     * The task type ID
     */
    'taskTypeId': string;
    /**
     * The type of relation (e.g. sys.childOf)
     */
    'relationType': string;
}
/**
 * Represents a sectioned display zone containing sections (no top-level fields)
 */
export interface TaskTypeSectionedDisplayZone {
    /**
     * Sections to display in this zone
     */
    'sections'?: Array<TaskTypeDisplaySection> | null;
}
/**
 * Represents display layout configuration for a template for a Task Type
 */
export interface TaskTypeTemplateDisplayLayout {
    /**
     * Main zone configuration (fields only)
     */
    'main'?: TaskTypeDisplayZone | null;
    /**
     * If set to true, hides the dependencies section from being rendered
     */
    'hideDependencies'?: boolean | null;
}
/**
 * Represents a User associated with Tasks
 */
export interface TaskUser {
    /**
     * User\'s identifier
     */
    'userId'?: string;
    /**
     * Indicator if the User is mentioned in the Task
     */
    'mentioned'?: boolean;
    /**
     * Indicator if the User is participating in the Task
     */
    'participating'?: boolean;
    /**
     * Indicator if the User is watching the Task
     */
    'watching'?: boolean;
    /**
     * Indicator if the User starred the Task
     */
    'starred'?: boolean;
}
/**
 * Represents a change to task user associations (added, modified, or removed).
 */
export interface TaskUserChangeResponse extends ITaskChange {
    /**
     * Gets or sets the type of change: \"added\", \"modified\", or \"removed\".
     */
    'changeType': string;
    /**
     * Gets or sets the user association details before the change. Null for Add operations. Contains userId and association flags (mentioned, participating, watching, starred).
     */
    'oldValue'?: TaskUser | null;
    /**
     * Gets or sets the user association details after the change. Null for Remove operations. Contains userId and association flags (mentioned, participating, watching, starred).
     */
    'newValue'?: TaskUser | null;
}
/**
 * Response model for updating multiple tasks.
 */
export interface TasksUpdateResponse {
    /**
     * Number of tasks successfully updated.
     */
    'updated'?: number;
    /**
     * Errors encountered during the update operation.
     */
    'errors'?: Array<string>;
}
/**
 * Represents a field of type \"text\".
 */
export interface TextField extends Field {
}
/**
 * Represents a text field value.
 */
export interface TextFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': string;
}
/**
 * Represents a field of type \"timestamp\".
 */
export interface TimestampField extends Field {
}
/**
 * Represents a timestamp field value.
 */
export interface TimestampFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': string;
}
/**
 * Represents a request to create or update workspace-specific Board configuration.
 */
export interface UpdateBoardConfigurationRequest {
    /**
     * The configuration for the Board, including field overrides.
     */
    'configuration': BoardConfiguration;
}
/**
 * Represents a request to update Task details.
 */
export interface UpdateTaskDetailsRequest {
    /**
     * Gets or sets the field values associated with the Task.  Set the value to null to remove the field.
     */
    'fieldValues'?: { [key: string]: NullableFieldValue; };
    /**
     * Gets or sets the labels to add to (`true`) or remove (`false`) from the Task.
     */
    'labels'?: { [key: string]: boolean; };
    /**
     * Get or sets all the checklists associated with the Task.
     */
    'checklists'?: Array<Checklist> | null;
    /**
     * Gets or sets the list of user ids to add or update as mentioned.
     */
    'userIdsMentioned'?: Array<string> | null;
}
/**
 * Represents a request to update a new Task.
 */
export interface UpdateTaskRequest {
    /**
     * Gets or sets the field values associated with the Task.  Set the value to null to remove the field.
     */
    'fieldValues'?: { [key: string]: NullableFieldValue; };
    /**
     * Gets or sets the labels to add to (`true`) or remove (`false`) from the Task.
     */
    'labels'?: { [key: string]: boolean; };
    /**
     * Get or sets all the checklists associated with the Task.
     */
    'checklists'?: Array<Checklist> | null;
    /**
     * Gets or sets the list of user ids to add or update as mentioned.
     */
    'userIdsMentioned'?: Array<string> | null;
    /**
     * Gets or sets the entity tag for optimistic concurrency control. When provided, the update will only succeed if the current ETag of the Task matches this value. If the ETag does not match (indicating the Task has been modified since it was last read), the server will respond with 412 Precondition Failed.
     */
    'eTag'?: string | null;
}
/**
 * Represents a request to update multiple Tasks based on filtering rules.
 */
export interface UpdateTasksRequest {
    /**
     * Collection of filtering rules. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters': Array<TaskFilter>;
    /**
     * The update request details.
     */
    'updateTaskRequest': UpdateTaskDetailsRequest;
}
/**
 * Represents a request to create or update a Board Type
 */
export interface UpsertBoardTypeRequest {
    /**
     * The name of the Board Type
     */
    'name': string;
    /**
     * Indicates whether the Board Type is disabled
     */
    'disabled'?: boolean;
    /**
     * Whether boards inheriting from this Board Type are scoped (one per workspace).
     */
    'scoped'?: boolean;
    /**
     * When true, accessing an unresolved boardKey no longer auto-provisions a new board instance for this Board Type — instances must be created explicitly.
     */
    'disableAutoProvision'?: boolean;
    /**
     * When true, a board instance\'s own configuration is not merged in — the Board Type\'s configuration is authoritative.
     */
    'isLocked'?: boolean;
    /**
     * States whether this Board Type\'s tasks belong in a workspace-wide, board-less query result: `false` (the default) means they participate, `true` means they are excluded unless the query names the board. Retroactive, not an access control, and does not affect a query that names the board.
     */
    'includedOnlyWhenRequested'?: boolean;
    /**
     * The optional configuration for the Board Type, including field and task type overrides.
     */
    'configuration'?: BoardConfiguration | null;
    /**
     * The Task Types permitted for creation on Boards of this Board Type. An empty or omitted list means unrestricted -- any Task Type may be used. Omitting the property (as opposed to sending an empty list) leaves a workspace-level override unset, so it inherits the global Board Type\'s list.
     */
    'allowedTaskTypes'?: Set<AllowedTaskType> | null;
}
/**
 * Represents a request to create or update a checkbox field.
 */
export interface UpsertCheckboxFieldRequest extends UpsertFieldRequest {
}


/**
 * Represents a request to create or update a computed property.
 */
export interface UpsertComputedPropertyFieldRequest extends UpsertFieldRequest {
    /**
     * Gets or sets the template used to calculate the
     */
    'template': string;
    /**
     * Gets or sets the output json schema for the computed value.
     */
    'outputSchema': JsonSchema;
    /**
     * Gets or sets the evaluation mode for the computed value.
     */
    'evaluationMode': UpsertComputedPropertyFieldRequestEvaluationModeEnum;
}

export const UpsertComputedPropertyFieldRequestEvaluationModeEnum = {
    Runtime: 'Runtime',
    Stored: 'Stored',
} as const;

export type UpsertComputedPropertyFieldRequestEvaluationModeEnum = typeof UpsertComputedPropertyFieldRequestEvaluationModeEnum[keyof typeof UpsertComputedPropertyFieldRequestEvaluationModeEnum];

/**
 * Represents a Context Configuration Update Request
 */
export interface UpsertContextConfigurationRequest {
    /**
     * Indicates whether the Context Configuration is disabled
     */
    'disabled': boolean;
    /**
     * Board identifier the Context Configuration is associated with (single-board surface). Optional and mutually exclusive with BoardKeys; carries cardinality only - see BoardMode for how the board set is determined, including what \"neither BoardKey nor BoardKeys\" means.
     */
    'boardKey'?: string | null;
    /**
     * Known set of board identifiers the Context Configuration is scoped to (multi-board surface). Optional and mutually exclusive with the single-board BoardKey; carries cardinality only - see BoardMode for how the board set is determined, including what \"neither BoardKey nor BoardKeys\" means. Setting both BoardKey and BoardKeys, or an empty BoardKeys set, is rejected at upsert.
     */
    'boardKeys'?: Array<string> | null;
    /**
     * How the board set is determined: \"explicit\" (BoardKey/BoardKeys), \"dynamic\" (a board supplied by the host at load, no keys on the config), or \"all_boards\" (no board set at all, the workspace-wide query). Optional - when omitted, resolves to \"explicit\" if keys are present, otherwise \"dynamic\". \"all_boards\" is never inferred; it is reachable only by stating it.
     */
    'boardMode'?: string | null;
    /**
     * Task Types associated with the Context Configuration
     */
    'taskTypeIds': Array<string>;
    /**
     * Filters applied to all pages in the context configuration
     */
    'filters': Array<TaskFilter>;
    /**
     * Context Configuration title. Optional - when null, the context configuration header is hidden in the UI.
     */
    'title'?: string | null;
    /**
     * User-specific filters, including quick access and all available filters
     */
    'userFilters': UpsertUserContextConfigurationFilter;
    /**
     * Pages within the context configuration
     */
    'pages': Array<UpsertContextConfigurationRequestPagesInner>;
}
/**
 * @type UpsertContextConfigurationRequestPagesInner
 * A page within a Context Configuration used in upsert requests
 */
export type UpsertContextConfigurationRequestPagesInner = ContextConfigurationPageInput | GridConfigurationPageInput | MasterDetailConfigurationPageInput | MobileListConfigurationPageInput;

/**
 * @type UpsertCustomField200Response
 * Represents the definition of a field that can be attached to a Task.
 */
export type UpsertCustomField200Response = CheckboxField | ComputedField | DateField | DropdownField | ExternalEntityPropertyField | ExternalEntityTypeField | Field | JsonField | NumberField | ReferenceField | RichTextField | StatusField | TaskField | TextField | TimestampField | UserField;

/**
 * @type UpsertCustomFieldRequest
 * Represents a request to create or update a custom field in TaskHub.
 */
export type UpsertCustomFieldRequest = UpsertCheckboxFieldRequest | UpsertComputedPropertyFieldRequest | UpsertDateFieldRequest | UpsertDropdownFieldRequest | UpsertExternalEntityFieldRequest | UpsertExternalEntityPropertyFieldRequest | UpsertFieldRequest | UpsertJsonFieldRequest | UpsertNumberFieldRequest | UpsertReferenceFieldRequest | UpsertRichTextFieldRequest | UpsertStatusFieldRequest | UpsertTaskFieldRequest | UpsertTextFieldRequest | UpsertTimestampFieldRequest | UpsertUserFieldRequest;

/**
 * Represents a request to create or update a date field.
 */
export interface UpsertDateFieldRequest extends UpsertFieldRequest {
}


/**
 * Represents a request to create or update a dropdown field.
 */
export interface UpsertDropdownFieldRequest extends UpsertFieldRequest {
    /**
     * Gets or sets the list of selectable options for the dropdown field.
     */
    'valueOptions'?: Array<FieldValueOption>;
    /**
     * Gets or sets a value indicating whether multiple options can be selected for this dropdown field.
     */
    'allowMultiChoice'?: boolean;
}


/**
 * Represents a request to create or update an external entity field.
 */
export interface UpsertExternalEntityFieldRequest extends UpsertFieldRequest {
    /**
     * Gets or sets the external entity type associated with this field when the field type is \"external_entity\".
     */
    'entityType': string;
    /**
     * Gets or sets an optional collection of external entity fields and their properties to use as values for this field in order of priority.
     */
    'valueSources'?: Array<ExternalEntityFieldValueSource> | null;
}


/**
 * Represents a request to create or update an external entity property field.
 */
export interface UpsertExternalEntityPropertyFieldRequest extends UpsertFieldRequest {
    /**
     * Gets or sets the collection of external entity fields and their properties to use as values for this field in order of priority.
     */
    'valueSources'?: Array<ExternalEntityFieldValueSource>;
}


/**
 * Represents an External Entity Type that defines External Entity Properties
 */
export interface UpsertExternalEntityTypeRequest {
    /**
     * External Entity Type name
     */
    'name': string;
    /**
     * Flag indicates if External Entity Type should not be used anymore
     */
    'disabled'?: boolean;
    /**
     * External Entity Type schema
     */
    'schema': ExternalEntityTypeSchema;
    /**
     * External Entity Type provider
     */
    'provider'?: ExternalEntityTypeProvider | null;
    /**
     * Scriban template for the link to an External Entity of this type. Variables: entity, monolithBaseUri.
     */
    'linkTemplate'?: string | null;
}
/**
 * Represents a request to create or update a custom field in TaskHub.
 */
export interface UpsertFieldRequest {
    'type': string;
    /**
     * Gets or sets the display name of the field.
     */
    'name': string;
    /**
     * Gets or sets a value indicating whether this field is required on tasks that use it.
     */
    'required'?: boolean;
    /**
     * Set to `true` to prevent this field from being used in new scenarios such as being added to a task type. The field will continue to be used in current scenarios such as existing task types and tasks. Leave null to keep the current value.
     */
    'disabled'?: boolean;
    /**
     * Gets or sets the access level for setting the value of this field.
     */
    'settableBy'?: UpsertFieldRequestSettableByEnum;
    /**
     * Sort method for the Field.
     */
    'sortMethod'?: string;
    /**
     * Optional feature gate configuration. When present, the field is only visible to orgs whose settings satisfy the configured gates.
     */
    'featureGates'?: FeatureGateConfiguration | null;
    /**
     * When `true`, marks this field as for internal system use only. Internal fields are excluded entirely from list and get-by-id responses for callers without the `st.task_hub.global.write` scope; unprivileged callers never observe this property set to `true`. Only allowed on global-workspace fields and only settable by callers with the required scope; requests that violate either constraint are rejected with HTTP 400. Omit (or send `null`) to preserve the current value on updates; defaults to `false` on create. Changing the flag in either direction requires the required scope.
     */
    'internal'?: boolean | null;
    /**
     * When `true`, this field\'s values are matched by task search queries on every task type that uses the field. A task type may override this via its field override. Only field types with meaningful text values may be marked searchable: text, dropdown, status, external_entity, external_entity_property, and user; requests setting `true` on other field types are rejected. Omit (or send `null`) to preserve the current value on updates; defaults to `false` on create.
     */
    'searchable'?: boolean | null;
}

export const UpsertFieldRequestSettableByEnum = {
    Public: 'Public',
    System: 'System',
} as const;

export type UpsertFieldRequestSettableByEnum = typeof UpsertFieldRequestSettableByEnum[keyof typeof UpsertFieldRequestSettableByEnum];

/**
 * Represents a request to create or update a JSON field.
 */
export interface UpsertJsonFieldRequest extends UpsertFieldRequest {
    /**
     * MFE configuration for rendering this JSON field.
     */
    'drawerMfe'?: JsonFieldDrawerMfeConfig | null;
}


/**
 * Represents common Label properties
 */
export interface UpsertLabelRequest {
    /**
     * Label\'s name
     */
    'name': string;
    /**
     * A color associated with this Label
     */
    'color'?: string | null;
    /**
     * An icon associated with this Label
     */
    'icon'?: Icon | null;
    /**
     * Indicates whether the Label is disabled
     */
    'disabled': boolean;
}
/**
 * Represents a request to create or update a number field.
 */
export interface UpsertNumberFieldRequest extends UpsertFieldRequest {
    /**
     * Gets or sets the numeric kind (default, percent).
     */
    'numericKind'?: string | null;
    /**
     * Gets or sets the desired decimal precision for display/UX (0 = integer-only, null = no preference).
     */
    'precision'?: number | null;
    /**
     * Gets or sets the minimum allowed value.
     */
    'min'?: number | null;
    /**
     * Gets or sets the maximum allowed value.
     */
    'max'?: number | null;
}


/**
 * Represents a request to create or update a Reference field.
 */
export interface UpsertReferenceFieldRequest extends UpsertFieldRequest {
}


/**
 * Represents a request to create or update a rich text field.
 */
export interface UpsertRichTextFieldRequest extends UpsertFieldRequest {
}


/**
 * Represents a request to create or update a status dropdown field.
 */
export interface UpsertStatusFieldRequest extends UpsertFieldRequest {
    /**
     * Gets or sets the list of selectable status options for the status dropdown field.
     */
    'statusValueOptions'?: Array<StatusFieldValueOption>;
}


/**
 * Represents a request to create or update a task field.
 */
export interface UpsertTaskFieldRequest extends UpsertFieldRequest {
}


/**
 * Represents a request to create a new Task Template.
 */
export interface UpsertTaskTemplateRequest {
    /**
     * Gets or sets the name of the Task Template.
     */
    'name': string;
    /**
     * Gets or sets the description of the Task Template.
     */
    'description': string;
    /**
     * Gets or sets the Board Key of the Task Template.
     */
    'applicableBoards'?: Array<string>;
    /**
     * Gets or sets the list of applicable entity types for the Task Template.
     */
    'applicableEntityTypes': Array<string>;
    /**
     * Gets or sets the list of applicable entity IDs for the Task Template.
     */
    'variables': Array<TaskTemplateVariablesInner>;
    /**
     * Gets or sets the list of tasks associated with the Task Template.
     */
    'tasks': Array<TaskTemplateTask>;
    /**
     * Gets or sets a value indicating whether the Task Template is disabled.
     */
    'disabled'?: boolean;
    /**
     * Gets or sets the reference to source from which this task template was copied, if any.
     */
    'copiedFrom'?: TaskTemplateCopiedFromReference | null;
}
/**
 * Represents a request to update existing Task Types
 */
export interface UpsertTaskTypeRequest {
    /**
     * Task Type\'s name
     */
    'name': string;
    /**
     * Key generation configuration for the Task Type.
     */
    'keyConfiguration'?: TaskTypeKeyConfiguration | null;
    /**
     * Task Type\'s custom Fields
     */
    'fields': Set<BoardConfigurationFieldOverridesInner>;
    /**
     * Indicates whether the Task Type is disabled
     */
    'disabled': boolean;
    /**
     * The auto correlation configuration for the Task Type.
     */
    'autoCorrelation'?: TaskTypeAutoCorrelation | null;
    /**
     * The display configuration for the Task Type.
     */
    'display'?: TaskTypeDisplayConfiguration | null;
    /**
     * If set to true indicates that the Task Type doesn\'t have checklists
     */
    'disallowChecklists'?: boolean | null;
    /**
     * If set to true indicates that the Task Type doesn\'t have communication hub
     */
    'disallowComms'?: boolean | null;
    /**
     * If set to true indicates that the Task Type doesn\'t have attachments
     */
    'disallowAttachments'?: boolean | null;
}
/**
 * Represents a request to create or update a text field.
 */
export interface UpsertTextFieldRequest extends UpsertFieldRequest {
}


/**
 * Represents a request to create or update a timestamp field.
 */
export interface UpsertTimestampFieldRequest extends UpsertFieldRequest {
}


/**
 * User-specific filters for upsert requests, including quick access and all available filters
 */
export interface UpsertUserContextConfigurationFilter {
    /**
     * Quick access filters that the user has marked for easy access
     */
    'quickAccess': Array<string>;
    /**
     * Initial filter to apply when the user first accesses the page
     */
    'initial': Array<TaskFilter>;
    /**
     * All filters available to the user
     */
    'all': Array<NamedFilterInput>;
}
/**
 * Represents a request to create or update a user field.
 */
export interface UpsertUserFieldRequest extends UpsertFieldRequest {
}


/**
 * Represents a User details
 */
export interface User {
    /**
     * User\'s identifier
     */
    'userId'?: string;
    /**
     * Display name of the User
     */
    'displayName': string;
    /**
     * User\'s initials
     */
    'initials': string;
    /**
     * User\'s email address.  Can be empty.
     */
    'email': string;
}
/**
 * User-specific filters, including quick access and all available filters
 */
export interface UserContextConfigurationFilter {
    /**
     * Quick access filters that the user has marked for easy access
     */
    'quickAccess': Array<string>;
    /**
     * Initial filter to apply when the user first accesses the page
     */
    'initial': Array<TaskFilter>;
    /**
     * All filters available to the user
     */
    'all': Array<NamedFilter>;
}
/**
 * Represents a field of type \"user\", used to reference a user entity.
 */
export interface UserField extends Field {
}
/**
 * Represents additional details of a specific field of \"user\" type.
 */
export interface UserFieldValue extends ResponseFieldValue {
    /**
     * Gets or sets the value of the field.
     */
    'value': string;
    /**
     * Returns full name of the user.
     */
    'displayName'?: string;
}
/**
 * Represents a Continuation response containing a page of items
 */
export interface UserIContinuationResponse {
    /**
     * Continuation Token from the previous call of this request
     */
    'continuationToken'?: string | null;
    /**
     * Desired page size to return
     */
    'pageSize'?: number;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<User>;
}
/**
 * Represents a paginated response containing a page of items
 */
export interface UserIPaginatedResponse {
    /**
     * The zero-based page index
     */
    'pageIndex'?: number;
    /**
     * Need to return page size as the server may disallow passing an arbitrarily large value
     */
    'pageSize'?: number;
    /**
     * Whether there is a previous page
     */
    'hasPreviousPage'?: boolean;
    /**
     * Whether there is a next page
     */
    'hasNextPage'?: boolean;
    /**
     * The items in the current page
     */
    'items'?: Array<User>;
    /**
     * Total count of items across pages. Only returned when requested.
     */
    'totalCount'?: number | null;
}
/**
 * A request to retrieve metrics for tasks in a workspace with optional board filtering.
 */
export interface WorkspaceMetricsRequest {
    /**
     * Optional collection of board keys to filter by. At most 100 keys. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`board_keys`). If null, metrics will be calculated across all boards in the workspace. If empty, no boards will match (returns no results).
     */
    'boardKeys'?: Array<string> | null;
    /**
     * Collection of filtering rules to apply when calculating metrics. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<TaskFilter> | null;
}
/**
 * Represents a Workspace, container for Task Boards
 */
export interface WorkspaceResponse {
    /**
     * Unique identifier for the Workspace
     */
    'workspaceId'?: string;
    /**
     * A list of Labels associated with this Workspace
     */
    'labels'?: Array<LabelReference>;
    /**
     * A color associated with this Workspace
     */
    'color'?: string | null;
    /**
     * A photo associated with this Workspace
     */
    'photo'?: Photo | null;
    /**
     * Represents a list of External Entities that should be associated with this Workspace
     */
    'externalEntities'?: Array<ExternalEntityReference>;
}
/**
 * A filter query to search Tasks across boards, paginated by continuation token. Suited to iterating every Task that matches a query; use ServiceTitan.TaskHub.Client.Contracts.WorkspaceTasksQuery to jump to an arbitrary page.
 */
export interface WorkspaceTasksContinuationQuery {
    /**
     * Collection of filtering rules. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<TaskFilter> | null;
    /**
     * The opaque token returned by the previous page\'s response. Omit to request the first page. A token is only valid with the same filters, sort, and search term that produced it; otherwise the request is rejected. Formula filters are re-evaluated on every request, so prefer literal values when walking every page.
     */
    'continuationToken'?: string | null;
    /**
     * The size of the page of Tasks to return. Every page but the last contains exactly this many Tasks.
     */
    'pageSize'?: number | null;
    /**
     * Details on how the query should sort results
     */
    'sort'?: TaskQuerySort | null;
    /**
     * Optional secondary sorting criteria for the query. Requires DB support for this feature. Multiple sorting is only supported for \'sys.due_date\' / \'sys.created_at\' fields (both ASC or both DESC) and for \'sys.priority\' DESC / \'sys.created_at\' ASC.
     */
    'secondarySort'?: TaskQuerySort | null;
    /**
     * Optional free-text search term. When provided, results are narrowed to tasks whose searchable field content matches the term.
     */
    'searchTerm'?: string | null;
    /**
     * Boards Ids to filter by. At most 100 keys. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`board_keys`).
     */
    'boardsKeys'?: Array<string>;
    /**
     * Indicates if scoped tasks should be included in search results.
     */
    'includeScopedTasks'?: boolean;
}
/**
 * A filter query to search and filter Tasks count by Board Ids and Filters.
 */
export interface WorkspaceTasksCountQuery {
    /**
     * Collection of filtering rules. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<TaskFilter> | null;
    /**
     * Optional free-text search term. When provided, the count is narrowed to tasks whose searchable field content matches the term.
     */
    'searchTerm'?: string | null;
    /**
     * Boards Ids to filter by. At most 100 keys. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`board_keys`).
     */
    'boardsKeys'?: Array<string>;
}
/**
 * A filter query to search and filter Tasks by Org ID, Board Ids, Status Definition Ids, and associated External Entities.
 */
export interface WorkspaceTasksQuery {
    /**
     * Collection of filtering rules. At most 50 filters; each filter value list is at most 100. Exceeding either maximum returns HTTP 400 `API_LIMIT_EXCEEDED`.
     */
    'filters'?: Array<TaskFilter> | null;
    /**
     * Whether to include to the total count in the response
     */
    'includeTotalCount'?: boolean;
    /**
     * The index of the page of Tasks to return.
     */
    'pageIndex'?: number;
    /**
     * The size of the page of Tasks to return.
     */
    'pageSize'?: number | null;
    /**
     * Details on how the query should sort results
     */
    'sort'?: TaskQuerySort | null;
    /**
     * Optional secondary sorting criteria for the query. Requires DB support for this feature. Multiple sorting is only supported for \'sys.due_date\' / \'sys.created_at\' fields (both ASC or both DESC) and for \'sys.priority\' DESC / \'sys.created_at\' ASC.
     */
    'secondarySort'?: TaskQuerySort | null;
    /**
     * Optional free-text search term. When provided, results are narrowed to tasks whose searchable field content matches the term.
     */
    'searchTerm'?: string | null;
    /**
     * Boards Ids to filter by. At most 100 keys. A larger collection returns HTTP 400 `API_LIMIT_EXCEEDED` (`board_keys`).
     */
    'boardsKeys'?: Array<string>;
    /**
     * Indicates if scoped tasks should be included in search results.
     */
    'includeScopedTasks'?: boolean;
}

/**
 * AttachmentsApi - axios parameter creator
 */
export const AttachmentsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Uploads an attachment to the given org.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAttachment: async (orgId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('createAttachment', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/attachments`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Uploads an attachment to the given board.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createBoardAttachment: async (orgId: string, boardKey: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('createBoardAttachment', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('createBoardAttachment', 'boardKey', boardKey)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/attachments`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AttachmentsApi - functional programming interface
 */
export const AttachmentsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AttachmentsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Uploads an attachment to the given org.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAttachment(orgId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AttachmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAttachment(orgId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AttachmentsApi.createAttachment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Uploads an attachment to the given board.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createBoardAttachment(orgId: string, boardKey: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AttachmentResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createBoardAttachment(orgId, boardKey, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AttachmentsApi.createBoardAttachment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AttachmentsApi - factory interface
 */
export const AttachmentsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AttachmentsApiFp(configuration)
    return {
        /**
         * 
         * @summary Uploads an attachment to the given org.
         * @param {AttachmentsApiCreateAttachmentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAttachment(requestParameters: AttachmentsApiCreateAttachmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<AttachmentResponse> {
            return localVarFp.createAttachment(requestParameters.orgId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Uploads an attachment to the given board.
         * @param {AttachmentsApiCreateBoardAttachmentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createBoardAttachment(requestParameters: AttachmentsApiCreateBoardAttachmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<AttachmentResponse> {
            return localVarFp.createBoardAttachment(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createAttachment operation in AttachmentsApi.
 */
export interface AttachmentsApiCreateAttachmentRequest {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for createBoardAttachment operation in AttachmentsApi.
 */
export interface AttachmentsApiCreateBoardAttachmentRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * AttachmentsApi - object-oriented interface
 */
export class AttachmentsApi extends BaseAPI {
    /**
     * 
     * @summary Uploads an attachment to the given org.
     * @param {AttachmentsApiCreateAttachmentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAttachment(requestParameters: AttachmentsApiCreateAttachmentRequest, options?: RawAxiosRequestConfig) {
        return AttachmentsApiFp(this.configuration).createAttachment(requestParameters.orgId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Uploads an attachment to the given board.
     * @param {AttachmentsApiCreateBoardAttachmentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createBoardAttachment(requestParameters: AttachmentsApiCreateBoardAttachmentRequest, options?: RawAxiosRequestConfig) {
        return AttachmentsApiFp(this.configuration).createBoardAttachment(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AuthApi - axios parameter creator
 */
export const AuthApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Retrieves basic details about the user\'s authenticated context.
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getInfo: async (apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/auth/info`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTitanGuid: async (apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/auth/titan-guid`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUsername: async (apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/auth/username`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AuthApi - functional programming interface
 */
export const AuthApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AuthApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Retrieves basic details about the user\'s authenticated context.
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getInfo(apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AuthInfo>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getInfo(apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthApi.getInfo']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTitanGuid(apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<string>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTitanGuid(apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthApi.getTitanGuid']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUsername(apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<string>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUsername(apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthApi.getUsername']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AuthApi - factory interface
 */
export const AuthApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AuthApiFp(configuration)
    return {
        /**
         * 
         * @summary Retrieves basic details about the user\'s authenticated context.
         * @param {AuthApiGetInfoRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getInfo(requestParameters: AuthApiGetInfoRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<AuthInfo> {
            return localVarFp.getInfo(requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AuthApiGetTitanGuidRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTitanGuid(requestParameters: AuthApiGetTitanGuidRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<string> {
            return localVarFp.getTitanGuid(requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {AuthApiGetUsernameRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUsername(requestParameters: AuthApiGetUsernameRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<string> {
            return localVarFp.getUsername(requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for getInfo operation in AuthApi.
 */
export interface AuthApiGetInfoRequest {
    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for getTitanGuid operation in AuthApi.
 */
export interface AuthApiGetTitanGuidRequest {
    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for getUsername operation in AuthApi.
 */
export interface AuthApiGetUsernameRequest {
    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * AuthApi - object-oriented interface
 */
export class AuthApi extends BaseAPI {
    /**
     * 
     * @summary Retrieves basic details about the user\'s authenticated context.
     * @param {AuthApiGetInfoRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getInfo(requestParameters: AuthApiGetInfoRequest = {}, options?: RawAxiosRequestConfig) {
        return AuthApiFp(this.configuration).getInfo(requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AuthApiGetTitanGuidRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getTitanGuid(requestParameters: AuthApiGetTitanGuidRequest = {}, options?: RawAxiosRequestConfig) {
        return AuthApiFp(this.configuration).getTitanGuid(requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {AuthApiGetUsernameRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getUsername(requestParameters: AuthApiGetUsernameRequest = {}, options?: RawAxiosRequestConfig) {
        return AuthApiFp(this.configuration).getUsername(requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * BoardTypesApi - axios parameter creator
 */
export const BoardTypesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Deletes a globally-defined board type.
         * @param {string} boardTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalBoardType: async (boardTypeId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'boardTypeId' is not null or undefined
            assertParamExists('deleteGlobalBoardType', 'boardTypeId', boardTypeId)
            const localVarPath = `/global/board-types/{boardTypeId}`
                .replace('{boardTypeId}', encodeURIComponent(String(boardTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a globally-defined board type by its id.
         * @param {string} boardTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalBoardType: async (boardTypeId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'boardTypeId' is not null or undefined
            assertParamExists('findGlobalBoardType', 'boardTypeId', boardTypeId)
            const localVarPath = `/global/board-types/{boardTypeId}`
                .replace('{boardTypeId}', encodeURIComponent(String(boardTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates or updates a globally-defined board type.
         * @param {string} boardTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertBoardTypeRequest} [upsertBoardTypeRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalBoardType: async (boardTypeId: string, apiVersion?: string, upsertBoardTypeRequest?: UpsertBoardTypeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'boardTypeId' is not null or undefined
            assertParamExists('upsertGlobalBoardType', 'boardTypeId', boardTypeId)
            const localVarPath = `/global/board-types/{boardTypeId}`
                .replace('{boardTypeId}', encodeURIComponent(String(boardTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertBoardTypeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * BoardTypesApi - functional programming interface
 */
export const BoardTypesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = BoardTypesApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Deletes a globally-defined board type.
         * @param {string} boardTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteGlobalBoardType(boardTypeId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteGlobalBoardType(boardTypeId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BoardTypesApi.deleteGlobalBoardType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a globally-defined board type by its id.
         * @param {string} boardTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findGlobalBoardType(boardTypeId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BoardTypeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findGlobalBoardType(boardTypeId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BoardTypesApi.findGlobalBoardType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates or updates a globally-defined board type.
         * @param {string} boardTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertBoardTypeRequest} [upsertBoardTypeRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertGlobalBoardType(boardTypeId: string, apiVersion?: string, upsertBoardTypeRequest?: UpsertBoardTypeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BoardTypeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertGlobalBoardType(boardTypeId, apiVersion, upsertBoardTypeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BoardTypesApi.upsertGlobalBoardType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * BoardTypesApi - factory interface
 */
export const BoardTypesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = BoardTypesApiFp(configuration)
    return {
        /**
         * 
         * @summary Deletes a globally-defined board type.
         * @param {BoardTypesApiDeleteGlobalBoardTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalBoardType(requestParameters: BoardTypesApiDeleteGlobalBoardTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteGlobalBoardType(requestParameters.boardTypeId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a globally-defined board type by its id.
         * @param {BoardTypesApiFindGlobalBoardTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalBoardType(requestParameters: BoardTypesApiFindGlobalBoardTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<BoardTypeResponse> {
            return localVarFp.findGlobalBoardType(requestParameters.boardTypeId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates or updates a globally-defined board type.
         * @param {BoardTypesApiUpsertGlobalBoardTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalBoardType(requestParameters: BoardTypesApiUpsertGlobalBoardTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<BoardTypeResponse> {
            return localVarFp.upsertGlobalBoardType(requestParameters.boardTypeId, requestParameters.apiVersion, requestParameters.upsertBoardTypeRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for deleteGlobalBoardType operation in BoardTypesApi.
 */
export interface BoardTypesApiDeleteGlobalBoardTypeRequest {
    readonly boardTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for findGlobalBoardType operation in BoardTypesApi.
 */
export interface BoardTypesApiFindGlobalBoardTypeRequest {
    readonly boardTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for upsertGlobalBoardType operation in BoardTypesApi.
 */
export interface BoardTypesApiUpsertGlobalBoardTypeRequest {
    readonly boardTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertBoardTypeRequest?: UpsertBoardTypeRequest
}

/**
 * BoardTypesApi - object-oriented interface
 */
export class BoardTypesApi extends BaseAPI {
    /**
     * 
     * @summary Deletes a globally-defined board type.
     * @param {BoardTypesApiDeleteGlobalBoardTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteGlobalBoardType(requestParameters: BoardTypesApiDeleteGlobalBoardTypeRequest, options?: RawAxiosRequestConfig) {
        return BoardTypesApiFp(this.configuration).deleteGlobalBoardType(requestParameters.boardTypeId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a globally-defined board type by its id.
     * @param {BoardTypesApiFindGlobalBoardTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findGlobalBoardType(requestParameters: BoardTypesApiFindGlobalBoardTypeRequest, options?: RawAxiosRequestConfig) {
        return BoardTypesApiFp(this.configuration).findGlobalBoardType(requestParameters.boardTypeId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates or updates a globally-defined board type.
     * @param {BoardTypesApiUpsertGlobalBoardTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertGlobalBoardType(requestParameters: BoardTypesApiUpsertGlobalBoardTypeRequest, options?: RawAxiosRequestConfig) {
        return BoardTypesApiFp(this.configuration).upsertGlobalBoardType(requestParameters.boardTypeId, requestParameters.apiVersion, requestParameters.upsertBoardTypeRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * BoardsApi - axios parameter creator
 */
export const BoardsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Creates a new board.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {CreateBoardRequest} [createBoardRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createBoard: async (orgId: string, apiVersion?: string, createBoardRequest?: CreateBoardRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('createBoard', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/boards`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createBoardRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a board by its key.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBoard: async (orgId: string, boardKey: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('getBoard', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('getBoard', 'boardKey', boardKey)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a workspace-specific Board configuration by its key.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBoardConfiguration: async (orgId: string, boardKey: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('getBoardConfiguration', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('getBoardConfiguration', 'boardKey', boardKey)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/configuration`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Searches for boards in the specified organization.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchBoardsRequest} [searchBoardsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchBoards: async (orgId: string, apiVersion?: string, searchBoardsRequest?: SearchBoardsRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchBoards', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/boards/search`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(searchBoardsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates or updates a workspace-specific Board configuration.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {UpdateBoardConfigurationRequest} [updateBoardConfigurationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateBoardConfiguration: async (orgId: string, boardKey: string, apiVersion?: string, updateBoardConfigurationRequest?: UpdateBoardConfigurationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('updateBoardConfiguration', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('updateBoardConfiguration', 'boardKey', boardKey)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/configuration`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateBoardConfigurationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * BoardsApi - functional programming interface
 */
export const BoardsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = BoardsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Creates a new board.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {CreateBoardRequest} [createBoardRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createBoard(orgId: string, apiVersion?: string, createBoardRequest?: CreateBoardRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BoardResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createBoard(orgId, apiVersion, createBoardRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BoardsApi.createBoard']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get a board by its key.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBoard(orgId: string, boardKey: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BoardResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBoard(orgId, boardKey, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BoardsApi.getBoard']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get a workspace-specific Board configuration by its key.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getBoardConfiguration(orgId: string, boardKey: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BoardConfiguration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getBoardConfiguration(orgId, boardKey, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BoardsApi.getBoardConfiguration']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Searches for boards in the specified organization.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchBoardsRequest} [searchBoardsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchBoards(orgId: string, apiVersion?: string, searchBoardsRequest?: SearchBoardsRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BoardResponseIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchBoards(orgId, apiVersion, searchBoardsRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BoardsApi.searchBoards']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates or updates a workspace-specific Board configuration.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {UpdateBoardConfigurationRequest} [updateBoardConfigurationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateBoardConfiguration(orgId: string, boardKey: string, apiVersion?: string, updateBoardConfigurationRequest?: UpdateBoardConfigurationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<BoardConfiguration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateBoardConfiguration(orgId, boardKey, apiVersion, updateBoardConfigurationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['BoardsApi.updateBoardConfiguration']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * BoardsApi - factory interface
 */
export const BoardsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = BoardsApiFp(configuration)
    return {
        /**
         * 
         * @summary Creates a new board.
         * @param {BoardsApiCreateBoardRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createBoard(requestParameters: BoardsApiCreateBoardRequest, options?: RawAxiosRequestConfig): AxiosPromise<BoardResponse> {
            return localVarFp.createBoard(requestParameters.orgId, requestParameters.apiVersion, requestParameters.createBoardRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a board by its key.
         * @param {BoardsApiGetBoardRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBoard(requestParameters: BoardsApiGetBoardRequest, options?: RawAxiosRequestConfig): AxiosPromise<BoardResponse> {
            return localVarFp.getBoard(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a workspace-specific Board configuration by its key.
         * @param {BoardsApiGetBoardConfigurationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getBoardConfiguration(requestParameters: BoardsApiGetBoardConfigurationRequest, options?: RawAxiosRequestConfig): AxiosPromise<BoardConfiguration> {
            return localVarFp.getBoardConfiguration(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Searches for boards in the specified organization.
         * @param {BoardsApiSearchBoardsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchBoards(requestParameters: BoardsApiSearchBoardsRequest, options?: RawAxiosRequestConfig): AxiosPromise<BoardResponseIPaginatedResponse> {
            return localVarFp.searchBoards(requestParameters.orgId, requestParameters.apiVersion, requestParameters.searchBoardsRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates or updates a workspace-specific Board configuration.
         * @param {BoardsApiUpdateBoardConfigurationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateBoardConfiguration(requestParameters: BoardsApiUpdateBoardConfigurationRequest, options?: RawAxiosRequestConfig): AxiosPromise<BoardConfiguration> {
            return localVarFp.updateBoardConfiguration(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.updateBoardConfigurationRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createBoard operation in BoardsApi.
 */
export interface BoardsApiCreateBoardRequest {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly createBoardRequest?: CreateBoardRequest
}

/**
 * Request parameters for getBoard operation in BoardsApi.
 */
export interface BoardsApiGetBoardRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for getBoardConfiguration operation in BoardsApi.
 */
export interface BoardsApiGetBoardConfigurationRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for searchBoards operation in BoardsApi.
 */
export interface BoardsApiSearchBoardsRequest {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly searchBoardsRequest?: SearchBoardsRequest
}

/**
 * Request parameters for updateBoardConfiguration operation in BoardsApi.
 */
export interface BoardsApiUpdateBoardConfigurationRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly updateBoardConfigurationRequest?: UpdateBoardConfigurationRequest
}

/**
 * BoardsApi - object-oriented interface
 */
export class BoardsApi extends BaseAPI {
    /**
     * 
     * @summary Creates a new board.
     * @param {BoardsApiCreateBoardRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createBoard(requestParameters: BoardsApiCreateBoardRequest, options?: RawAxiosRequestConfig) {
        return BoardsApiFp(this.configuration).createBoard(requestParameters.orgId, requestParameters.apiVersion, requestParameters.createBoardRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get a board by its key.
     * @param {BoardsApiGetBoardRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBoard(requestParameters: BoardsApiGetBoardRequest, options?: RawAxiosRequestConfig) {
        return BoardsApiFp(this.configuration).getBoard(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get a workspace-specific Board configuration by its key.
     * @param {BoardsApiGetBoardConfigurationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getBoardConfiguration(requestParameters: BoardsApiGetBoardConfigurationRequest, options?: RawAxiosRequestConfig) {
        return BoardsApiFp(this.configuration).getBoardConfiguration(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Searches for boards in the specified organization.
     * @param {BoardsApiSearchBoardsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchBoards(requestParameters: BoardsApiSearchBoardsRequest, options?: RawAxiosRequestConfig) {
        return BoardsApiFp(this.configuration).searchBoards(requestParameters.orgId, requestParameters.apiVersion, requestParameters.searchBoardsRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates or updates a workspace-specific Board configuration.
     * @param {BoardsApiUpdateBoardConfigurationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateBoardConfiguration(requestParameters: BoardsApiUpdateBoardConfigurationRequest, options?: RawAxiosRequestConfig) {
        return BoardsApiFp(this.configuration).updateBoardConfiguration(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.updateBoardConfigurationRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ContextConfigurationsApi - axios parameter creator
 */
export const ContextConfigurationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Deletes a user-defined contextConfiguration.
         * @param {string} orgId 
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteContextConfiguration: async (orgId: string, contextConfigurationId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('deleteContextConfiguration', 'orgId', orgId)
            // verify required parameter 'contextConfigurationId' is not null or undefined
            assertParamExists('deleteContextConfiguration', 'contextConfigurationId', contextConfigurationId)
            const localVarPath = `/orgs/{orgId}/context-configurations/{contextConfigurationId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{contextConfigurationId}', encodeURIComponent(String(contextConfigurationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Deletes a globally-defined contextConfiguration.
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalContextConfiguration: async (contextConfigurationId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contextConfigurationId' is not null or undefined
            assertParamExists('deleteGlobalContextConfiguration', 'contextConfigurationId', contextConfigurationId)
            const localVarPath = `/global/context-configurations/{contextConfigurationId}`
                .replace('{contextConfigurationId}', encodeURIComponent(String(contextConfigurationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a user-defined contextConfiguration by its id or returns globally-defined one if user-defined does not exist.
         * @param {string} orgId 
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findContextConfiguration: async (orgId: string, contextConfigurationId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('findContextConfiguration', 'orgId', orgId)
            // verify required parameter 'contextConfigurationId' is not null or undefined
            assertParamExists('findContextConfiguration', 'contextConfigurationId', contextConfigurationId)
            const localVarPath = `/orgs/{orgId}/context-configurations/{contextConfigurationId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{contextConfigurationId}', encodeURIComponent(String(contextConfigurationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a globally-defined contextConfiguration by its id.
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalContextConfiguration: async (contextConfigurationId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contextConfigurationId' is not null or undefined
            assertParamExists('findGlobalContextConfiguration', 'contextConfigurationId', contextConfigurationId)
            const localVarPath = `/global/context-configurations/{contextConfigurationId}`
                .replace('{contextConfigurationId}', encodeURIComponent(String(contextConfigurationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined Context Configurations.
         * @param {string} orgId 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchContextConfigurations: async (orgId: string, page?: number, pageSize?: number, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchContextConfigurations', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/context-configurations`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['pageSize'] = pageSize;
            }

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates a new user-defined contextConfiguration or updates an existing one if it already exists.
         * @param {string} orgId 
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertContextConfigurationRequest} [upsertContextConfigurationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertContextConfiguration: async (orgId: string, contextConfigurationId: string, apiVersion?: string, upsertContextConfigurationRequest?: UpsertContextConfigurationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('upsertContextConfiguration', 'orgId', orgId)
            // verify required parameter 'contextConfigurationId' is not null or undefined
            assertParamExists('upsertContextConfiguration', 'contextConfigurationId', contextConfigurationId)
            const localVarPath = `/orgs/{orgId}/context-configurations/{contextConfigurationId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{contextConfigurationId}', encodeURIComponent(String(contextConfigurationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertContextConfigurationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates a new globally-defined contextConfiguration or updates an existing one if it already exists.
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertContextConfigurationRequest} [upsertContextConfigurationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalContextConfiguration: async (contextConfigurationId: string, apiVersion?: string, upsertContextConfigurationRequest?: UpsertContextConfigurationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contextConfigurationId' is not null or undefined
            assertParamExists('upsertGlobalContextConfiguration', 'contextConfigurationId', contextConfigurationId)
            const localVarPath = `/global/context-configurations/{contextConfigurationId}`
                .replace('{contextConfigurationId}', encodeURIComponent(String(contextConfigurationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertContextConfigurationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ContextConfigurationsApi - functional programming interface
 */
export const ContextConfigurationsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ContextConfigurationsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Deletes a user-defined contextConfiguration.
         * @param {string} orgId 
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteContextConfiguration(orgId: string, contextConfigurationId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteContextConfiguration(orgId, contextConfigurationId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContextConfigurationsApi.deleteContextConfiguration']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Deletes a globally-defined contextConfiguration.
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteGlobalContextConfiguration(contextConfigurationId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteGlobalContextConfiguration(contextConfigurationId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContextConfigurationsApi.deleteGlobalContextConfiguration']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a user-defined contextConfiguration by its id or returns globally-defined one if user-defined does not exist.
         * @param {string} orgId 
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findContextConfiguration(orgId: string, contextConfigurationId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContextConfiguration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findContextConfiguration(orgId, contextConfigurationId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContextConfigurationsApi.findContextConfiguration']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a globally-defined contextConfiguration by its id.
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findGlobalContextConfiguration(contextConfigurationId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContextConfiguration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findGlobalContextConfiguration(contextConfigurationId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContextConfigurationsApi.findGlobalContextConfiguration']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined Context Configurations.
         * @param {string} orgId 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchContextConfigurations(orgId: string, page?: number, pageSize?: number, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContextConfigurationIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchContextConfigurations(orgId, page, pageSize, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContextConfigurationsApi.searchContextConfigurations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates a new user-defined contextConfiguration or updates an existing one if it already exists.
         * @param {string} orgId 
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertContextConfigurationRequest} [upsertContextConfigurationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertContextConfiguration(orgId: string, contextConfigurationId: string, apiVersion?: string, upsertContextConfigurationRequest?: UpsertContextConfigurationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContextConfiguration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertContextConfiguration(orgId, contextConfigurationId, apiVersion, upsertContextConfigurationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContextConfigurationsApi.upsertContextConfiguration']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates a new globally-defined contextConfiguration or updates an existing one if it already exists.
         * @param {string} contextConfigurationId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertContextConfigurationRequest} [upsertContextConfigurationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertGlobalContextConfiguration(contextConfigurationId: string, apiVersion?: string, upsertContextConfigurationRequest?: UpsertContextConfigurationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContextConfiguration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertGlobalContextConfiguration(contextConfigurationId, apiVersion, upsertContextConfigurationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContextConfigurationsApi.upsertGlobalContextConfiguration']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ContextConfigurationsApi - factory interface
 */
export const ContextConfigurationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ContextConfigurationsApiFp(configuration)
    return {
        /**
         * 
         * @summary Deletes a user-defined contextConfiguration.
         * @param {ContextConfigurationsApiDeleteContextConfigurationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteContextConfiguration(requestParameters: ContextConfigurationsApiDeleteContextConfigurationRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteContextConfiguration(requestParameters.orgId, requestParameters.contextConfigurationId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Deletes a globally-defined contextConfiguration.
         * @param {ContextConfigurationsApiDeleteGlobalContextConfigurationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalContextConfiguration(requestParameters: ContextConfigurationsApiDeleteGlobalContextConfigurationRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteGlobalContextConfiguration(requestParameters.contextConfigurationId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a user-defined contextConfiguration by its id or returns globally-defined one if user-defined does not exist.
         * @param {ContextConfigurationsApiFindContextConfigurationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findContextConfiguration(requestParameters: ContextConfigurationsApiFindContextConfigurationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContextConfiguration> {
            return localVarFp.findContextConfiguration(requestParameters.orgId, requestParameters.contextConfigurationId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a globally-defined contextConfiguration by its id.
         * @param {ContextConfigurationsApiFindGlobalContextConfigurationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalContextConfiguration(requestParameters: ContextConfigurationsApiFindGlobalContextConfigurationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContextConfiguration> {
            return localVarFp.findGlobalContextConfiguration(requestParameters.contextConfigurationId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined Context Configurations.
         * @param {ContextConfigurationsApiSearchContextConfigurationsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchContextConfigurations(requestParameters: ContextConfigurationsApiSearchContextConfigurationsRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContextConfigurationIPaginatedResponse> {
            return localVarFp.searchContextConfigurations(requestParameters.orgId, requestParameters.page, requestParameters.pageSize, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates a new user-defined contextConfiguration or updates an existing one if it already exists.
         * @param {ContextConfigurationsApiUpsertContextConfigurationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertContextConfiguration(requestParameters: ContextConfigurationsApiUpsertContextConfigurationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContextConfiguration> {
            return localVarFp.upsertContextConfiguration(requestParameters.orgId, requestParameters.contextConfigurationId, requestParameters.apiVersion, requestParameters.upsertContextConfigurationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates a new globally-defined contextConfiguration or updates an existing one if it already exists.
         * @param {ContextConfigurationsApiUpsertGlobalContextConfigurationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalContextConfiguration(requestParameters: ContextConfigurationsApiUpsertGlobalContextConfigurationRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContextConfiguration> {
            return localVarFp.upsertGlobalContextConfiguration(requestParameters.contextConfigurationId, requestParameters.apiVersion, requestParameters.upsertContextConfigurationRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for deleteContextConfiguration operation in ContextConfigurationsApi.
 */
export interface ContextConfigurationsApiDeleteContextConfigurationRequest {
    readonly orgId: string

    readonly contextConfigurationId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for deleteGlobalContextConfiguration operation in ContextConfigurationsApi.
 */
export interface ContextConfigurationsApiDeleteGlobalContextConfigurationRequest {
    readonly contextConfigurationId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for findContextConfiguration operation in ContextConfigurationsApi.
 */
export interface ContextConfigurationsApiFindContextConfigurationRequest {
    readonly orgId: string

    readonly contextConfigurationId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for findGlobalContextConfiguration operation in ContextConfigurationsApi.
 */
export interface ContextConfigurationsApiFindGlobalContextConfigurationRequest {
    readonly contextConfigurationId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for searchContextConfigurations operation in ContextConfigurationsApi.
 */
export interface ContextConfigurationsApiSearchContextConfigurationsRequest {
    readonly orgId: string

    readonly page?: number

    readonly pageSize?: number

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for upsertContextConfiguration operation in ContextConfigurationsApi.
 */
export interface ContextConfigurationsApiUpsertContextConfigurationRequest {
    readonly orgId: string

    readonly contextConfigurationId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertContextConfigurationRequest?: UpsertContextConfigurationRequest
}

/**
 * Request parameters for upsertGlobalContextConfiguration operation in ContextConfigurationsApi.
 */
export interface ContextConfigurationsApiUpsertGlobalContextConfigurationRequest {
    readonly contextConfigurationId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertContextConfigurationRequest?: UpsertContextConfigurationRequest
}

/**
 * ContextConfigurationsApi - object-oriented interface
 */
export class ContextConfigurationsApi extends BaseAPI {
    /**
     * 
     * @summary Deletes a user-defined contextConfiguration.
     * @param {ContextConfigurationsApiDeleteContextConfigurationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteContextConfiguration(requestParameters: ContextConfigurationsApiDeleteContextConfigurationRequest, options?: RawAxiosRequestConfig) {
        return ContextConfigurationsApiFp(this.configuration).deleteContextConfiguration(requestParameters.orgId, requestParameters.contextConfigurationId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Deletes a globally-defined contextConfiguration.
     * @param {ContextConfigurationsApiDeleteGlobalContextConfigurationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteGlobalContextConfiguration(requestParameters: ContextConfigurationsApiDeleteGlobalContextConfigurationRequest, options?: RawAxiosRequestConfig) {
        return ContextConfigurationsApiFp(this.configuration).deleteGlobalContextConfiguration(requestParameters.contextConfigurationId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a user-defined contextConfiguration by its id or returns globally-defined one if user-defined does not exist.
     * @param {ContextConfigurationsApiFindContextConfigurationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findContextConfiguration(requestParameters: ContextConfigurationsApiFindContextConfigurationRequest, options?: RawAxiosRequestConfig) {
        return ContextConfigurationsApiFp(this.configuration).findContextConfiguration(requestParameters.orgId, requestParameters.contextConfigurationId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a globally-defined contextConfiguration by its id.
     * @param {ContextConfigurationsApiFindGlobalContextConfigurationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findGlobalContextConfiguration(requestParameters: ContextConfigurationsApiFindGlobalContextConfigurationRequest, options?: RawAxiosRequestConfig) {
        return ContextConfigurationsApiFp(this.configuration).findGlobalContextConfiguration(requestParameters.contextConfigurationId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a paginated collection of existing globally-defined and user-defined Context Configurations.
     * @param {ContextConfigurationsApiSearchContextConfigurationsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchContextConfigurations(requestParameters: ContextConfigurationsApiSearchContextConfigurationsRequest, options?: RawAxiosRequestConfig) {
        return ContextConfigurationsApiFp(this.configuration).searchContextConfigurations(requestParameters.orgId, requestParameters.page, requestParameters.pageSize, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates a new user-defined contextConfiguration or updates an existing one if it already exists.
     * @param {ContextConfigurationsApiUpsertContextConfigurationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertContextConfiguration(requestParameters: ContextConfigurationsApiUpsertContextConfigurationRequest, options?: RawAxiosRequestConfig) {
        return ContextConfigurationsApiFp(this.configuration).upsertContextConfiguration(requestParameters.orgId, requestParameters.contextConfigurationId, requestParameters.apiVersion, requestParameters.upsertContextConfigurationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates a new globally-defined contextConfiguration or updates an existing one if it already exists.
     * @param {ContextConfigurationsApiUpsertGlobalContextConfigurationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertGlobalContextConfiguration(requestParameters: ContextConfigurationsApiUpsertGlobalContextConfigurationRequest, options?: RawAxiosRequestConfig) {
        return ContextConfigurationsApiFp(this.configuration).upsertGlobalContextConfiguration(requestParameters.contextConfigurationId, requestParameters.apiVersion, requestParameters.upsertContextConfigurationRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * DataPlatformApi - axios parameter creator
 */
export const DataPlatformApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Generates a data platform schema for the specified global task type.
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateSchema: async (taskTypeId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'taskTypeId' is not null or undefined
            assertParamExists('generateSchema', 'taskTypeId', taskTypeId)
            const localVarPath = `/global/data-platform/schemas/{taskTypeId}`
                .replace('{taskTypeId}', encodeURIComponent(String(taskTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * DataPlatformApi - functional programming interface
 */
export const DataPlatformApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = DataPlatformApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Generates a data platform schema for the specified global task type.
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async generateSchema(taskTypeId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DataPlatformSchema>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.generateSchema(taskTypeId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DataPlatformApi.generateSchema']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * DataPlatformApi - factory interface
 */
export const DataPlatformApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = DataPlatformApiFp(configuration)
    return {
        /**
         * 
         * @summary Generates a data platform schema for the specified global task type.
         * @param {DataPlatformApiGenerateSchemaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateSchema(requestParameters: DataPlatformApiGenerateSchemaRequest, options?: RawAxiosRequestConfig): AxiosPromise<DataPlatformSchema> {
            return localVarFp.generateSchema(requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for generateSchema operation in DataPlatformApi.
 */
export interface DataPlatformApiGenerateSchemaRequest {
    readonly taskTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * DataPlatformApi - object-oriented interface
 */
export class DataPlatformApi extends BaseAPI {
    /**
     * 
     * @summary Generates a data platform schema for the specified global task type.
     * @param {DataPlatformApiGenerateSchemaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public generateSchema(requestParameters: DataPlatformApiGenerateSchemaRequest, options?: RawAxiosRequestConfig) {
        return DataPlatformApiFp(this.configuration).generateSchema(requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ExternalEntitiesApi - axios parameter creator
 */
export const ExternalEntitiesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Gets the list of external entities by type.
         * @param {string} orgId 
         * @param {string} externalEntityTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchExternalEntitiesRequest} [searchExternalEntitiesRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findExternalEntities: async (orgId: string, externalEntityTypeId: string, apiVersion?: string, searchExternalEntitiesRequest?: SearchExternalEntitiesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('findExternalEntities', 'orgId', orgId)
            // verify required parameter 'externalEntityTypeId' is not null or undefined
            assertParamExists('findExternalEntities', 'externalEntityTypeId', externalEntityTypeId)
            const localVarPath = `/orgs/{orgId}/external-entities/{externalEntityTypeId}/search`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{externalEntityTypeId}', encodeURIComponent(String(externalEntityTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(searchExternalEntitiesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Returns an external entity.
         * @param {string} orgId 
         * @param {string} externalEntityTypeId 
         * @param {string} externalEntityId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExternalEntity: async (orgId: string, externalEntityTypeId: string, externalEntityId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('getExternalEntity', 'orgId', orgId)
            // verify required parameter 'externalEntityTypeId' is not null or undefined
            assertParamExists('getExternalEntity', 'externalEntityTypeId', externalEntityTypeId)
            // verify required parameter 'externalEntityId' is not null or undefined
            assertParamExists('getExternalEntity', 'externalEntityId', externalEntityId)
            const localVarPath = `/orgs/{orgId}/external-entities/{externalEntityTypeId}/{externalEntityId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{externalEntityTypeId}', encodeURIComponent(String(externalEntityTypeId)))
                .replace('{externalEntityId}', encodeURIComponent(String(externalEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Notifies Task Hub that external entities changed so it can refresh them. Processed asynchronously.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {InvalidateExternalEntitiesRequest} [invalidateExternalEntitiesRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        invalidateExternalEntities: async (orgId: string, apiVersion?: string, invalidateExternalEntitiesRequest?: InvalidateExternalEntitiesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('invalidateExternalEntities', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/external-entities/invalidations`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(invalidateExternalEntitiesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Updates an external entity.
         * @param {string} orgId 
         * @param {string} externalEntityTypeId 
         * @param {string} externalEntityId 
         * @param {string} [apiVersion] The requested API version
         * @param {any} [body] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertExternalEntity: async (orgId: string, externalEntityTypeId: string, externalEntityId: string, apiVersion?: string, body?: any, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('upsertExternalEntity', 'orgId', orgId)
            // verify required parameter 'externalEntityTypeId' is not null or undefined
            assertParamExists('upsertExternalEntity', 'externalEntityTypeId', externalEntityTypeId)
            // verify required parameter 'externalEntityId' is not null or undefined
            assertParamExists('upsertExternalEntity', 'externalEntityId', externalEntityId)
            const localVarPath = `/orgs/{orgId}/external-entities/{externalEntityTypeId}/{externalEntityId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{externalEntityTypeId}', encodeURIComponent(String(externalEntityTypeId)))
                .replace('{externalEntityId}', encodeURIComponent(String(externalEntityId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(body, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ExternalEntitiesApi - functional programming interface
 */
export const ExternalEntitiesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ExternalEntitiesApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Gets the list of external entities by type.
         * @param {string} orgId 
         * @param {string} externalEntityTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchExternalEntitiesRequest} [searchExternalEntitiesRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findExternalEntities(orgId: string, externalEntityTypeId: string, apiVersion?: string, searchExternalEntitiesRequest?: SearchExternalEntitiesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ExternalEntityIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findExternalEntities(orgId, externalEntityTypeId, apiVersion, searchExternalEntitiesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExternalEntitiesApi.findExternalEntities']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Returns an external entity.
         * @param {string} orgId 
         * @param {string} externalEntityTypeId 
         * @param {string} externalEntityId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getExternalEntity(orgId: string, externalEntityTypeId: string, externalEntityId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ExternalEntity>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getExternalEntity(orgId, externalEntityTypeId, externalEntityId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExternalEntitiesApi.getExternalEntity']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Notifies Task Hub that external entities changed so it can refresh them. Processed asynchronously.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {InvalidateExternalEntitiesRequest} [invalidateExternalEntitiesRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async invalidateExternalEntities(orgId: string, apiVersion?: string, invalidateExternalEntitiesRequest?: InvalidateExternalEntitiesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.invalidateExternalEntities(orgId, apiVersion, invalidateExternalEntitiesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExternalEntitiesApi.invalidateExternalEntities']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Updates an external entity.
         * @param {string} orgId 
         * @param {string} externalEntityTypeId 
         * @param {string} externalEntityId 
         * @param {string} [apiVersion] The requested API version
         * @param {any} [body] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertExternalEntity(orgId: string, externalEntityTypeId: string, externalEntityId: string, apiVersion?: string, body?: any, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ExternalEntity>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertExternalEntity(orgId, externalEntityTypeId, externalEntityId, apiVersion, body, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExternalEntitiesApi.upsertExternalEntity']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ExternalEntitiesApi - factory interface
 */
export const ExternalEntitiesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ExternalEntitiesApiFp(configuration)
    return {
        /**
         * 
         * @summary Gets the list of external entities by type.
         * @param {ExternalEntitiesApiFindExternalEntitiesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findExternalEntities(requestParameters: ExternalEntitiesApiFindExternalEntitiesRequest, options?: RawAxiosRequestConfig): AxiosPromise<ExternalEntityIPaginatedResponse> {
            return localVarFp.findExternalEntities(requestParameters.orgId, requestParameters.externalEntityTypeId, requestParameters.apiVersion, requestParameters.searchExternalEntitiesRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Returns an external entity.
         * @param {ExternalEntitiesApiGetExternalEntityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getExternalEntity(requestParameters: ExternalEntitiesApiGetExternalEntityRequest, options?: RawAxiosRequestConfig): AxiosPromise<ExternalEntity> {
            return localVarFp.getExternalEntity(requestParameters.orgId, requestParameters.externalEntityTypeId, requestParameters.externalEntityId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Notifies Task Hub that external entities changed so it can refresh them. Processed asynchronously.
         * @param {ExternalEntitiesApiInvalidateExternalEntitiesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        invalidateExternalEntities(requestParameters: ExternalEntitiesApiInvalidateExternalEntitiesRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.invalidateExternalEntities(requestParameters.orgId, requestParameters.apiVersion, requestParameters.invalidateExternalEntitiesRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Updates an external entity.
         * @param {ExternalEntitiesApiUpsertExternalEntityRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertExternalEntity(requestParameters: ExternalEntitiesApiUpsertExternalEntityRequest, options?: RawAxiosRequestConfig): AxiosPromise<ExternalEntity> {
            return localVarFp.upsertExternalEntity(requestParameters.orgId, requestParameters.externalEntityTypeId, requestParameters.externalEntityId, requestParameters.apiVersion, requestParameters.body, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for findExternalEntities operation in ExternalEntitiesApi.
 */
export interface ExternalEntitiesApiFindExternalEntitiesRequest {
    readonly orgId: string

    readonly externalEntityTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly searchExternalEntitiesRequest?: SearchExternalEntitiesRequest
}

/**
 * Request parameters for getExternalEntity operation in ExternalEntitiesApi.
 */
export interface ExternalEntitiesApiGetExternalEntityRequest {
    readonly orgId: string

    readonly externalEntityTypeId: string

    readonly externalEntityId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for invalidateExternalEntities operation in ExternalEntitiesApi.
 */
export interface ExternalEntitiesApiInvalidateExternalEntitiesRequest {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly invalidateExternalEntitiesRequest?: InvalidateExternalEntitiesRequest
}

/**
 * Request parameters for upsertExternalEntity operation in ExternalEntitiesApi.
 */
export interface ExternalEntitiesApiUpsertExternalEntityRequest {
    readonly orgId: string

    readonly externalEntityTypeId: string

    readonly externalEntityId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly body?: any
}

/**
 * ExternalEntitiesApi - object-oriented interface
 */
export class ExternalEntitiesApi extends BaseAPI {
    /**
     * 
     * @summary Gets the list of external entities by type.
     * @param {ExternalEntitiesApiFindExternalEntitiesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findExternalEntities(requestParameters: ExternalEntitiesApiFindExternalEntitiesRequest, options?: RawAxiosRequestConfig) {
        return ExternalEntitiesApiFp(this.configuration).findExternalEntities(requestParameters.orgId, requestParameters.externalEntityTypeId, requestParameters.apiVersion, requestParameters.searchExternalEntitiesRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Returns an external entity.
     * @param {ExternalEntitiesApiGetExternalEntityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getExternalEntity(requestParameters: ExternalEntitiesApiGetExternalEntityRequest, options?: RawAxiosRequestConfig) {
        return ExternalEntitiesApiFp(this.configuration).getExternalEntity(requestParameters.orgId, requestParameters.externalEntityTypeId, requestParameters.externalEntityId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Notifies Task Hub that external entities changed so it can refresh them. Processed asynchronously.
     * @param {ExternalEntitiesApiInvalidateExternalEntitiesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public invalidateExternalEntities(requestParameters: ExternalEntitiesApiInvalidateExternalEntitiesRequest, options?: RawAxiosRequestConfig) {
        return ExternalEntitiesApiFp(this.configuration).invalidateExternalEntities(requestParameters.orgId, requestParameters.apiVersion, requestParameters.invalidateExternalEntitiesRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Updates an external entity.
     * @param {ExternalEntitiesApiUpsertExternalEntityRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertExternalEntity(requestParameters: ExternalEntitiesApiUpsertExternalEntityRequest, options?: RawAxiosRequestConfig) {
        return ExternalEntitiesApiFp(this.configuration).upsertExternalEntity(requestParameters.orgId, requestParameters.externalEntityTypeId, requestParameters.externalEntityId, requestParameters.apiVersion, requestParameters.body, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ExternalEntityTypesApi - axios parameter creator
 */
export const ExternalEntityTypesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Deletes a globally-defined external entity type.
         * @param {string} externalEntityTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalExternalEntityType: async (externalEntityTypeId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalEntityTypeId' is not null or undefined
            assertParamExists('deleteGlobalExternalEntityType', 'externalEntityTypeId', externalEntityTypeId)
            const localVarPath = `/global/external-entity-types/{externalEntityTypeId}`
                .replace('{externalEntityTypeId}', encodeURIComponent(String(externalEntityTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a globally-defined external entity type by its ID.
         * @param {string} externalEntityTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findExternalEntityType: async (externalEntityTypeId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalEntityTypeId' is not null or undefined
            assertParamExists('findExternalEntityType', 'externalEntityTypeId', externalEntityTypeId)
            const localVarPath = `/global/external-entity-types/{externalEntityTypeId}`
                .replace('{externalEntityTypeId}', encodeURIComponent(String(externalEntityTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates or updates a globally-defined external entity type.
         * @param {string} externalEntityTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertExternalEntityTypeRequest} [upsertExternalEntityTypeRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalExternalEntityType: async (externalEntityTypeId: string, apiVersion?: string, upsertExternalEntityTypeRequest?: UpsertExternalEntityTypeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'externalEntityTypeId' is not null or undefined
            assertParamExists('upsertGlobalExternalEntityType', 'externalEntityTypeId', externalEntityTypeId)
            const localVarPath = `/global/external-entity-types/{externalEntityTypeId}`
                .replace('{externalEntityTypeId}', encodeURIComponent(String(externalEntityTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertExternalEntityTypeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ExternalEntityTypesApi - functional programming interface
 */
export const ExternalEntityTypesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ExternalEntityTypesApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Deletes a globally-defined external entity type.
         * @param {string} externalEntityTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteGlobalExternalEntityType(externalEntityTypeId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteGlobalExternalEntityType(externalEntityTypeId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExternalEntityTypesApi.deleteGlobalExternalEntityType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a globally-defined external entity type by its ID.
         * @param {string} externalEntityTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findExternalEntityType(externalEntityTypeId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ExternalEntityType>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findExternalEntityType(externalEntityTypeId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExternalEntityTypesApi.findExternalEntityType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates or updates a globally-defined external entity type.
         * @param {string} externalEntityTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertExternalEntityTypeRequest} [upsertExternalEntityTypeRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertGlobalExternalEntityType(externalEntityTypeId: string, apiVersion?: string, upsertExternalEntityTypeRequest?: UpsertExternalEntityTypeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ExternalEntityType>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertGlobalExternalEntityType(externalEntityTypeId, apiVersion, upsertExternalEntityTypeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ExternalEntityTypesApi.upsertGlobalExternalEntityType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ExternalEntityTypesApi - factory interface
 */
export const ExternalEntityTypesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ExternalEntityTypesApiFp(configuration)
    return {
        /**
         * 
         * @summary Deletes a globally-defined external entity type.
         * @param {ExternalEntityTypesApiDeleteGlobalExternalEntityTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalExternalEntityType(requestParameters: ExternalEntityTypesApiDeleteGlobalExternalEntityTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteGlobalExternalEntityType(requestParameters.externalEntityTypeId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a globally-defined external entity type by its ID.
         * @param {ExternalEntityTypesApiFindExternalEntityTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findExternalEntityType(requestParameters: ExternalEntityTypesApiFindExternalEntityTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<ExternalEntityType> {
            return localVarFp.findExternalEntityType(requestParameters.externalEntityTypeId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates or updates a globally-defined external entity type.
         * @param {ExternalEntityTypesApiUpsertGlobalExternalEntityTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalExternalEntityType(requestParameters: ExternalEntityTypesApiUpsertGlobalExternalEntityTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<ExternalEntityType> {
            return localVarFp.upsertGlobalExternalEntityType(requestParameters.externalEntityTypeId, requestParameters.apiVersion, requestParameters.upsertExternalEntityTypeRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for deleteGlobalExternalEntityType operation in ExternalEntityTypesApi.
 */
export interface ExternalEntityTypesApiDeleteGlobalExternalEntityTypeRequest {
    readonly externalEntityTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for findExternalEntityType operation in ExternalEntityTypesApi.
 */
export interface ExternalEntityTypesApiFindExternalEntityTypeRequest {
    readonly externalEntityTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for upsertGlobalExternalEntityType operation in ExternalEntityTypesApi.
 */
export interface ExternalEntityTypesApiUpsertGlobalExternalEntityTypeRequest {
    readonly externalEntityTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertExternalEntityTypeRequest?: UpsertExternalEntityTypeRequest
}

/**
 * ExternalEntityTypesApi - object-oriented interface
 */
export class ExternalEntityTypesApi extends BaseAPI {
    /**
     * 
     * @summary Deletes a globally-defined external entity type.
     * @param {ExternalEntityTypesApiDeleteGlobalExternalEntityTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteGlobalExternalEntityType(requestParameters: ExternalEntityTypesApiDeleteGlobalExternalEntityTypeRequest, options?: RawAxiosRequestConfig) {
        return ExternalEntityTypesApiFp(this.configuration).deleteGlobalExternalEntityType(requestParameters.externalEntityTypeId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a globally-defined external entity type by its ID.
     * @param {ExternalEntityTypesApiFindExternalEntityTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findExternalEntityType(requestParameters: ExternalEntityTypesApiFindExternalEntityTypeRequest, options?: RawAxiosRequestConfig) {
        return ExternalEntityTypesApiFp(this.configuration).findExternalEntityType(requestParameters.externalEntityTypeId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates or updates a globally-defined external entity type.
     * @param {ExternalEntityTypesApiUpsertGlobalExternalEntityTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertGlobalExternalEntityType(requestParameters: ExternalEntityTypesApiUpsertGlobalExternalEntityTypeRequest, options?: RawAxiosRequestConfig) {
        return ExternalEntityTypesApiFp(this.configuration).upsertGlobalExternalEntityType(requestParameters.externalEntityTypeId, requestParameters.apiVersion, requestParameters.upsertExternalEntityTypeRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * FieldsApi - axios parameter creator
 */
export const FieldsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Adds or enables/disables a field value option for a user-defined dropdown field.
         * @param {string} orgId 
         * @param {string} fieldId 
         * @param {string} fieldValueOptionId 
         * @param {string} [apiVersion] The requested API version
         * @param {FieldValueOptionDetails} [fieldValueOptionDetails] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addOrUpdateDropdownFieldValue: async (orgId: string, fieldId: string, fieldValueOptionId: string, apiVersion?: string, fieldValueOptionDetails?: FieldValueOptionDetails, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('addOrUpdateDropdownFieldValue', 'orgId', orgId)
            // verify required parameter 'fieldId' is not null or undefined
            assertParamExists('addOrUpdateDropdownFieldValue', 'fieldId', fieldId)
            // verify required parameter 'fieldValueOptionId' is not null or undefined
            assertParamExists('addOrUpdateDropdownFieldValue', 'fieldValueOptionId', fieldValueOptionId)
            const localVarPath = `/orgs/{orgId}/fields/{fieldId}/values/{fieldValueOptionId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{fieldId}', encodeURIComponent(String(fieldId)))
                .replace('{fieldValueOptionId}', encodeURIComponent(String(fieldValueOptionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(fieldValueOptionDetails, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Deletes a user-defined field.
         * @param {string} orgId 
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteField: async (orgId: string, fieldId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('deleteField', 'orgId', orgId)
            // verify required parameter 'fieldId' is not null or undefined
            assertParamExists('deleteField', 'fieldId', fieldId)
            const localVarPath = `/orgs/{orgId}/fields/{fieldId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{fieldId}', encodeURIComponent(String(fieldId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Deletes a globally-defined field.
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalField: async (fieldId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'fieldId' is not null or undefined
            assertParamExists('deleteGlobalField', 'fieldId', fieldId)
            const localVarPath = `/global/fields/{fieldId}`
                .replace('{fieldId}', encodeURIComponent(String(fieldId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a globally-defined field by its id.
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalField: async (fieldId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'fieldId' is not null or undefined
            assertParamExists('findGlobalField', 'fieldId', fieldId)
            const localVarPath = `/global/fields/{fieldId}`
                .replace('{fieldId}', encodeURIComponent(String(fieldId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined fields.
         * @param {string} orgId 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchFields: async (orgId: string, page?: number, pageSize?: number, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchFields', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/fields`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['pageSize'] = pageSize;
            }

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates or updates a user-defined field.
         * @param {string} orgId 
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertCustomFieldRequest} [upsertCustomFieldRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertCustomField: async (orgId: string, fieldId: string, apiVersion?: string, upsertCustomFieldRequest?: UpsertCustomFieldRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('upsertCustomField', 'orgId', orgId)
            // verify required parameter 'fieldId' is not null or undefined
            assertParamExists('upsertCustomField', 'fieldId', fieldId)
            const localVarPath = `/orgs/{orgId}/fields/{fieldId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{fieldId}', encodeURIComponent(String(fieldId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertCustomFieldRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates or updates a globally-defined field.
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertCustomFieldRequest} [upsertCustomFieldRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalField: async (fieldId: string, apiVersion?: string, upsertCustomFieldRequest?: UpsertCustomFieldRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'fieldId' is not null or undefined
            assertParamExists('upsertGlobalField', 'fieldId', fieldId)
            const localVarPath = `/global/fields/{fieldId}`
                .replace('{fieldId}', encodeURIComponent(String(fieldId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertCustomFieldRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * FieldsApi - functional programming interface
 */
export const FieldsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = FieldsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Adds or enables/disables a field value option for a user-defined dropdown field.
         * @param {string} orgId 
         * @param {string} fieldId 
         * @param {string} fieldValueOptionId 
         * @param {string} [apiVersion] The requested API version
         * @param {FieldValueOptionDetails} [fieldValueOptionDetails] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addOrUpdateDropdownFieldValue(orgId: string, fieldId: string, fieldValueOptionId: string, apiVersion?: string, fieldValueOptionDetails?: FieldValueOptionDetails, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addOrUpdateDropdownFieldValue(orgId, fieldId, fieldValueOptionId, apiVersion, fieldValueOptionDetails, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FieldsApi.addOrUpdateDropdownFieldValue']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Deletes a user-defined field.
         * @param {string} orgId 
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteField(orgId: string, fieldId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteField(orgId, fieldId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FieldsApi.deleteField']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Deletes a globally-defined field.
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteGlobalField(fieldId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteGlobalField(fieldId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FieldsApi.deleteGlobalField']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a globally-defined field by its id.
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findGlobalField(fieldId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UpsertCustomField200Response>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findGlobalField(fieldId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FieldsApi.findGlobalField']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined fields.
         * @param {string} orgId 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchFields(orgId: string, page?: number, pageSize?: number, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FieldIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchFields(orgId, page, pageSize, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FieldsApi.searchFields']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates or updates a user-defined field.
         * @param {string} orgId 
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertCustomFieldRequest} [upsertCustomFieldRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertCustomField(orgId: string, fieldId: string, apiVersion?: string, upsertCustomFieldRequest?: UpsertCustomFieldRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UpsertCustomField200Response>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertCustomField(orgId, fieldId, apiVersion, upsertCustomFieldRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FieldsApi.upsertCustomField']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates or updates a globally-defined field.
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertCustomFieldRequest} [upsertCustomFieldRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertGlobalField(fieldId: string, apiVersion?: string, upsertCustomFieldRequest?: UpsertCustomFieldRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UpsertCustomField200Response>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertGlobalField(fieldId, apiVersion, upsertCustomFieldRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FieldsApi.upsertGlobalField']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * FieldsApi - factory interface
 */
export const FieldsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = FieldsApiFp(configuration)
    return {
        /**
         * 
         * @summary Adds or enables/disables a field value option for a user-defined dropdown field.
         * @param {FieldsApiAddOrUpdateDropdownFieldValueRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addOrUpdateDropdownFieldValue(requestParameters: FieldsApiAddOrUpdateDropdownFieldValueRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.addOrUpdateDropdownFieldValue(requestParameters.orgId, requestParameters.fieldId, requestParameters.fieldValueOptionId, requestParameters.apiVersion, requestParameters.fieldValueOptionDetails, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Deletes a user-defined field.
         * @param {FieldsApiDeleteFieldRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteField(requestParameters: FieldsApiDeleteFieldRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteField(requestParameters.orgId, requestParameters.fieldId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Deletes a globally-defined field.
         * @param {FieldsApiDeleteGlobalFieldRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalField(requestParameters: FieldsApiDeleteGlobalFieldRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteGlobalField(requestParameters.fieldId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a globally-defined field by its id.
         * @param {FieldsApiFindGlobalFieldRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalField(requestParameters: FieldsApiFindGlobalFieldRequest, options?: RawAxiosRequestConfig): AxiosPromise<UpsertCustomField200Response> {
            return localVarFp.findGlobalField(requestParameters.fieldId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined fields.
         * @param {FieldsApiSearchFieldsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchFields(requestParameters: FieldsApiSearchFieldsRequest, options?: RawAxiosRequestConfig): AxiosPromise<FieldIPaginatedResponse> {
            return localVarFp.searchFields(requestParameters.orgId, requestParameters.page, requestParameters.pageSize, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates or updates a user-defined field.
         * @param {FieldsApiUpsertCustomFieldRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertCustomField(requestParameters: FieldsApiUpsertCustomFieldRequest, options?: RawAxiosRequestConfig): AxiosPromise<UpsertCustomField200Response> {
            return localVarFp.upsertCustomField(requestParameters.orgId, requestParameters.fieldId, requestParameters.apiVersion, requestParameters.upsertCustomFieldRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates or updates a globally-defined field.
         * @param {FieldsApiUpsertGlobalFieldRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalField(requestParameters: FieldsApiUpsertGlobalFieldRequest, options?: RawAxiosRequestConfig): AxiosPromise<UpsertCustomField200Response> {
            return localVarFp.upsertGlobalField(requestParameters.fieldId, requestParameters.apiVersion, requestParameters.upsertCustomFieldRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for addOrUpdateDropdownFieldValue operation in FieldsApi.
 */
export interface FieldsApiAddOrUpdateDropdownFieldValueRequest {
    readonly orgId: string

    readonly fieldId: string

    readonly fieldValueOptionId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly fieldValueOptionDetails?: FieldValueOptionDetails
}

/**
 * Request parameters for deleteField operation in FieldsApi.
 */
export interface FieldsApiDeleteFieldRequest {
    readonly orgId: string

    readonly fieldId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for deleteGlobalField operation in FieldsApi.
 */
export interface FieldsApiDeleteGlobalFieldRequest {
    readonly fieldId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for findGlobalField operation in FieldsApi.
 */
export interface FieldsApiFindGlobalFieldRequest {
    readonly fieldId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for searchFields operation in FieldsApi.
 */
export interface FieldsApiSearchFieldsRequest {
    readonly orgId: string

    readonly page?: number

    readonly pageSize?: number

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for upsertCustomField operation in FieldsApi.
 */
export interface FieldsApiUpsertCustomFieldRequest {
    readonly orgId: string

    readonly fieldId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertCustomFieldRequest?: UpsertCustomFieldRequest
}

/**
 * Request parameters for upsertGlobalField operation in FieldsApi.
 */
export interface FieldsApiUpsertGlobalFieldRequest {
    readonly fieldId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertCustomFieldRequest?: UpsertCustomFieldRequest
}

/**
 * FieldsApi - object-oriented interface
 */
export class FieldsApi extends BaseAPI {
    /**
     * 
     * @summary Adds or enables/disables a field value option for a user-defined dropdown field.
     * @param {FieldsApiAddOrUpdateDropdownFieldValueRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addOrUpdateDropdownFieldValue(requestParameters: FieldsApiAddOrUpdateDropdownFieldValueRequest, options?: RawAxiosRequestConfig) {
        return FieldsApiFp(this.configuration).addOrUpdateDropdownFieldValue(requestParameters.orgId, requestParameters.fieldId, requestParameters.fieldValueOptionId, requestParameters.apiVersion, requestParameters.fieldValueOptionDetails, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Deletes a user-defined field.
     * @param {FieldsApiDeleteFieldRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteField(requestParameters: FieldsApiDeleteFieldRequest, options?: RawAxiosRequestConfig) {
        return FieldsApiFp(this.configuration).deleteField(requestParameters.orgId, requestParameters.fieldId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Deletes a globally-defined field.
     * @param {FieldsApiDeleteGlobalFieldRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteGlobalField(requestParameters: FieldsApiDeleteGlobalFieldRequest, options?: RawAxiosRequestConfig) {
        return FieldsApiFp(this.configuration).deleteGlobalField(requestParameters.fieldId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a globally-defined field by its id.
     * @param {FieldsApiFindGlobalFieldRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findGlobalField(requestParameters: FieldsApiFindGlobalFieldRequest, options?: RawAxiosRequestConfig) {
        return FieldsApiFp(this.configuration).findGlobalField(requestParameters.fieldId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a paginated collection of existing globally-defined and user-defined fields.
     * @param {FieldsApiSearchFieldsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchFields(requestParameters: FieldsApiSearchFieldsRequest, options?: RawAxiosRequestConfig) {
        return FieldsApiFp(this.configuration).searchFields(requestParameters.orgId, requestParameters.page, requestParameters.pageSize, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates or updates a user-defined field.
     * @param {FieldsApiUpsertCustomFieldRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertCustomField(requestParameters: FieldsApiUpsertCustomFieldRequest, options?: RawAxiosRequestConfig) {
        return FieldsApiFp(this.configuration).upsertCustomField(requestParameters.orgId, requestParameters.fieldId, requestParameters.apiVersion, requestParameters.upsertCustomFieldRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates or updates a globally-defined field.
     * @param {FieldsApiUpsertGlobalFieldRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertGlobalField(requestParameters: FieldsApiUpsertGlobalFieldRequest, options?: RawAxiosRequestConfig) {
        return FieldsApiFp(this.configuration).upsertGlobalField(requestParameters.fieldId, requestParameters.apiVersion, requestParameters.upsertCustomFieldRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * LabelsApi - axios parameter creator
 */
export const LabelsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Deletes a globally-defined label.
         * @param {string} labelId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalLabel: async (labelId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'labelId' is not null or undefined
            assertParamExists('deleteGlobalLabel', 'labelId', labelId)
            const localVarPath = `/global/labels/{labelId}`
                .replace('{labelId}', encodeURIComponent(String(labelId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Deletes a user-defined label.
         * @param {string} orgId 
         * @param {string} labelId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteLabel: async (orgId: string, labelId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('deleteLabel', 'orgId', orgId)
            // verify required parameter 'labelId' is not null or undefined
            assertParamExists('deleteLabel', 'labelId', labelId)
            const localVarPath = `/orgs/{orgId}/labels/{labelId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{labelId}', encodeURIComponent(String(labelId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a globally-defined label by its id.
         * @param {string} labelId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalLabel: async (labelId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'labelId' is not null or undefined
            assertParamExists('findGlobalLabel', 'labelId', labelId)
            const localVarPath = `/global/labels/{labelId}`
                .replace('{labelId}', encodeURIComponent(String(labelId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined labels.
         * @param {string} orgId 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchLabels: async (orgId: string, page?: number, pageSize?: number, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchLabels', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/labels`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['pageSize'] = pageSize;
            }

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates a new globally-defined label or updates an existing one if it already exists.
         * @param {string} labelId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertLabelRequest} [upsertLabelRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalLabel: async (labelId: string, apiVersion?: string, upsertLabelRequest?: UpsertLabelRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'labelId' is not null or undefined
            assertParamExists('upsertGlobalLabel', 'labelId', labelId)
            const localVarPath = `/global/labels/{labelId}`
                .replace('{labelId}', encodeURIComponent(String(labelId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertLabelRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates a new user-defined label or updates an existing one if it already exists.
         * @param {string} orgId 
         * @param {string} labelId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertLabelRequest} [upsertLabelRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertLabel: async (orgId: string, labelId: string, apiVersion?: string, upsertLabelRequest?: UpsertLabelRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('upsertLabel', 'orgId', orgId)
            // verify required parameter 'labelId' is not null or undefined
            assertParamExists('upsertLabel', 'labelId', labelId)
            const localVarPath = `/orgs/{orgId}/labels/{labelId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{labelId}', encodeURIComponent(String(labelId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertLabelRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * LabelsApi - functional programming interface
 */
export const LabelsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = LabelsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Deletes a globally-defined label.
         * @param {string} labelId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteGlobalLabel(labelId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteGlobalLabel(labelId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LabelsApi.deleteGlobalLabel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Deletes a user-defined label.
         * @param {string} orgId 
         * @param {string} labelId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteLabel(orgId: string, labelId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteLabel(orgId, labelId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LabelsApi.deleteLabel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a globally-defined label by its id.
         * @param {string} labelId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findGlobalLabel(labelId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Label>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findGlobalLabel(labelId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LabelsApi.findGlobalLabel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined labels.
         * @param {string} orgId 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchLabels(orgId: string, page?: number, pageSize?: number, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LabelIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchLabels(orgId, page, pageSize, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LabelsApi.searchLabels']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates a new globally-defined label or updates an existing one if it already exists.
         * @param {string} labelId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertLabelRequest} [upsertLabelRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertGlobalLabel(labelId: string, apiVersion?: string, upsertLabelRequest?: UpsertLabelRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Label>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertGlobalLabel(labelId, apiVersion, upsertLabelRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LabelsApi.upsertGlobalLabel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates a new user-defined label or updates an existing one if it already exists.
         * @param {string} orgId 
         * @param {string} labelId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertLabelRequest} [upsertLabelRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertLabel(orgId: string, labelId: string, apiVersion?: string, upsertLabelRequest?: UpsertLabelRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Label>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertLabel(orgId, labelId, apiVersion, upsertLabelRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LabelsApi.upsertLabel']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * LabelsApi - factory interface
 */
export const LabelsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = LabelsApiFp(configuration)
    return {
        /**
         * 
         * @summary Deletes a globally-defined label.
         * @param {LabelsApiDeleteGlobalLabelRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalLabel(requestParameters: LabelsApiDeleteGlobalLabelRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteGlobalLabel(requestParameters.labelId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Deletes a user-defined label.
         * @param {LabelsApiDeleteLabelRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteLabel(requestParameters: LabelsApiDeleteLabelRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteLabel(requestParameters.orgId, requestParameters.labelId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a globally-defined label by its id.
         * @param {LabelsApiFindGlobalLabelRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalLabel(requestParameters: LabelsApiFindGlobalLabelRequest, options?: RawAxiosRequestConfig): AxiosPromise<Label> {
            return localVarFp.findGlobalLabel(requestParameters.labelId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined labels.
         * @param {LabelsApiSearchLabelsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchLabels(requestParameters: LabelsApiSearchLabelsRequest, options?: RawAxiosRequestConfig): AxiosPromise<LabelIPaginatedResponse> {
            return localVarFp.searchLabels(requestParameters.orgId, requestParameters.page, requestParameters.pageSize, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates a new globally-defined label or updates an existing one if it already exists.
         * @param {LabelsApiUpsertGlobalLabelRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalLabel(requestParameters: LabelsApiUpsertGlobalLabelRequest, options?: RawAxiosRequestConfig): AxiosPromise<Label> {
            return localVarFp.upsertGlobalLabel(requestParameters.labelId, requestParameters.apiVersion, requestParameters.upsertLabelRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates a new user-defined label or updates an existing one if it already exists.
         * @param {LabelsApiUpsertLabelRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertLabel(requestParameters: LabelsApiUpsertLabelRequest, options?: RawAxiosRequestConfig): AxiosPromise<Label> {
            return localVarFp.upsertLabel(requestParameters.orgId, requestParameters.labelId, requestParameters.apiVersion, requestParameters.upsertLabelRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for deleteGlobalLabel operation in LabelsApi.
 */
export interface LabelsApiDeleteGlobalLabelRequest {
    readonly labelId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for deleteLabel operation in LabelsApi.
 */
export interface LabelsApiDeleteLabelRequest {
    readonly orgId: string

    readonly labelId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for findGlobalLabel operation in LabelsApi.
 */
export interface LabelsApiFindGlobalLabelRequest {
    readonly labelId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for searchLabels operation in LabelsApi.
 */
export interface LabelsApiSearchLabelsRequest {
    readonly orgId: string

    readonly page?: number

    readonly pageSize?: number

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for upsertGlobalLabel operation in LabelsApi.
 */
export interface LabelsApiUpsertGlobalLabelRequest {
    readonly labelId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertLabelRequest?: UpsertLabelRequest
}

/**
 * Request parameters for upsertLabel operation in LabelsApi.
 */
export interface LabelsApiUpsertLabelRequest {
    readonly orgId: string

    readonly labelId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertLabelRequest?: UpsertLabelRequest
}

/**
 * LabelsApi - object-oriented interface
 */
export class LabelsApi extends BaseAPI {
    /**
     * 
     * @summary Deletes a globally-defined label.
     * @param {LabelsApiDeleteGlobalLabelRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteGlobalLabel(requestParameters: LabelsApiDeleteGlobalLabelRequest, options?: RawAxiosRequestConfig) {
        return LabelsApiFp(this.configuration).deleteGlobalLabel(requestParameters.labelId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Deletes a user-defined label.
     * @param {LabelsApiDeleteLabelRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteLabel(requestParameters: LabelsApiDeleteLabelRequest, options?: RawAxiosRequestConfig) {
        return LabelsApiFp(this.configuration).deleteLabel(requestParameters.orgId, requestParameters.labelId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a globally-defined label by its id.
     * @param {LabelsApiFindGlobalLabelRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findGlobalLabel(requestParameters: LabelsApiFindGlobalLabelRequest, options?: RawAxiosRequestConfig) {
        return LabelsApiFp(this.configuration).findGlobalLabel(requestParameters.labelId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a paginated collection of existing globally-defined and user-defined labels.
     * @param {LabelsApiSearchLabelsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchLabels(requestParameters: LabelsApiSearchLabelsRequest, options?: RawAxiosRequestConfig) {
        return LabelsApiFp(this.configuration).searchLabels(requestParameters.orgId, requestParameters.page, requestParameters.pageSize, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates a new globally-defined label or updates an existing one if it already exists.
     * @param {LabelsApiUpsertGlobalLabelRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertGlobalLabel(requestParameters: LabelsApiUpsertGlobalLabelRequest, options?: RawAxiosRequestConfig) {
        return LabelsApiFp(this.configuration).upsertGlobalLabel(requestParameters.labelId, requestParameters.apiVersion, requestParameters.upsertLabelRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates a new user-defined label or updates an existing one if it already exists.
     * @param {LabelsApiUpsertLabelRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertLabel(requestParameters: LabelsApiUpsertLabelRequest, options?: RawAxiosRequestConfig) {
        return LabelsApiFp(this.configuration).upsertLabel(requestParameters.orgId, requestParameters.labelId, requestParameters.apiVersion, requestParameters.upsertLabelRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * MetricsApi - axios parameter creator
 */
export const MetricsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Query metrics for tasks in a board.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} metricId 
         * @param {string} [apiVersion] The requested API version
         * @param {BoardMetricsRequest} [boardMetricsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        queryMetrics: async (orgId: string, boardKey: string, metricId: string, apiVersion?: string, boardMetricsRequest?: BoardMetricsRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('queryMetrics', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('queryMetrics', 'boardKey', boardKey)
            // verify required parameter 'metricId' is not null or undefined
            assertParamExists('queryMetrics', 'metricId', metricId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/metrics/{metricId}/query`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{metricId}', encodeURIComponent(String(metricId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(boardMetricsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Query metrics for tasks in the workspace implied by the org, with optional board filtering.
         * @param {string} orgId 
         * @param {string} metricId 
         * @param {string} [apiVersion] The requested API version
         * @param {WorkspaceMetricsRequest} [workspaceMetricsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        queryWorkspaceMetrics: async (orgId: string, metricId: string, apiVersion?: string, workspaceMetricsRequest?: WorkspaceMetricsRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('queryWorkspaceMetrics', 'orgId', orgId)
            // verify required parameter 'metricId' is not null or undefined
            assertParamExists('queryWorkspaceMetrics', 'metricId', metricId)
            const localVarPath = `/orgs/{orgId}/metrics/{metricId}/query`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{metricId}', encodeURIComponent(String(metricId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(workspaceMetricsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * MetricsApi - functional programming interface
 */
export const MetricsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = MetricsApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Query metrics for tasks in a board.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} metricId 
         * @param {string} [apiVersion] The requested API version
         * @param {BoardMetricsRequest} [boardMetricsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async queryMetrics(orgId: string, boardKey: string, metricId: string, apiVersion?: string, boardMetricsRequest?: BoardMetricsRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MetricsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.queryMetrics(orgId, boardKey, metricId, apiVersion, boardMetricsRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MetricsApi.queryMetrics']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Query metrics for tasks in the workspace implied by the org, with optional board filtering.
         * @param {string} orgId 
         * @param {string} metricId 
         * @param {string} [apiVersion] The requested API version
         * @param {WorkspaceMetricsRequest} [workspaceMetricsRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async queryWorkspaceMetrics(orgId: string, metricId: string, apiVersion?: string, workspaceMetricsRequest?: WorkspaceMetricsRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MetricsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.queryWorkspaceMetrics(orgId, metricId, apiVersion, workspaceMetricsRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['MetricsApi.queryWorkspaceMetrics']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * MetricsApi - factory interface
 */
export const MetricsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = MetricsApiFp(configuration)
    return {
        /**
         * 
         * @summary Query metrics for tasks in a board.
         * @param {MetricsApiQueryMetricsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        queryMetrics(requestParameters: MetricsApiQueryMetricsRequest, options?: RawAxiosRequestConfig): AxiosPromise<MetricsResponse> {
            return localVarFp.queryMetrics(requestParameters.orgId, requestParameters.boardKey, requestParameters.metricId, requestParameters.apiVersion, requestParameters.boardMetricsRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Query metrics for tasks in the workspace implied by the org, with optional board filtering.
         * @param {MetricsApiQueryWorkspaceMetricsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        queryWorkspaceMetrics(requestParameters: MetricsApiQueryWorkspaceMetricsRequest, options?: RawAxiosRequestConfig): AxiosPromise<MetricsResponse> {
            return localVarFp.queryWorkspaceMetrics(requestParameters.orgId, requestParameters.metricId, requestParameters.apiVersion, requestParameters.workspaceMetricsRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for queryMetrics operation in MetricsApi.
 */
export interface MetricsApiQueryMetricsRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly metricId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly boardMetricsRequest?: BoardMetricsRequest
}

/**
 * Request parameters for queryWorkspaceMetrics operation in MetricsApi.
 */
export interface MetricsApiQueryWorkspaceMetricsRequest {
    readonly orgId: string

    readonly metricId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly workspaceMetricsRequest?: WorkspaceMetricsRequest
}

/**
 * MetricsApi - object-oriented interface
 */
export class MetricsApi extends BaseAPI {
    /**
     * 
     * @summary Query metrics for tasks in a board.
     * @param {MetricsApiQueryMetricsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public queryMetrics(requestParameters: MetricsApiQueryMetricsRequest, options?: RawAxiosRequestConfig) {
        return MetricsApiFp(this.configuration).queryMetrics(requestParameters.orgId, requestParameters.boardKey, requestParameters.metricId, requestParameters.apiVersion, requestParameters.boardMetricsRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Query metrics for tasks in the workspace implied by the org, with optional board filtering.
     * @param {MetricsApiQueryWorkspaceMetricsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public queryWorkspaceMetrics(requestParameters: MetricsApiQueryWorkspaceMetricsRequest, options?: RawAxiosRequestConfig) {
        return MetricsApiFp(this.configuration).queryWorkspaceMetrics(requestParameters.orgId, requestParameters.metricId, requestParameters.apiVersion, requestParameters.workspaceMetricsRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * TaskTemplatesApi - axios parameter creator
 */
export const TaskTemplatesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Archive a user-defined task template by its ID.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveTaskTemplate: async (orgId: string, taskTemplateId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('archiveTaskTemplate', 'orgId', orgId)
            // verify required parameter 'taskTemplateId' is not null or undefined
            assertParamExists('archiveTaskTemplate', 'taskTemplateId', taskTemplateId)
            const localVarPath = `/orgs/{orgId}/templates/{taskTemplateId}/archive`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{taskTemplateId}', encodeURIComponent(String(taskTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Delete a globally-defined task template by its ID.
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalTaskTemplate: async (taskTemplateId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'taskTemplateId' is not null or undefined
            assertParamExists('deleteGlobalTaskTemplate', 'taskTemplateId', taskTemplateId)
            const localVarPath = `/global/templates/{taskTemplateId}`
                .replace('{taskTemplateId}', encodeURIComponent(String(taskTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Delete a user-defined task template by its ID.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTaskTemplate: async (orgId: string, taskTemplateId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('deleteTaskTemplate', 'orgId', orgId)
            // verify required parameter 'taskTemplateId' is not null or undefined
            assertParamExists('deleteTaskTemplate', 'taskTemplateId', taskTemplateId)
            const localVarPath = `/orgs/{orgId}/templates/{taskTemplateId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{taskTemplateId}', encodeURIComponent(String(taskTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Execute a user-defined task template.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {ExecuteTaskTemplateRequest} [executeTaskTemplateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        executeTaskTemplate: async (orgId: string, taskTemplateId: string, apiVersion?: string, executeTaskTemplateRequest?: ExecuteTaskTemplateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('executeTaskTemplate', 'orgId', orgId)
            // verify required parameter 'taskTemplateId' is not null or undefined
            assertParamExists('executeTaskTemplate', 'taskTemplateId', taskTemplateId)
            const localVarPath = `/orgs/{orgId}/templates/{taskTemplateId}/executions`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{taskTemplateId}', encodeURIComponent(String(taskTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(executeTaskTemplateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a globally-defined task template by its ID.
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getGlobalTaskTemplate: async (taskTemplateId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'taskTemplateId' is not null or undefined
            assertParamExists('getGlobalTaskTemplate', 'taskTemplateId', taskTemplateId)
            const localVarPath = `/global/templates/{taskTemplateId}`
                .replace('{taskTemplateId}', encodeURIComponent(String(taskTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a user-defined task template by its ID.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTaskTemplate: async (orgId: string, taskTemplateId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('getTaskTemplate', 'orgId', orgId)
            // verify required parameter 'taskTemplateId' is not null or undefined
            assertParamExists('getTaskTemplate', 'taskTemplateId', taskTemplateId)
            const localVarPath = `/orgs/{orgId}/templates/{taskTemplateId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{taskTemplateId}', encodeURIComponent(String(taskTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Search for global Task Templates, returning summary information.
         * @param {string} [apiVersion] The requested API version
         * @param {SearchTaskTemplatesRequest} [searchTaskTemplatesRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchGlobalTaskTemplates: async (apiVersion?: string, searchTaskTemplatesRequest?: SearchTaskTemplatesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/global/templates/search-summary`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(searchTaskTemplatesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Search for Task Templates in an organization.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchTaskTemplatesRequest} [searchTaskTemplatesRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskTemplates: async (orgId: string, apiVersion?: string, searchTaskTemplatesRequest?: SearchTaskTemplatesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchTaskTemplates', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/templates/search`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(searchTaskTemplatesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Search for Task Templates in an organization, returning summary information.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchTaskTemplatesRequest} [searchTaskTemplatesRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskTemplatesSummary: async (orgId: string, apiVersion?: string, searchTaskTemplatesRequest?: SearchTaskTemplatesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchTaskTemplatesSummary', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/templates/search-summary`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(searchTaskTemplatesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Unarchive a user-defined task template by its ID.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unarchiveTaskTemplate: async (orgId: string, taskTemplateId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('unarchiveTaskTemplate', 'orgId', orgId)
            // verify required parameter 'taskTemplateId' is not null or undefined
            assertParamExists('unarchiveTaskTemplate', 'taskTemplateId', taskTemplateId)
            const localVarPath = `/orgs/{orgId}/templates/{taskTemplateId}/unarchive`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{taskTemplateId}', encodeURIComponent(String(taskTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates a globally-defined task template.
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertTaskTemplateRequest} [upsertTaskTemplateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalTaskTemplate: async (taskTemplateId: string, apiVersion?: string, upsertTaskTemplateRequest?: UpsertTaskTemplateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'taskTemplateId' is not null or undefined
            assertParamExists('upsertGlobalTaskTemplate', 'taskTemplateId', taskTemplateId)
            const localVarPath = `/global/templates/{taskTemplateId}`
                .replace('{taskTemplateId}', encodeURIComponent(String(taskTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertTaskTemplateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates a user-defined task template.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertTaskTemplateRequest} [upsertTaskTemplateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertTaskTemplate: async (orgId: string, taskTemplateId: string, apiVersion?: string, upsertTaskTemplateRequest?: UpsertTaskTemplateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('upsertTaskTemplate', 'orgId', orgId)
            // verify required parameter 'taskTemplateId' is not null or undefined
            assertParamExists('upsertTaskTemplate', 'taskTemplateId', taskTemplateId)
            const localVarPath = `/orgs/{orgId}/templates/{taskTemplateId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{taskTemplateId}', encodeURIComponent(String(taskTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertTaskTemplateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * TaskTemplatesApi - functional programming interface
 */
export const TaskTemplatesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = TaskTemplatesApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Archive a user-defined task template by its ID.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async archiveTaskTemplate(orgId: string, taskTemplateId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTemplate>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.archiveTaskTemplate(orgId, taskTemplateId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.archiveTaskTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Delete a globally-defined task template by its ID.
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteGlobalTaskTemplate(taskTemplateId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteGlobalTaskTemplate(taskTemplateId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.deleteGlobalTaskTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Delete a user-defined task template by its ID.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteTaskTemplate(orgId: string, taskTemplateId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteTaskTemplate(orgId, taskTemplateId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.deleteTaskTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Execute a user-defined task template.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {ExecuteTaskTemplateRequest} [executeTaskTemplateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async executeTaskTemplate(orgId: string, taskTemplateId: string, apiVersion?: string, executeTaskTemplateRequest?: ExecuteTaskTemplateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTemplateExecution>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.executeTaskTemplate(orgId, taskTemplateId, apiVersion, executeTaskTemplateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.executeTaskTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get a globally-defined task template by its ID.
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getGlobalTaskTemplate(taskTemplateId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTemplate>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getGlobalTaskTemplate(taskTemplateId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.getGlobalTaskTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get a user-defined task template by its ID.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTaskTemplate(orgId: string, taskTemplateId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTemplate>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTaskTemplate(orgId, taskTemplateId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.getTaskTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Search for global Task Templates, returning summary information.
         * @param {string} [apiVersion] The requested API version
         * @param {SearchTaskTemplatesRequest} [searchTaskTemplatesRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchGlobalTaskTemplates(apiVersion?: string, searchTaskTemplatesRequest?: SearchTaskTemplatesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTemplateSummaryIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchGlobalTaskTemplates(apiVersion, searchTaskTemplatesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.searchGlobalTaskTemplates']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Search for Task Templates in an organization.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchTaskTemplatesRequest} [searchTaskTemplatesRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchTaskTemplates(orgId: string, apiVersion?: string, searchTaskTemplatesRequest?: SearchTaskTemplatesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTemplateIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchTaskTemplates(orgId, apiVersion, searchTaskTemplatesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.searchTaskTemplates']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Search for Task Templates in an organization, returning summary information.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchTaskTemplatesRequest} [searchTaskTemplatesRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchTaskTemplatesSummary(orgId: string, apiVersion?: string, searchTaskTemplatesRequest?: SearchTaskTemplatesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTemplateSummaryIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchTaskTemplatesSummary(orgId, apiVersion, searchTaskTemplatesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.searchTaskTemplatesSummary']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Unarchive a user-defined task template by its ID.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unarchiveTaskTemplate(orgId: string, taskTemplateId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTemplate>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unarchiveTaskTemplate(orgId, taskTemplateId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.unarchiveTaskTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates a globally-defined task template.
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertTaskTemplateRequest} [upsertTaskTemplateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertGlobalTaskTemplate(taskTemplateId: string, apiVersion?: string, upsertTaskTemplateRequest?: UpsertTaskTemplateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTemplate>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertGlobalTaskTemplate(taskTemplateId, apiVersion, upsertTaskTemplateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.upsertGlobalTaskTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates a user-defined task template.
         * @param {string} orgId 
         * @param {string} taskTemplateId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertTaskTemplateRequest} [upsertTaskTemplateRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertTaskTemplate(orgId: string, taskTemplateId: string, apiVersion?: string, upsertTaskTemplateRequest?: UpsertTaskTemplateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTemplate>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertTaskTemplate(orgId, taskTemplateId, apiVersion, upsertTaskTemplateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTemplatesApi.upsertTaskTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * TaskTemplatesApi - factory interface
 */
export const TaskTemplatesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = TaskTemplatesApiFp(configuration)
    return {
        /**
         * 
         * @summary Archive a user-defined task template by its ID.
         * @param {TaskTemplatesApiArchiveTaskTemplateRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveTaskTemplate(requestParameters: TaskTemplatesApiArchiveTaskTemplateRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTemplate> {
            return localVarFp.archiveTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Delete a globally-defined task template by its ID.
         * @param {TaskTemplatesApiDeleteGlobalTaskTemplateRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalTaskTemplate(requestParameters: TaskTemplatesApiDeleteGlobalTaskTemplateRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteGlobalTaskTemplate(requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Delete a user-defined task template by its ID.
         * @param {TaskTemplatesApiDeleteTaskTemplateRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTaskTemplate(requestParameters: TaskTemplatesApiDeleteTaskTemplateRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Execute a user-defined task template.
         * @param {TaskTemplatesApiExecuteTaskTemplateRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        executeTaskTemplate(requestParameters: TaskTemplatesApiExecuteTaskTemplateRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTemplateExecution> {
            return localVarFp.executeTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, requestParameters.executeTaskTemplateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a globally-defined task template by its ID.
         * @param {TaskTemplatesApiGetGlobalTaskTemplateRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getGlobalTaskTemplate(requestParameters: TaskTemplatesApiGetGlobalTaskTemplateRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTemplate> {
            return localVarFp.getGlobalTaskTemplate(requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a user-defined task template by its ID.
         * @param {TaskTemplatesApiGetTaskTemplateRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTaskTemplate(requestParameters: TaskTemplatesApiGetTaskTemplateRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTemplate> {
            return localVarFp.getTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Search for global Task Templates, returning summary information.
         * @param {TaskTemplatesApiSearchGlobalTaskTemplatesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchGlobalTaskTemplates(requestParameters: TaskTemplatesApiSearchGlobalTaskTemplatesRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<TaskTemplateSummaryIPaginatedResponse> {
            return localVarFp.searchGlobalTaskTemplates(requestParameters.apiVersion, requestParameters.searchTaskTemplatesRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Search for Task Templates in an organization.
         * @param {TaskTemplatesApiSearchTaskTemplatesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskTemplates(requestParameters: TaskTemplatesApiSearchTaskTemplatesRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTemplateIPaginatedResponse> {
            return localVarFp.searchTaskTemplates(requestParameters.orgId, requestParameters.apiVersion, requestParameters.searchTaskTemplatesRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Search for Task Templates in an organization, returning summary information.
         * @param {TaskTemplatesApiSearchTaskTemplatesSummaryRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskTemplatesSummary(requestParameters: TaskTemplatesApiSearchTaskTemplatesSummaryRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTemplateSummaryIPaginatedResponse> {
            return localVarFp.searchTaskTemplatesSummary(requestParameters.orgId, requestParameters.apiVersion, requestParameters.searchTaskTemplatesRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Unarchive a user-defined task template by its ID.
         * @param {TaskTemplatesApiUnarchiveTaskTemplateRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unarchiveTaskTemplate(requestParameters: TaskTemplatesApiUnarchiveTaskTemplateRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTemplate> {
            return localVarFp.unarchiveTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates a globally-defined task template.
         * @param {TaskTemplatesApiUpsertGlobalTaskTemplateRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalTaskTemplate(requestParameters: TaskTemplatesApiUpsertGlobalTaskTemplateRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTemplate> {
            return localVarFp.upsertGlobalTaskTemplate(requestParameters.taskTemplateId, requestParameters.apiVersion, requestParameters.upsertTaskTemplateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates a user-defined task template.
         * @param {TaskTemplatesApiUpsertTaskTemplateRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertTaskTemplate(requestParameters: TaskTemplatesApiUpsertTaskTemplateRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTemplate> {
            return localVarFp.upsertTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, requestParameters.upsertTaskTemplateRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for archiveTaskTemplate operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiArchiveTaskTemplateRequest {
    readonly orgId: string

    readonly taskTemplateId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for deleteGlobalTaskTemplate operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiDeleteGlobalTaskTemplateRequest {
    readonly taskTemplateId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for deleteTaskTemplate operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiDeleteTaskTemplateRequest {
    readonly orgId: string

    readonly taskTemplateId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for executeTaskTemplate operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiExecuteTaskTemplateRequest {
    readonly orgId: string

    readonly taskTemplateId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly executeTaskTemplateRequest?: ExecuteTaskTemplateRequest
}

/**
 * Request parameters for getGlobalTaskTemplate operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiGetGlobalTaskTemplateRequest {
    readonly taskTemplateId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for getTaskTemplate operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiGetTaskTemplateRequest {
    readonly orgId: string

    readonly taskTemplateId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for searchGlobalTaskTemplates operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiSearchGlobalTaskTemplatesRequest {
    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly searchTaskTemplatesRequest?: SearchTaskTemplatesRequest
}

/**
 * Request parameters for searchTaskTemplates operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiSearchTaskTemplatesRequest {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly searchTaskTemplatesRequest?: SearchTaskTemplatesRequest
}

/**
 * Request parameters for searchTaskTemplatesSummary operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiSearchTaskTemplatesSummaryRequest {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly searchTaskTemplatesRequest?: SearchTaskTemplatesRequest
}

/**
 * Request parameters for unarchiveTaskTemplate operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiUnarchiveTaskTemplateRequest {
    readonly orgId: string

    readonly taskTemplateId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for upsertGlobalTaskTemplate operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiUpsertGlobalTaskTemplateRequest {
    readonly taskTemplateId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertTaskTemplateRequest?: UpsertTaskTemplateRequest
}

/**
 * Request parameters for upsertTaskTemplate operation in TaskTemplatesApi.
 */
export interface TaskTemplatesApiUpsertTaskTemplateRequest {
    readonly orgId: string

    readonly taskTemplateId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertTaskTemplateRequest?: UpsertTaskTemplateRequest
}

/**
 * TaskTemplatesApi - object-oriented interface
 */
export class TaskTemplatesApi extends BaseAPI {
    /**
     * 
     * @summary Archive a user-defined task template by its ID.
     * @param {TaskTemplatesApiArchiveTaskTemplateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public archiveTaskTemplate(requestParameters: TaskTemplatesApiArchiveTaskTemplateRequest, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).archiveTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Delete a globally-defined task template by its ID.
     * @param {TaskTemplatesApiDeleteGlobalTaskTemplateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteGlobalTaskTemplate(requestParameters: TaskTemplatesApiDeleteGlobalTaskTemplateRequest, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).deleteGlobalTaskTemplate(requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Delete a user-defined task template by its ID.
     * @param {TaskTemplatesApiDeleteTaskTemplateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteTaskTemplate(requestParameters: TaskTemplatesApiDeleteTaskTemplateRequest, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).deleteTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Execute a user-defined task template.
     * @param {TaskTemplatesApiExecuteTaskTemplateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public executeTaskTemplate(requestParameters: TaskTemplatesApiExecuteTaskTemplateRequest, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).executeTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, requestParameters.executeTaskTemplateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get a globally-defined task template by its ID.
     * @param {TaskTemplatesApiGetGlobalTaskTemplateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getGlobalTaskTemplate(requestParameters: TaskTemplatesApiGetGlobalTaskTemplateRequest, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).getGlobalTaskTemplate(requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get a user-defined task template by its ID.
     * @param {TaskTemplatesApiGetTaskTemplateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getTaskTemplate(requestParameters: TaskTemplatesApiGetTaskTemplateRequest, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).getTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Search for global Task Templates, returning summary information.
     * @param {TaskTemplatesApiSearchGlobalTaskTemplatesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchGlobalTaskTemplates(requestParameters: TaskTemplatesApiSearchGlobalTaskTemplatesRequest = {}, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).searchGlobalTaskTemplates(requestParameters.apiVersion, requestParameters.searchTaskTemplatesRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Search for Task Templates in an organization.
     * @param {TaskTemplatesApiSearchTaskTemplatesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchTaskTemplates(requestParameters: TaskTemplatesApiSearchTaskTemplatesRequest, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).searchTaskTemplates(requestParameters.orgId, requestParameters.apiVersion, requestParameters.searchTaskTemplatesRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Search for Task Templates in an organization, returning summary information.
     * @param {TaskTemplatesApiSearchTaskTemplatesSummaryRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchTaskTemplatesSummary(requestParameters: TaskTemplatesApiSearchTaskTemplatesSummaryRequest, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).searchTaskTemplatesSummary(requestParameters.orgId, requestParameters.apiVersion, requestParameters.searchTaskTemplatesRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Unarchive a user-defined task template by its ID.
     * @param {TaskTemplatesApiUnarchiveTaskTemplateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unarchiveTaskTemplate(requestParameters: TaskTemplatesApiUnarchiveTaskTemplateRequest, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).unarchiveTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates a globally-defined task template.
     * @param {TaskTemplatesApiUpsertGlobalTaskTemplateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertGlobalTaskTemplate(requestParameters: TaskTemplatesApiUpsertGlobalTaskTemplateRequest, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).upsertGlobalTaskTemplate(requestParameters.taskTemplateId, requestParameters.apiVersion, requestParameters.upsertTaskTemplateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates a user-defined task template.
     * @param {TaskTemplatesApiUpsertTaskTemplateRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertTaskTemplate(requestParameters: TaskTemplatesApiUpsertTaskTemplateRequest, options?: RawAxiosRequestConfig) {
        return TaskTemplatesApiFp(this.configuration).upsertTaskTemplate(requestParameters.orgId, requestParameters.taskTemplateId, requestParameters.apiVersion, requestParameters.upsertTaskTemplateRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * TaskTypesApi - axios parameter creator
 */
export const TaskTypesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Deletes a globally-defined task type.
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalTaskType: async (taskTypeId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'taskTypeId' is not null or undefined
            assertParamExists('deleteGlobalTaskType', 'taskTypeId', taskTypeId)
            const localVarPath = `/global/task-types/{taskTypeId}`
                .replace('{taskTypeId}', encodeURIComponent(String(taskTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Deletes a user-defined task type.
         * @param {string} orgId 
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTaskType: async (orgId: string, taskTypeId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('deleteTaskType', 'orgId', orgId)
            // verify required parameter 'taskTypeId' is not null or undefined
            assertParamExists('deleteTaskType', 'taskTypeId', taskTypeId)
            const localVarPath = `/orgs/{orgId}/task-types/{taskTypeId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{taskTypeId}', encodeURIComponent(String(taskTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a board-aware task type by its id.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findBoardTaskType: async (orgId: string, boardKey: string, taskTypeId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('findBoardTaskType', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('findBoardTaskType', 'boardKey', boardKey)
            // verify required parameter 'taskTypeId' is not null or undefined
            assertParamExists('findBoardTaskType', 'taskTypeId', taskTypeId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/task-types/{taskTypeId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskTypeId}', encodeURIComponent(String(taskTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a globally-defined task type by its id.
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalTaskType: async (taskTypeId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'taskTypeId' is not null or undefined
            assertParamExists('findGlobalTaskType', 'taskTypeId', taskTypeId)
            const localVarPath = `/global/task-types/{taskTypeId}`
                .replace('{taskTypeId}', encodeURIComponent(String(taskTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets the configuration for a globally-defined task type by its id.
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalTaskTypeConfiguration: async (taskTypeId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'taskTypeId' is not null or undefined
            assertParamExists('findGlobalTaskTypeConfiguration', 'taskTypeId', taskTypeId)
            const localVarPath = `/global/task-types/{taskTypeId}/config`
                .replace('{taskTypeId}', encodeURIComponent(String(taskTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The schema is a projection of the existing wire contract. Supported `operation` values are `create` (describes `POST /orgs/{orgId}/boards/{boardKey}/tasks`), `update` (describes `POST /orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/update`), and `response` (describes the typed-wrapper shape of `TaskResponse.FieldValues` — each field property is a discriminated object with a `type` const and a `value` typed to the field\'s contract type).
         * @summary Gets a JSON Schema describing the `FieldValues` portion of a request body for the specified board-aware task type.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskTypeId 
         * @param {string} [operation] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFieldValuesSchema: async (orgId: string, boardKey: string, taskTypeId: string, operation?: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('getFieldValuesSchema', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('getFieldValuesSchema', 'boardKey', boardKey)
            // verify required parameter 'taskTypeId' is not null or undefined
            assertParamExists('getFieldValuesSchema', 'taskTypeId', taskTypeId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/task-types/{taskTypeId}/field-values/schema`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskTypeId}', encodeURIComponent(String(taskTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (operation !== undefined) {
                localVarQueryParameter['operation'] = operation;
            }

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'application/schema+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined task types.
         * @param {string} orgId 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskTypes: async (orgId: string, page?: number, pageSize?: number, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchTaskTypes', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/task-types`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (pageSize !== undefined) {
                localVarQueryParameter['pageSize'] = pageSize;
            }

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates or updates a globally-defined task type.
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertTaskTypeRequest} [upsertTaskTypeRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalTaskType: async (taskTypeId: string, apiVersion?: string, upsertTaskTypeRequest?: UpsertTaskTypeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'taskTypeId' is not null or undefined
            assertParamExists('upsertGlobalTaskType', 'taskTypeId', taskTypeId)
            const localVarPath = `/global/task-types/{taskTypeId}`
                .replace('{taskTypeId}', encodeURIComponent(String(taskTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertTaskTypeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates or updates a user-defined task type.
         * @param {string} orgId 
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertTaskTypeRequest} [upsertTaskTypeRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertTaskType: async (orgId: string, taskTypeId: string, apiVersion?: string, upsertTaskTypeRequest?: UpsertTaskTypeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('upsertTaskType', 'orgId', orgId)
            // verify required parameter 'taskTypeId' is not null or undefined
            assertParamExists('upsertTaskType', 'taskTypeId', taskTypeId)
            const localVarPath = `/orgs/{orgId}/task-types/{taskTypeId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{taskTypeId}', encodeURIComponent(String(taskTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(upsertTaskTypeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * TaskTypesApi - functional programming interface
 */
export const TaskTypesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = TaskTypesApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Deletes a globally-defined task type.
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteGlobalTaskType(taskTypeId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteGlobalTaskType(taskTypeId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTypesApi.deleteGlobalTaskType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Deletes a user-defined task type.
         * @param {string} orgId 
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteTaskType(orgId: string, taskTypeId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteTaskType(orgId, taskTypeId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTypesApi.deleteTaskType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a board-aware task type by its id.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findBoardTaskType(orgId: string, boardKey: string, taskTypeId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskType>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findBoardTaskType(orgId, boardKey, taskTypeId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTypesApi.findBoardTaskType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a globally-defined task type by its id.
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findGlobalTaskType(taskTypeId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskType>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findGlobalTaskType(taskTypeId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTypesApi.findGlobalTaskType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets the configuration for a globally-defined task type by its id.
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findGlobalTaskTypeConfiguration(taskTypeId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTypeConfiguration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findGlobalTaskTypeConfiguration(taskTypeId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTypesApi.findGlobalTaskTypeConfiguration']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The schema is a projection of the existing wire contract. Supported `operation` values are `create` (describes `POST /orgs/{orgId}/boards/{boardKey}/tasks`), `update` (describes `POST /orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/update`), and `response` (describes the typed-wrapper shape of `TaskResponse.FieldValues` — each field property is a discriminated object with a `type` const and a `value` typed to the field\'s contract type).
         * @summary Gets a JSON Schema describing the `FieldValues` portion of a request body for the specified board-aware task type.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskTypeId 
         * @param {string} [operation] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getFieldValuesSchema(orgId: string, boardKey: string, taskTypeId: string, operation?: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<JsonSchema>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getFieldValuesSchema(orgId, boardKey, taskTypeId, operation, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTypesApi.getFieldValuesSchema']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined task types.
         * @param {string} orgId 
         * @param {number} [page] 
         * @param {number} [pageSize] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchTaskTypes(orgId: string, page?: number, pageSize?: number, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTypeIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchTaskTypes(orgId, page, pageSize, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTypesApi.searchTaskTypes']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates or updates a globally-defined task type.
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertTaskTypeRequest} [upsertTaskTypeRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertGlobalTaskType(taskTypeId: string, apiVersion?: string, upsertTaskTypeRequest?: UpsertTaskTypeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTypeConfiguration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertGlobalTaskType(taskTypeId, apiVersion, upsertTaskTypeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTypesApi.upsertGlobalTaskType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates or updates a user-defined task type.
         * @param {string} orgId 
         * @param {string} taskTypeId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpsertTaskTypeRequest} [upsertTaskTypeRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async upsertTaskType(orgId: string, taskTypeId: string, apiVersion?: string, upsertTaskTypeRequest?: UpsertTaskTypeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskTypeConfiguration>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.upsertTaskType(orgId, taskTypeId, apiVersion, upsertTaskTypeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TaskTypesApi.upsertTaskType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * TaskTypesApi - factory interface
 */
export const TaskTypesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = TaskTypesApiFp(configuration)
    return {
        /**
         * 
         * @summary Deletes a globally-defined task type.
         * @param {TaskTypesApiDeleteGlobalTaskTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteGlobalTaskType(requestParameters: TaskTypesApiDeleteGlobalTaskTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteGlobalTaskType(requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Deletes a user-defined task type.
         * @param {TaskTypesApiDeleteTaskTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTaskType(requestParameters: TaskTypesApiDeleteTaskTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteTaskType(requestParameters.orgId, requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a board-aware task type by its id.
         * @param {TaskTypesApiFindBoardTaskTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findBoardTaskType(requestParameters: TaskTypesApiFindBoardTaskTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskType> {
            return localVarFp.findBoardTaskType(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a globally-defined task type by its id.
         * @param {TaskTypesApiFindGlobalTaskTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalTaskType(requestParameters: TaskTypesApiFindGlobalTaskTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskType> {
            return localVarFp.findGlobalTaskType(requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets the configuration for a globally-defined task type by its id.
         * @param {TaskTypesApiFindGlobalTaskTypeConfigurationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findGlobalTaskTypeConfiguration(requestParameters: TaskTypesApiFindGlobalTaskTypeConfigurationRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTypeConfiguration> {
            return localVarFp.findGlobalTaskTypeConfiguration(requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * The schema is a projection of the existing wire contract. Supported `operation` values are `create` (describes `POST /orgs/{orgId}/boards/{boardKey}/tasks`), `update` (describes `POST /orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/update`), and `response` (describes the typed-wrapper shape of `TaskResponse.FieldValues` — each field property is a discriminated object with a `type` const and a `value` typed to the field\'s contract type).
         * @summary Gets a JSON Schema describing the `FieldValues` portion of a request body for the specified board-aware task type.
         * @param {TaskTypesApiGetFieldValuesSchemaRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFieldValuesSchema(requestParameters: TaskTypesApiGetFieldValuesSchemaRequest, options?: RawAxiosRequestConfig): AxiosPromise<JsonSchema> {
            return localVarFp.getFieldValuesSchema(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskTypeId, requestParameters.operation, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a paginated collection of existing globally-defined and user-defined task types.
         * @param {TaskTypesApiSearchTaskTypesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskTypes(requestParameters: TaskTypesApiSearchTaskTypesRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTypeIPaginatedResponse> {
            return localVarFp.searchTaskTypes(requestParameters.orgId, requestParameters.page, requestParameters.pageSize, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates or updates a globally-defined task type.
         * @param {TaskTypesApiUpsertGlobalTaskTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertGlobalTaskType(requestParameters: TaskTypesApiUpsertGlobalTaskTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTypeConfiguration> {
            return localVarFp.upsertGlobalTaskType(requestParameters.taskTypeId, requestParameters.apiVersion, requestParameters.upsertTaskTypeRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates or updates a user-defined task type.
         * @param {TaskTypesApiUpsertTaskTypeRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        upsertTaskType(requestParameters: TaskTypesApiUpsertTaskTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskTypeConfiguration> {
            return localVarFp.upsertTaskType(requestParameters.orgId, requestParameters.taskTypeId, requestParameters.apiVersion, requestParameters.upsertTaskTypeRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for deleteGlobalTaskType operation in TaskTypesApi.
 */
export interface TaskTypesApiDeleteGlobalTaskTypeRequest {
    readonly taskTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for deleteTaskType operation in TaskTypesApi.
 */
export interface TaskTypesApiDeleteTaskTypeRequest {
    readonly orgId: string

    readonly taskTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for findBoardTaskType operation in TaskTypesApi.
 */
export interface TaskTypesApiFindBoardTaskTypeRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for findGlobalTaskType operation in TaskTypesApi.
 */
export interface TaskTypesApiFindGlobalTaskTypeRequest {
    readonly taskTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for findGlobalTaskTypeConfiguration operation in TaskTypesApi.
 */
export interface TaskTypesApiFindGlobalTaskTypeConfigurationRequest {
    readonly taskTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for getFieldValuesSchema operation in TaskTypesApi.
 */
export interface TaskTypesApiGetFieldValuesSchemaRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskTypeId: string

    readonly operation?: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for searchTaskTypes operation in TaskTypesApi.
 */
export interface TaskTypesApiSearchTaskTypesRequest {
    readonly orgId: string

    readonly page?: number

    readonly pageSize?: number

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for upsertGlobalTaskType operation in TaskTypesApi.
 */
export interface TaskTypesApiUpsertGlobalTaskTypeRequest {
    readonly taskTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertTaskTypeRequest?: UpsertTaskTypeRequest
}

/**
 * Request parameters for upsertTaskType operation in TaskTypesApi.
 */
export interface TaskTypesApiUpsertTaskTypeRequest {
    readonly orgId: string

    readonly taskTypeId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly upsertTaskTypeRequest?: UpsertTaskTypeRequest
}

/**
 * TaskTypesApi - object-oriented interface
 */
export class TaskTypesApi extends BaseAPI {
    /**
     * 
     * @summary Deletes a globally-defined task type.
     * @param {TaskTypesApiDeleteGlobalTaskTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteGlobalTaskType(requestParameters: TaskTypesApiDeleteGlobalTaskTypeRequest, options?: RawAxiosRequestConfig) {
        return TaskTypesApiFp(this.configuration).deleteGlobalTaskType(requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Deletes a user-defined task type.
     * @param {TaskTypesApiDeleteTaskTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteTaskType(requestParameters: TaskTypesApiDeleteTaskTypeRequest, options?: RawAxiosRequestConfig) {
        return TaskTypesApiFp(this.configuration).deleteTaskType(requestParameters.orgId, requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a board-aware task type by its id.
     * @param {TaskTypesApiFindBoardTaskTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findBoardTaskType(requestParameters: TaskTypesApiFindBoardTaskTypeRequest, options?: RawAxiosRequestConfig) {
        return TaskTypesApiFp(this.configuration).findBoardTaskType(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a globally-defined task type by its id.
     * @param {TaskTypesApiFindGlobalTaskTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findGlobalTaskType(requestParameters: TaskTypesApiFindGlobalTaskTypeRequest, options?: RawAxiosRequestConfig) {
        return TaskTypesApiFp(this.configuration).findGlobalTaskType(requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets the configuration for a globally-defined task type by its id.
     * @param {TaskTypesApiFindGlobalTaskTypeConfigurationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findGlobalTaskTypeConfiguration(requestParameters: TaskTypesApiFindGlobalTaskTypeConfigurationRequest, options?: RawAxiosRequestConfig) {
        return TaskTypesApiFp(this.configuration).findGlobalTaskTypeConfiguration(requestParameters.taskTypeId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The schema is a projection of the existing wire contract. Supported `operation` values are `create` (describes `POST /orgs/{orgId}/boards/{boardKey}/tasks`), `update` (describes `POST /orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/update`), and `response` (describes the typed-wrapper shape of `TaskResponse.FieldValues` — each field property is a discriminated object with a `type` const and a `value` typed to the field\'s contract type).
     * @summary Gets a JSON Schema describing the `FieldValues` portion of a request body for the specified board-aware task type.
     * @param {TaskTypesApiGetFieldValuesSchemaRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getFieldValuesSchema(requestParameters: TaskTypesApiGetFieldValuesSchemaRequest, options?: RawAxiosRequestConfig) {
        return TaskTypesApiFp(this.configuration).getFieldValuesSchema(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskTypeId, requestParameters.operation, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a paginated collection of existing globally-defined and user-defined task types.
     * @param {TaskTypesApiSearchTaskTypesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchTaskTypes(requestParameters: TaskTypesApiSearchTaskTypesRequest, options?: RawAxiosRequestConfig) {
        return TaskTypesApiFp(this.configuration).searchTaskTypes(requestParameters.orgId, requestParameters.page, requestParameters.pageSize, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates or updates a globally-defined task type.
     * @param {TaskTypesApiUpsertGlobalTaskTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertGlobalTaskType(requestParameters: TaskTypesApiUpsertGlobalTaskTypeRequest, options?: RawAxiosRequestConfig) {
        return TaskTypesApiFp(this.configuration).upsertGlobalTaskType(requestParameters.taskTypeId, requestParameters.apiVersion, requestParameters.upsertTaskTypeRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates or updates a user-defined task type.
     * @param {TaskTypesApiUpsertTaskTypeRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public upsertTaskType(requestParameters: TaskTypesApiUpsertTaskTypeRequest, options?: RawAxiosRequestConfig) {
        return TaskTypesApiFp(this.configuration).upsertTaskType(requestParameters.orgId, requestParameters.taskTypeId, requestParameters.apiVersion, requestParameters.upsertTaskTypeRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * TasksApi - axios parameter creator
 */
export const TasksApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Adds a relation to a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {Relation} [relation] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addTaskRelation: async (orgId: string, boardKey: string, taskId: string, apiVersion?: string, relation?: Relation, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('addTaskRelation', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('addTaskRelation', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('addTaskRelation', 'taskId', taskId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/relations`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(relation, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Archives a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveTask: async (orgId: string, boardKey: string, taskId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('archiveTask', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('archiveTask', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('archiveTask', 'taskId', taskId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/archive`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Assigns a task to a user.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} userId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignTask: async (orgId: string, boardKey: string, taskId: string, userId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('assignTask', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('assignTask', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('assignTask', 'taskId', taskId)
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('assignTask', 'userId', userId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/assign/{userId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)))
                .replace('{userId}', encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Constraints: - Maximum 50 task IDs per request - All tasks must belong to the same board (enforced by the route) - Already archived tasks are treated as no-ops (idempotent) - The entire batch succeeds or fails together — partial archives are not supported - A task with a non-archived child outside this request blocks the entire batch
         * @summary Bulk archives a set of Tasks by their IDs.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {BulkArchiveTasksRequest} bulkArchiveTasksRequest 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkArchiveTasks: async (orgId: string, boardKey: string, bulkArchiveTasksRequest: BulkArchiveTasksRequest, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('bulkArchiveTasks', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('bulkArchiveTasks', 'boardKey', boardKey)
            // verify required parameter 'bulkArchiveTasksRequest' is not null or undefined
            assertParamExists('bulkArchiveTasks', 'bulkArchiveTasksRequest', bulkArchiveTasksRequest)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/bulk-archive`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bulkArchiveTasksRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Each task item must specify its TaskId, ETag, and update details.              Constraints: - Maximum 50 task items per request - All tasks must belong to the same board (enforced by the route) - Each task\'s ETag must match the current version for optimistic concurrency - The entire batch succeeds or fails together — partial updates are not supported
         * @summary Bulk updates multiple tasks, each with their own update properties.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {BulkUpdateIndividualTasksRequest} bulkUpdateIndividualTasksRequest 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkUpdateIndividualTasks: async (orgId: string, boardKey: string, bulkUpdateIndividualTasksRequest: BulkUpdateIndividualTasksRequest, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('bulkUpdateIndividualTasks', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('bulkUpdateIndividualTasks', 'boardKey', boardKey)
            // verify required parameter 'bulkUpdateIndividualTasksRequest' is not null or undefined
            assertParamExists('bulkUpdateIndividualTasks', 'bulkUpdateIndividualTasksRequest', bulkUpdateIndividualTasksRequest)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/bulk-updates`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bulkUpdateIndividualTasksRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * This endpoint provides a simpler alternative to the query-based bulk update for the common use case of updating a specific set of selected tasks.              Constraints: - Maximum 50 task IDs per request - All tasks must belong to the same board - All tasks must belong to the same workspace (enforced by route) - Updates are applied transactionally (all succeed or all fail)
         * @summary Bulk updates multiple tasks by their IDs.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {BulkUpdateTasksRequest} bulkUpdateTasksRequest 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkUpdateTasks: async (orgId: string, boardKey: string, bulkUpdateTasksRequest: BulkUpdateTasksRequest, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('bulkUpdateTasks', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('bulkUpdateTasks', 'boardKey', boardKey)
            // verify required parameter 'bulkUpdateTasksRequest' is not null or undefined
            assertParamExists('bulkUpdateTasks', 'bulkUpdateTasksRequest', bulkUpdateTasksRequest)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/bulk-update`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(bulkUpdateTasksRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Searches for Tasks count in a board.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {BoardTasksCountQuery} [boardTasksCountQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        countBoardTasks: async (orgId: string, boardKey: string, apiVersion?: string, boardTasksCountQuery?: BoardTasksCountQuery, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('countBoardTasks', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('countBoardTasks', 'boardKey', boardKey)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/search/count`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(boardTasksCountQuery, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Searches for Tasks count across boards.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {WorkspaceTasksCountQuery} [workspaceTasksCountQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        countTasks: async (orgId: string, apiVersion?: string, workspaceTasksCountQuery?: WorkspaceTasksCountQuery, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('countTasks', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/tasks/search/count`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(workspaceTasksCountQuery, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Creates a new task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {CreateTaskRequest} [createTaskRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createTask: async (orgId: string, boardKey: string, apiVersion?: string, createTaskRequest?: CreateTaskRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('createTask', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('createTask', 'boardKey', boardKey)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createTaskRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Associates an attachment to a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {CreateTaskAttachmentRequest} [createTaskAttachmentRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createTaskAttachment: async (orgId: string, boardKey: string, taskId: string, apiVersion?: string, createTaskAttachmentRequest?: CreateTaskAttachmentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('createTaskAttachment', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('createTaskAttachment', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('createTaskAttachment', 'taskId', taskId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/attachments`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createTaskAttachmentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Removes an attachment association from a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} attachmentId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTaskAttachment: async (orgId: string, boardKey: string, taskId: string, attachmentId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('deleteTaskAttachment', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('deleteTaskAttachment', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('deleteTaskAttachment', 'taskId', taskId)
            // verify required parameter 'attachmentId' is not null or undefined
            assertParamExists('deleteTaskAttachment', 'attachmentId', attachmentId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/attachments/{attachmentId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)))
                .replace('{attachmentId}', encodeURIComponent(String(attachmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} attachmentId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadTaskAttachment: async (orgId: string, boardKey: string, taskId: string, attachmentId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('downloadTaskAttachment', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('downloadTaskAttachment', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('downloadTaskAttachment', 'taskId', taskId)
            // verify required parameter 'attachmentId' is not null or undefined
            assertParamExists('downloadTaskAttachment', 'attachmentId', attachmentId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/attachments/{attachmentId}/download`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)))
                .replace('{attachmentId}', encodeURIComponent(String(attachmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} attachmentId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadTaskAttachmentLink: async (orgId: string, boardKey: string, taskId: string, attachmentId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('downloadTaskAttachmentLink', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('downloadTaskAttachmentLink', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('downloadTaskAttachmentLink', 'taskId', taskId)
            // verify required parameter 'attachmentId' is not null or undefined
            assertParamExists('downloadTaskAttachmentLink', 'attachmentId', attachmentId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/attachments/{attachmentId}/download-link`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)))
                .replace('{attachmentId}', encodeURIComponent(String(attachmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Ensures a Comm Hub conversation exists for the given task, creating one if absent. Idempotent — safe to call repeatedly. Both Desktop and Mobile FE clients call this before rendering the Comm Hub MFE.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        ensureConversation: async (orgId: string, boardKey: string, taskId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('ensureConversation', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('ensureConversation', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('ensureConversation', 'taskId', taskId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/ensure-conversation`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a task by its ID.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTask: async (orgId: string, boardKey: string, taskId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('getTask', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('getTask', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('getTask', 'taskId', taskId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a task by its ID without requiring a board key. Uses a workspace-scoped query instead of a point read.
         * @param {string} orgId 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTaskInWorkspace: async (orgId: string, taskId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('getTaskInWorkspace', 'orgId', orgId)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('getTaskInWorkspace', 'taskId', taskId)
            const localVarPath = `/orgs/{orgId}/tasks/{taskId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{taskId}', encodeURIComponent(String(taskId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Adds or updates a user as mentioned.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} userId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        mentionUser: async (orgId: string, boardKey: string, taskId: string, userId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('mentionUser', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('mentionUser', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('mentionUser', 'taskId', taskId)
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('mentionUser', 'userId', userId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/users/{userId}/mentioned`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)))
                .replace('{userId}', encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Previews a task\'s field values so that field value resolution can occur without side effects.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {TaskFieldPreviewRequest} [taskFieldPreviewRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewTaskFields: async (orgId: string, boardKey: string, apiVersion?: string, taskFieldPreviewRequest?: TaskFieldPreviewRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('previewTaskFields', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('previewTaskFields', 'boardKey', boardKey)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/fields/preview`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(taskFieldPreviewRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Removes a relation from a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} relatedTaskId 
         * @param {string} [relationType] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        removeTaskRelation: async (orgId: string, boardKey: string, taskId: string, relatedTaskId: string, relationType?: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('removeTaskRelation', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('removeTaskRelation', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('removeTaskRelation', 'taskId', taskId)
            // verify required parameter 'relatedTaskId' is not null or undefined
            assertParamExists('removeTaskRelation', 'relatedTaskId', relatedTaskId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/relations/{relatedTaskId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)))
                .replace('{relatedTaskId}', encodeURIComponent(String(relatedTaskId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (relationType !== undefined) {
                localVarQueryParameter['relationType'] = relationType;
            }

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Renames an attachment associated with a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} attachmentId 
         * @param {string} [apiVersion] The requested API version
         * @param {RenameTaskAttachmentRequest} [renameTaskAttachmentRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        renameTaskAttachment: async (orgId: string, boardKey: string, taskId: string, attachmentId: string, apiVersion?: string, renameTaskAttachmentRequest?: RenameTaskAttachmentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('renameTaskAttachment', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('renameTaskAttachment', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('renameTaskAttachment', 'taskId', taskId)
            // verify required parameter 'attachmentId' is not null or undefined
            assertParamExists('renameTaskAttachment', 'attachmentId', attachmentId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/attachments/{attachmentId}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)))
                .replace('{attachmentId}', encodeURIComponent(String(attachmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(renameTaskAttachmentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Returns a page of roll-up tasks (e.g. `jpm.job` / `jpm.project`) whose member tasks satisfy the request\'s member filters, additionally narrowed by roll-up-level filters. The matching member->roll-up id set is resolved and consumed entirely server-side.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {RollUpMemberFilterQuery} [rollUpMemberFilterQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchRollUpsByMemberFilter: async (orgId: string, boardKey: string, apiVersion?: string, rollUpMemberFilterQuery?: RollUpMemberFilterQuery, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchRollUpsByMemberFilter', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('searchRollUpsByMemberFilter', 'boardKey', boardKey)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/search/roll-ups`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(rollUpMemberFilterQuery, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Gets a paginated collection of existing audit logs for a specified task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchTaskAuditLogRequest} [searchTaskAuditLogRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskAuditLog: async (orgId: string, boardKey: string, taskId: string, apiVersion?: string, searchTaskAuditLogRequest?: SearchTaskAuditLogRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchTaskAuditLog', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('searchTaskAuditLog', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('searchTaskAuditLog', 'taskId', taskId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/audit-log-items/search`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(searchTaskAuditLogRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Searches for distinct values on Tasks for a given FieldId.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchTaskFieldValuesQuery} [searchTaskFieldValuesQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskFieldValues: async (orgId: string, boardKey: string, fieldId: string, apiVersion?: string, searchTaskFieldValuesQuery?: SearchTaskFieldValuesQuery, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchTaskFieldValues', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('searchTaskFieldValues', 'boardKey', boardKey)
            // verify required parameter 'fieldId' is not null or undefined
            assertParamExists('searchTaskFieldValues', 'fieldId', fieldId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/fields/{fieldId}/values/search`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{fieldId}', encodeURIComponent(String(fieldId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(searchTaskFieldValuesQuery, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Searches for distinct values on Tasks for a given FieldId across multiple Boards, unioning each board\'s field definition so board-override-only options are preserved.
         * @param {string} orgId 
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchWorkspaceTaskFieldValuesQuery} [searchWorkspaceTaskFieldValuesQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskFieldValuesAcrossBoards: async (orgId: string, fieldId: string, apiVersion?: string, searchWorkspaceTaskFieldValuesQuery?: SearchWorkspaceTaskFieldValuesQuery, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchTaskFieldValuesAcrossBoards', 'orgId', orgId)
            // verify required parameter 'fieldId' is not null or undefined
            assertParamExists('searchTaskFieldValuesAcrossBoards', 'fieldId', fieldId)
            const localVarPath = `/orgs/{orgId}/tasks/fields/{fieldId}/values/search`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{fieldId}', encodeURIComponent(String(fieldId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(searchWorkspaceTaskFieldValuesQuery, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Searches for tasks in a board.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {BoardTasksQuery} [boardTasksQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTasks: async (orgId: string, boardKey: string, apiVersion?: string, boardTasksQuery?: BoardTasksQuery, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchTasks', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('searchTasks', 'boardKey', boardKey)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/search`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(boardTasksQuery, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Searches for tasks across boards.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {WorkspaceTasksQuery} [workspaceTasksQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTasks2: async (orgId: string, apiVersion?: string, workspaceTasksQuery?: WorkspaceTasksQuery, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchTasks2', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/tasks/search`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(workspaceTasksQuery, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Searches for tasks in a board, paginated by continuation token. Every page but the last contains exactly the requested page size. Pass the returned token back unchanged, with the same filters, sort, and search term, to get the next page; a token presented with a different query is rejected with 400.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {BoardTasksContinuationQuery} [boardTasksContinuationQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTasksByContinuation: async (orgId: string, boardKey: string, apiVersion?: string, boardTasksContinuationQuery?: BoardTasksContinuationQuery, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchTasksByContinuation', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('searchTasksByContinuation', 'boardKey', boardKey)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/search/continuation`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(boardTasksContinuationQuery, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Searches for tasks across boards, paginated by continuation token. Every page but the last contains exactly the requested page size. Pass the returned token back unchanged, with the same filters, boards, sort, and search term, to get the next page; a token presented with a different query is rejected with 400.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {WorkspaceTasksContinuationQuery} [workspaceTasksContinuationQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTasksByContinuation2: async (orgId: string, apiVersion?: string, workspaceTasksContinuationQuery?: WorkspaceTasksContinuationQuery, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchTasksByContinuation2', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/tasks/search/continuation`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(workspaceTasksContinuationQuery, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Returns the top task per supplied group value, where groups are defined by the value of an external-entity field on the task. One result per input group value, in input order, with `Task` = `null` when no document matched.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {BoardTasksTopPerGroupQuery} [boardTasksTopPerGroupQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTopTaskPerGroup: async (orgId: string, boardKey: string, apiVersion?: string, boardTasksTopPerGroupQuery?: BoardTasksTopPerGroupQuery, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchTopTaskPerGroup', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('searchTopTaskPerGroup', 'boardKey', boardKey)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/search/top-per-group`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(boardTasksTopPerGroupQuery, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Unarchives a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unarchiveTask: async (orgId: string, boardKey: string, taskId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('unarchiveTask', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('unarchiveTask', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('unarchiveTask', 'taskId', taskId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/unarchive`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Unassigns a task from a user.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unassignTask: async (orgId: string, boardKey: string, taskId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('unassignTask', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('unassignTask', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('unassignTask', 'taskId', taskId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/unassign`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Updates a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpdateTaskRequest} [updateTaskRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateTask: async (orgId: string, boardKey: string, taskId: string, apiVersion?: string, updateTaskRequest?: UpdateTaskRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('updateTask', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('updateTask', 'boardKey', boardKey)
            // verify required parameter 'taskId' is not null or undefined
            assertParamExists('updateTask', 'taskId', taskId)
            const localVarPath = `/orgs/{orgId}/boards/{boardKey}/tasks/{taskId}/update`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)))
                .replace('{taskId}', encodeURIComponent(String(taskId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateTaskRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Page through `POST /orgs/{orgId}/boards/{boardKey}/tasks/search` to collect the task IDs, then send them to `POST /orgs/{orgId}/boards/{boardKey}/tasks/bulk-update` in batches of up to 50. Unlike this endpoint, each batch is applied all-or-nothing and returns the updated tasks instead of a count and warnings, so exclude tasks that should not change (for example, archived ones) in the search.
         * @summary Deprecated: updates every task that matches the filters.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {UpdateTasksRequest} [updateTasksRequest] 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        updateTasks: async (orgId: string, boardKey: string, apiVersion?: string, updateTasksRequest?: UpdateTasksRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('updateTasks', 'orgId', orgId)
            // verify required parameter 'boardKey' is not null or undefined
            assertParamExists('updateTasks', 'boardKey', boardKey)
            const localVarPath = `/internal/orgs/{orgId}/boards/{boardKey}/tasks/update`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{boardKey}', encodeURIComponent(String(boardKey)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateTasksRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * TasksApi - functional programming interface
 */
export const TasksApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = TasksApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Adds a relation to a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {Relation} [relation] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addTaskRelation(orgId: string, boardKey: string, taskId: string, apiVersion?: string, relation?: Relation, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addTaskRelation(orgId, boardKey, taskId, apiVersion, relation, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.addTaskRelation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Archives a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async archiveTask(orgId: string, boardKey: string, taskId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.archiveTask(orgId, boardKey, taskId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.archiveTask']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Assigns a task to a user.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} userId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async assignTask(orgId: string, boardKey: string, taskId: string, userId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.assignTask(orgId, boardKey, taskId, userId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.assignTask']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Constraints: - Maximum 50 task IDs per request - All tasks must belong to the same board (enforced by the route) - Already archived tasks are treated as no-ops (idempotent) - The entire batch succeeds or fails together — partial archives are not supported - A task with a non-archived child outside this request blocks the entire batch
         * @summary Bulk archives a set of Tasks by their IDs.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {BulkArchiveTasksRequest} bulkArchiveTasksRequest 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bulkArchiveTasks(orgId: string, boardKey: string, bulkArchiveTasksRequest: BulkArchiveTasksRequest, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bulkArchiveTasks(orgId, boardKey, bulkArchiveTasksRequest, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.bulkArchiveTasks']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Each task item must specify its TaskId, ETag, and update details.              Constraints: - Maximum 50 task items per request - All tasks must belong to the same board (enforced by the route) - Each task\'s ETag must match the current version for optimistic concurrency - The entire batch succeeds or fails together — partial updates are not supported
         * @summary Bulk updates multiple tasks, each with their own update properties.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {BulkUpdateIndividualTasksRequest} bulkUpdateIndividualTasksRequest 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bulkUpdateIndividualTasks(orgId: string, boardKey: string, bulkUpdateIndividualTasksRequest: BulkUpdateIndividualTasksRequest, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<TaskResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bulkUpdateIndividualTasks(orgId, boardKey, bulkUpdateIndividualTasksRequest, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.bulkUpdateIndividualTasks']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * This endpoint provides a simpler alternative to the query-based bulk update for the common use case of updating a specific set of selected tasks.              Constraints: - Maximum 50 task IDs per request - All tasks must belong to the same board - All tasks must belong to the same workspace (enforced by route) - Updates are applied transactionally (all succeed or all fail)
         * @summary Bulk updates multiple tasks by their IDs.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {BulkUpdateTasksRequest} bulkUpdateTasksRequest 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async bulkUpdateTasks(orgId: string, boardKey: string, bulkUpdateTasksRequest: BulkUpdateTasksRequest, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<TaskResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.bulkUpdateTasks(orgId, boardKey, bulkUpdateTasksRequest, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.bulkUpdateTasks']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Searches for Tasks count in a board.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {BoardTasksCountQuery} [boardTasksCountQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async countBoardTasks(orgId: string, boardKey: string, apiVersion?: string, boardTasksCountQuery?: BoardTasksCountQuery, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskCountResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.countBoardTasks(orgId, boardKey, apiVersion, boardTasksCountQuery, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.countBoardTasks']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Searches for Tasks count across boards.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {WorkspaceTasksCountQuery} [workspaceTasksCountQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async countTasks(orgId: string, apiVersion?: string, workspaceTasksCountQuery?: WorkspaceTasksCountQuery, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskCountResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.countTasks(orgId, apiVersion, workspaceTasksCountQuery, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.countTasks']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Creates a new task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {CreateTaskRequest} [createTaskRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createTask(orgId: string, boardKey: string, apiVersion?: string, createTaskRequest?: CreateTaskRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createTask(orgId, boardKey, apiVersion, createTaskRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.createTask']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Associates an attachment to a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {CreateTaskAttachmentRequest} [createTaskAttachmentRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createTaskAttachment(orgId: string, boardKey: string, taskId: string, apiVersion?: string, createTaskAttachmentRequest?: CreateTaskAttachmentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskAttachment>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createTaskAttachment(orgId, boardKey, taskId, apiVersion, createTaskAttachmentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.createTaskAttachment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Removes an attachment association from a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} attachmentId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteTaskAttachment(orgId: string, boardKey: string, taskId: string, attachmentId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteTaskAttachment(orgId, boardKey, taskId, attachmentId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.deleteTaskAttachment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} attachmentId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadTaskAttachment(orgId: string, boardKey: string, taskId: string, attachmentId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadTaskAttachment(orgId, boardKey, taskId, attachmentId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.downloadTaskAttachment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} attachmentId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadTaskAttachmentLink(orgId: string, boardKey: string, taskId: string, attachmentId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AttachmentDownload>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadTaskAttachmentLink(orgId, boardKey, taskId, attachmentId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.downloadTaskAttachmentLink']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Ensures a Comm Hub conversation exists for the given task, creating one if absent. Idempotent — safe to call repeatedly. Both Desktop and Mobile FE clients call this before rendering the Comm Hub MFE.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async ensureConversation(orgId: string, boardKey: string, taskId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<EnsureConversationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.ensureConversation(orgId, boardKey, taskId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.ensureConversation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get a task by its ID.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTask(orgId: string, boardKey: string, taskId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTask(orgId, boardKey, taskId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.getTask']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get a task by its ID without requiring a board key. Uses a workspace-scoped query instead of a point read.
         * @param {string} orgId 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTaskInWorkspace(orgId: string, taskId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTaskInWorkspace(orgId, taskId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.getTaskInWorkspace']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Adds or updates a user as mentioned.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} userId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async mentionUser(orgId: string, boardKey: string, taskId: string, userId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.mentionUser(orgId, boardKey, taskId, userId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.mentionUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Previews a task\'s field values so that field value resolution can occur without side effects.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {TaskFieldPreviewRequest} [taskFieldPreviewRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewTaskFields(orgId: string, boardKey: string, apiVersion?: string, taskFieldPreviewRequest?: TaskFieldPreviewRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskFieldPreviewResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewTaskFields(orgId, boardKey, apiVersion, taskFieldPreviewRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.previewTaskFields']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Removes a relation from a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} relatedTaskId 
         * @param {string} [relationType] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async removeTaskRelation(orgId: string, boardKey: string, taskId: string, relatedTaskId: string, relationType?: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.removeTaskRelation(orgId, boardKey, taskId, relatedTaskId, relationType, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.removeTaskRelation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Renames an attachment associated with a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} attachmentId 
         * @param {string} [apiVersion] The requested API version
         * @param {RenameTaskAttachmentRequest} [renameTaskAttachmentRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async renameTaskAttachment(orgId: string, boardKey: string, taskId: string, attachmentId: string, apiVersion?: string, renameTaskAttachmentRequest?: RenameTaskAttachmentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskAttachment>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.renameTaskAttachment(orgId, boardKey, taskId, attachmentId, apiVersion, renameTaskAttachmentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.renameTaskAttachment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Returns a page of roll-up tasks (e.g. `jpm.job` / `jpm.project`) whose member tasks satisfy the request\'s member filters, additionally narrowed by roll-up-level filters. The matching member->roll-up id set is resolved and consumed entirely server-side.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {RollUpMemberFilterQuery} [rollUpMemberFilterQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchRollUpsByMemberFilter(orgId: string, boardKey: string, apiVersion?: string, rollUpMemberFilterQuery?: RollUpMemberFilterQuery, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponseIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchRollUpsByMemberFilter(orgId, boardKey, apiVersion, rollUpMemberFilterQuery, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.searchRollUpsByMemberFilter']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Gets a paginated collection of existing audit logs for a specified task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchTaskAuditLogRequest} [searchTaskAuditLogRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchTaskAuditLog(orgId: string, boardKey: string, taskId: string, apiVersion?: string, searchTaskAuditLogRequest?: SearchTaskAuditLogRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskAuditLogResponseIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchTaskAuditLog(orgId, boardKey, taskId, apiVersion, searchTaskAuditLogRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.searchTaskAuditLog']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Searches for distinct values on Tasks for a given FieldId.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchTaskFieldValuesQuery} [searchTaskFieldValuesQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchTaskFieldValues(orgId: string, boardKey: string, fieldId: string, apiVersion?: string, searchTaskFieldValuesQuery?: SearchTaskFieldValuesQuery, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SearchTaskFieldValuesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchTaskFieldValues(orgId, boardKey, fieldId, apiVersion, searchTaskFieldValuesQuery, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.searchTaskFieldValues']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Searches for distinct values on Tasks for a given FieldId across multiple Boards, unioning each board\'s field definition so board-override-only options are preserved.
         * @param {string} orgId 
         * @param {string} fieldId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchWorkspaceTaskFieldValuesQuery} [searchWorkspaceTaskFieldValuesQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchTaskFieldValuesAcrossBoards(orgId: string, fieldId: string, apiVersion?: string, searchWorkspaceTaskFieldValuesQuery?: SearchWorkspaceTaskFieldValuesQuery, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SearchTaskFieldValuesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchTaskFieldValuesAcrossBoards(orgId, fieldId, apiVersion, searchWorkspaceTaskFieldValuesQuery, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.searchTaskFieldValuesAcrossBoards']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Searches for tasks in a board.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {BoardTasksQuery} [boardTasksQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchTasks(orgId: string, boardKey: string, apiVersion?: string, boardTasksQuery?: BoardTasksQuery, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponseIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchTasks(orgId, boardKey, apiVersion, boardTasksQuery, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.searchTasks']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Searches for tasks across boards.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {WorkspaceTasksQuery} [workspaceTasksQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchTasks2(orgId: string, apiVersion?: string, workspaceTasksQuery?: WorkspaceTasksQuery, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponseIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchTasks2(orgId, apiVersion, workspaceTasksQuery, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.searchTasks2']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Searches for tasks in a board, paginated by continuation token. Every page but the last contains exactly the requested page size. Pass the returned token back unchanged, with the same filters, sort, and search term, to get the next page; a token presented with a different query is rejected with 400.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {BoardTasksContinuationQuery} [boardTasksContinuationQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchTasksByContinuation(orgId: string, boardKey: string, apiVersion?: string, boardTasksContinuationQuery?: BoardTasksContinuationQuery, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponseIContinuationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchTasksByContinuation(orgId, boardKey, apiVersion, boardTasksContinuationQuery, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.searchTasksByContinuation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Searches for tasks across boards, paginated by continuation token. Every page but the last contains exactly the requested page size. Pass the returned token back unchanged, with the same filters, boards, sort, and search term, to get the next page; a token presented with a different query is rejected with 400.
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {WorkspaceTasksContinuationQuery} [workspaceTasksContinuationQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchTasksByContinuation2(orgId: string, apiVersion?: string, workspaceTasksContinuationQuery?: WorkspaceTasksContinuationQuery, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponseIContinuationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchTasksByContinuation2(orgId, apiVersion, workspaceTasksContinuationQuery, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.searchTasksByContinuation2']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Returns the top task per supplied group value, where groups are defined by the value of an external-entity field on the task. One result per input group value, in input order, with `Task` = `null` when no document matched.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {BoardTasksTopPerGroupQuery} [boardTasksTopPerGroupQuery] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchTopTaskPerGroup(orgId: string, boardKey: string, apiVersion?: string, boardTasksTopPerGroupQuery?: BoardTasksTopPerGroupQuery, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GroupedTasksResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchTopTaskPerGroup(orgId, boardKey, apiVersion, boardTasksTopPerGroupQuery, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.searchTopTaskPerGroup']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Unarchives a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unarchiveTask(orgId: string, boardKey: string, taskId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unarchiveTask(orgId, boardKey, taskId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.unarchiveTask']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Unassigns a task from a user.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unassignTask(orgId: string, boardKey: string, taskId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unassignTask(orgId, boardKey, taskId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.unassignTask']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Updates a task.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} taskId 
         * @param {string} [apiVersion] The requested API version
         * @param {UpdateTaskRequest} [updateTaskRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateTask(orgId: string, boardKey: string, taskId: string, apiVersion?: string, updateTaskRequest?: UpdateTaskRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TaskResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateTask(orgId, boardKey, taskId, apiVersion, updateTaskRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.updateTask']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Page through `POST /orgs/{orgId}/boards/{boardKey}/tasks/search` to collect the task IDs, then send them to `POST /orgs/{orgId}/boards/{boardKey}/tasks/bulk-update` in batches of up to 50. Unlike this endpoint, each batch is applied all-or-nothing and returns the updated tasks instead of a count and warnings, so exclude tasks that should not change (for example, archived ones) in the search.
         * @summary Deprecated: updates every task that matches the filters.
         * @param {string} orgId 
         * @param {string} boardKey 
         * @param {string} [apiVersion] The requested API version
         * @param {UpdateTasksRequest} [updateTasksRequest] 
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        async updateTasks(orgId: string, boardKey: string, apiVersion?: string, updateTasksRequest?: UpdateTasksRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TasksUpdateResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateTasks(orgId, boardKey, apiVersion, updateTasksRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TasksApi.updateTasks']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * TasksApi - factory interface
 */
export const TasksApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = TasksApiFp(configuration)
    return {
        /**
         * 
         * @summary Adds a relation to a task.
         * @param {TasksApiAddTaskRelationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addTaskRelation(requestParameters: TasksApiAddTaskRelationRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponse> {
            return localVarFp.addTaskRelation(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, requestParameters.relation, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Archives a task.
         * @param {TasksApiArchiveTaskRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveTask(requestParameters: TasksApiArchiveTaskRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponse> {
            return localVarFp.archiveTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Assigns a task to a user.
         * @param {TasksApiAssignTaskRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        assignTask(requestParameters: TasksApiAssignTaskRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponse> {
            return localVarFp.assignTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.userId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * Constraints: - Maximum 50 task IDs per request - All tasks must belong to the same board (enforced by the route) - Already archived tasks are treated as no-ops (idempotent) - The entire batch succeeds or fails together — partial archives are not supported - A task with a non-archived child outside this request blocks the entire batch
         * @summary Bulk archives a set of Tasks by their IDs.
         * @param {TasksApiBulkArchiveTasksRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkArchiveTasks(requestParameters: TasksApiBulkArchiveTasksRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.bulkArchiveTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.bulkArchiveTasksRequest, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * Each task item must specify its TaskId, ETag, and update details.              Constraints: - Maximum 50 task items per request - All tasks must belong to the same board (enforced by the route) - Each task\'s ETag must match the current version for optimistic concurrency - The entire batch succeeds or fails together — partial updates are not supported
         * @summary Bulk updates multiple tasks, each with their own update properties.
         * @param {TasksApiBulkUpdateIndividualTasksRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkUpdateIndividualTasks(requestParameters: TasksApiBulkUpdateIndividualTasksRequest, options?: RawAxiosRequestConfig): AxiosPromise<Array<TaskResponse>> {
            return localVarFp.bulkUpdateIndividualTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.bulkUpdateIndividualTasksRequest, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * This endpoint provides a simpler alternative to the query-based bulk update for the common use case of updating a specific set of selected tasks.              Constraints: - Maximum 50 task IDs per request - All tasks must belong to the same board - All tasks must belong to the same workspace (enforced by route) - Updates are applied transactionally (all succeed or all fail)
         * @summary Bulk updates multiple tasks by their IDs.
         * @param {TasksApiBulkUpdateTasksRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        bulkUpdateTasks(requestParameters: TasksApiBulkUpdateTasksRequest, options?: RawAxiosRequestConfig): AxiosPromise<Array<TaskResponse>> {
            return localVarFp.bulkUpdateTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.bulkUpdateTasksRequest, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Searches for Tasks count in a board.
         * @param {TasksApiCountBoardTasksRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        countBoardTasks(requestParameters: TasksApiCountBoardTasksRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskCountResponse> {
            return localVarFp.countBoardTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.boardTasksCountQuery, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Searches for Tasks count across boards.
         * @param {TasksApiCountTasksRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        countTasks(requestParameters: TasksApiCountTasksRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskCountResponse> {
            return localVarFp.countTasks(requestParameters.orgId, requestParameters.apiVersion, requestParameters.workspaceTasksCountQuery, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Creates a new task.
         * @param {TasksApiCreateTaskRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createTask(requestParameters: TasksApiCreateTaskRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponse> {
            return localVarFp.createTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.createTaskRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Associates an attachment to a task.
         * @param {TasksApiCreateTaskAttachmentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createTaskAttachment(requestParameters: TasksApiCreateTaskAttachmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskAttachment> {
            return localVarFp.createTaskAttachment(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, requestParameters.createTaskAttachmentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Removes an attachment association from a task.
         * @param {TasksApiDeleteTaskAttachmentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTaskAttachment(requestParameters: TasksApiDeleteTaskAttachmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteTaskAttachment(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.attachmentId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {TasksApiDownloadTaskAttachmentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadTaskAttachment(requestParameters: TasksApiDownloadTaskAttachmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.downloadTaskAttachment(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.attachmentId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {TasksApiDownloadTaskAttachmentLinkRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadTaskAttachmentLink(requestParameters: TasksApiDownloadTaskAttachmentLinkRequest, options?: RawAxiosRequestConfig): AxiosPromise<AttachmentDownload> {
            return localVarFp.downloadTaskAttachmentLink(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.attachmentId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Ensures a Comm Hub conversation exists for the given task, creating one if absent. Idempotent — safe to call repeatedly. Both Desktop and Mobile FE clients call this before rendering the Comm Hub MFE.
         * @param {TasksApiEnsureConversationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        ensureConversation(requestParameters: TasksApiEnsureConversationRequest, options?: RawAxiosRequestConfig): AxiosPromise<EnsureConversationResponse> {
            return localVarFp.ensureConversation(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a task by its ID.
         * @param {TasksApiGetTaskRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTask(requestParameters: TasksApiGetTaskRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponse> {
            return localVarFp.getTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a task by its ID without requiring a board key. Uses a workspace-scoped query instead of a point read.
         * @param {TasksApiGetTaskInWorkspaceRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTaskInWorkspace(requestParameters: TasksApiGetTaskInWorkspaceRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponse> {
            return localVarFp.getTaskInWorkspace(requestParameters.orgId, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Adds or updates a user as mentioned.
         * @param {TasksApiMentionUserRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        mentionUser(requestParameters: TasksApiMentionUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponse> {
            return localVarFp.mentionUser(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.userId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Previews a task\'s field values so that field value resolution can occur without side effects.
         * @param {TasksApiPreviewTaskFieldsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewTaskFields(requestParameters: TasksApiPreviewTaskFieldsRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskFieldPreviewResponse> {
            return localVarFp.previewTaskFields(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.taskFieldPreviewRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Removes a relation from a task.
         * @param {TasksApiRemoveTaskRelationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        removeTaskRelation(requestParameters: TasksApiRemoveTaskRelationRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponse> {
            return localVarFp.removeTaskRelation(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.relatedTaskId, requestParameters.relationType, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Renames an attachment associated with a task.
         * @param {TasksApiRenameTaskAttachmentRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        renameTaskAttachment(requestParameters: TasksApiRenameTaskAttachmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskAttachment> {
            return localVarFp.renameTaskAttachment(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.attachmentId, requestParameters.apiVersion, requestParameters.renameTaskAttachmentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Returns a page of roll-up tasks (e.g. `jpm.job` / `jpm.project`) whose member tasks satisfy the request\'s member filters, additionally narrowed by roll-up-level filters. The matching member->roll-up id set is resolved and consumed entirely server-side.
         * @param {TasksApiSearchRollUpsByMemberFilterRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchRollUpsByMemberFilter(requestParameters: TasksApiSearchRollUpsByMemberFilterRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponseIPaginatedResponse> {
            return localVarFp.searchRollUpsByMemberFilter(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.rollUpMemberFilterQuery, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Gets a paginated collection of existing audit logs for a specified task.
         * @param {TasksApiSearchTaskAuditLogRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskAuditLog(requestParameters: TasksApiSearchTaskAuditLogRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskAuditLogResponseIPaginatedResponse> {
            return localVarFp.searchTaskAuditLog(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, requestParameters.searchTaskAuditLogRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Searches for distinct values on Tasks for a given FieldId.
         * @param {TasksApiSearchTaskFieldValuesRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskFieldValues(requestParameters: TasksApiSearchTaskFieldValuesRequest, options?: RawAxiosRequestConfig): AxiosPromise<SearchTaskFieldValuesResponse> {
            return localVarFp.searchTaskFieldValues(requestParameters.orgId, requestParameters.boardKey, requestParameters.fieldId, requestParameters.apiVersion, requestParameters.searchTaskFieldValuesQuery, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Searches for distinct values on Tasks for a given FieldId across multiple Boards, unioning each board\'s field definition so board-override-only options are preserved.
         * @param {TasksApiSearchTaskFieldValuesAcrossBoardsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTaskFieldValuesAcrossBoards(requestParameters: TasksApiSearchTaskFieldValuesAcrossBoardsRequest, options?: RawAxiosRequestConfig): AxiosPromise<SearchTaskFieldValuesResponse> {
            return localVarFp.searchTaskFieldValuesAcrossBoards(requestParameters.orgId, requestParameters.fieldId, requestParameters.apiVersion, requestParameters.searchWorkspaceTaskFieldValuesQuery, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Searches for tasks in a board.
         * @param {TasksApiSearchTasksRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTasks(requestParameters: TasksApiSearchTasksRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponseIPaginatedResponse> {
            return localVarFp.searchTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.boardTasksQuery, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Searches for tasks across boards.
         * @param {TasksApiSearchTasks2Request} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTasks2(requestParameters: TasksApiSearchTasks2Request, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponseIPaginatedResponse> {
            return localVarFp.searchTasks2(requestParameters.orgId, requestParameters.apiVersion, requestParameters.workspaceTasksQuery, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Searches for tasks in a board, paginated by continuation token. Every page but the last contains exactly the requested page size. Pass the returned token back unchanged, with the same filters, sort, and search term, to get the next page; a token presented with a different query is rejected with 400.
         * @param {TasksApiSearchTasksByContinuationRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTasksByContinuation(requestParameters: TasksApiSearchTasksByContinuationRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponseIContinuationResponse> {
            return localVarFp.searchTasksByContinuation(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.boardTasksContinuationQuery, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Searches for tasks across boards, paginated by continuation token. Every page but the last contains exactly the requested page size. Pass the returned token back unchanged, with the same filters, boards, sort, and search term, to get the next page; a token presented with a different query is rejected with 400.
         * @param {TasksApiSearchTasksByContinuation2Request} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTasksByContinuation2(requestParameters: TasksApiSearchTasksByContinuation2Request, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponseIContinuationResponse> {
            return localVarFp.searchTasksByContinuation2(requestParameters.orgId, requestParameters.apiVersion, requestParameters.workspaceTasksContinuationQuery, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Returns the top task per supplied group value, where groups are defined by the value of an external-entity field on the task. One result per input group value, in input order, with `Task` = `null` when no document matched.
         * @param {TasksApiSearchTopTaskPerGroupRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchTopTaskPerGroup(requestParameters: TasksApiSearchTopTaskPerGroupRequest, options?: RawAxiosRequestConfig): AxiosPromise<GroupedTasksResponse> {
            return localVarFp.searchTopTaskPerGroup(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.boardTasksTopPerGroupQuery, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Unarchives a task.
         * @param {TasksApiUnarchiveTaskRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unarchiveTask(requestParameters: TasksApiUnarchiveTaskRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponse> {
            return localVarFp.unarchiveTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Unassigns a task from a user.
         * @param {TasksApiUnassignTaskRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unassignTask(requestParameters: TasksApiUnassignTaskRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponse> {
            return localVarFp.unassignTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Updates a task.
         * @param {TasksApiUpdateTaskRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateTask(requestParameters: TasksApiUpdateTaskRequest, options?: RawAxiosRequestConfig): AxiosPromise<TaskResponse> {
            return localVarFp.updateTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, requestParameters.updateTaskRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Page through `POST /orgs/{orgId}/boards/{boardKey}/tasks/search` to collect the task IDs, then send them to `POST /orgs/{orgId}/boards/{boardKey}/tasks/bulk-update` in batches of up to 50. Unlike this endpoint, each batch is applied all-or-nothing and returns the updated tasks instead of a count and warnings, so exclude tasks that should not change (for example, archived ones) in the search.
         * @summary Deprecated: updates every task that matches the filters.
         * @param {TasksApiUpdateTasksRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        updateTasks(requestParameters: TasksApiUpdateTasksRequest, options?: RawAxiosRequestConfig): AxiosPromise<TasksUpdateResponse> {
            return localVarFp.updateTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.updateTasksRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for addTaskRelation operation in TasksApi.
 */
export interface TasksApiAddTaskRelationRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly relation?: Relation
}

/**
 * Request parameters for archiveTask operation in TasksApi.
 */
export interface TasksApiArchiveTaskRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for assignTask operation in TasksApi.
 */
export interface TasksApiAssignTaskRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    readonly userId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for bulkArchiveTasks operation in TasksApi.
 */
export interface TasksApiBulkArchiveTasksRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly bulkArchiveTasksRequest: BulkArchiveTasksRequest

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for bulkUpdateIndividualTasks operation in TasksApi.
 */
export interface TasksApiBulkUpdateIndividualTasksRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly bulkUpdateIndividualTasksRequest: BulkUpdateIndividualTasksRequest

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for bulkUpdateTasks operation in TasksApi.
 */
export interface TasksApiBulkUpdateTasksRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly bulkUpdateTasksRequest: BulkUpdateTasksRequest

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for countBoardTasks operation in TasksApi.
 */
export interface TasksApiCountBoardTasksRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly boardTasksCountQuery?: BoardTasksCountQuery
}

/**
 * Request parameters for countTasks operation in TasksApi.
 */
export interface TasksApiCountTasksRequest {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly workspaceTasksCountQuery?: WorkspaceTasksCountQuery
}

/**
 * Request parameters for createTask operation in TasksApi.
 */
export interface TasksApiCreateTaskRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly createTaskRequest?: CreateTaskRequest
}

/**
 * Request parameters for createTaskAttachment operation in TasksApi.
 */
export interface TasksApiCreateTaskAttachmentRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly createTaskAttachmentRequest?: CreateTaskAttachmentRequest
}

/**
 * Request parameters for deleteTaskAttachment operation in TasksApi.
 */
export interface TasksApiDeleteTaskAttachmentRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    readonly attachmentId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for downloadTaskAttachment operation in TasksApi.
 */
export interface TasksApiDownloadTaskAttachmentRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    readonly attachmentId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for downloadTaskAttachmentLink operation in TasksApi.
 */
export interface TasksApiDownloadTaskAttachmentLinkRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    readonly attachmentId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for ensureConversation operation in TasksApi.
 */
export interface TasksApiEnsureConversationRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for getTask operation in TasksApi.
 */
export interface TasksApiGetTaskRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for getTaskInWorkspace operation in TasksApi.
 */
export interface TasksApiGetTaskInWorkspaceRequest {
    readonly orgId: string

    readonly taskId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for mentionUser operation in TasksApi.
 */
export interface TasksApiMentionUserRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    readonly userId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for previewTaskFields operation in TasksApi.
 */
export interface TasksApiPreviewTaskFieldsRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly taskFieldPreviewRequest?: TaskFieldPreviewRequest
}

/**
 * Request parameters for removeTaskRelation operation in TasksApi.
 */
export interface TasksApiRemoveTaskRelationRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    readonly relatedTaskId: string

    readonly relationType?: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for renameTaskAttachment operation in TasksApi.
 */
export interface TasksApiRenameTaskAttachmentRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    readonly attachmentId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly renameTaskAttachmentRequest?: RenameTaskAttachmentRequest
}

/**
 * Request parameters for searchRollUpsByMemberFilter operation in TasksApi.
 */
export interface TasksApiSearchRollUpsByMemberFilterRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly rollUpMemberFilterQuery?: RollUpMemberFilterQuery
}

/**
 * Request parameters for searchTaskAuditLog operation in TasksApi.
 */
export interface TasksApiSearchTaskAuditLogRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly searchTaskAuditLogRequest?: SearchTaskAuditLogRequest
}

/**
 * Request parameters for searchTaskFieldValues operation in TasksApi.
 */
export interface TasksApiSearchTaskFieldValuesRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly fieldId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly searchTaskFieldValuesQuery?: SearchTaskFieldValuesQuery
}

/**
 * Request parameters for searchTaskFieldValuesAcrossBoards operation in TasksApi.
 */
export interface TasksApiSearchTaskFieldValuesAcrossBoardsRequest {
    readonly orgId: string

    readonly fieldId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly searchWorkspaceTaskFieldValuesQuery?: SearchWorkspaceTaskFieldValuesQuery
}

/**
 * Request parameters for searchTasks operation in TasksApi.
 */
export interface TasksApiSearchTasksRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly boardTasksQuery?: BoardTasksQuery
}

/**
 * Request parameters for searchTasks2 operation in TasksApi.
 */
export interface TasksApiSearchTasks2Request {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly workspaceTasksQuery?: WorkspaceTasksQuery
}

/**
 * Request parameters for searchTasksByContinuation operation in TasksApi.
 */
export interface TasksApiSearchTasksByContinuationRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly boardTasksContinuationQuery?: BoardTasksContinuationQuery
}

/**
 * Request parameters for searchTasksByContinuation2 operation in TasksApi.
 */
export interface TasksApiSearchTasksByContinuation2Request {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly workspaceTasksContinuationQuery?: WorkspaceTasksContinuationQuery
}

/**
 * Request parameters for searchTopTaskPerGroup operation in TasksApi.
 */
export interface TasksApiSearchTopTaskPerGroupRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly boardTasksTopPerGroupQuery?: BoardTasksTopPerGroupQuery
}

/**
 * Request parameters for unarchiveTask operation in TasksApi.
 */
export interface TasksApiUnarchiveTaskRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for unassignTask operation in TasksApi.
 */
export interface TasksApiUnassignTaskRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for updateTask operation in TasksApi.
 */
export interface TasksApiUpdateTaskRequest {
    readonly orgId: string

    readonly boardKey: string

    readonly taskId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly updateTaskRequest?: UpdateTaskRequest
}

/**
 * Request parameters for updateTasks operation in TasksApi.
 */
export interface TasksApiUpdateTasksRequest {
    readonly orgId: string

    readonly boardKey: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly updateTasksRequest?: UpdateTasksRequest
}

/**
 * TasksApi - object-oriented interface
 */
export class TasksApi extends BaseAPI {
    /**
     * 
     * @summary Adds a relation to a task.
     * @param {TasksApiAddTaskRelationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addTaskRelation(requestParameters: TasksApiAddTaskRelationRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).addTaskRelation(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, requestParameters.relation, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Archives a task.
     * @param {TasksApiArchiveTaskRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public archiveTask(requestParameters: TasksApiArchiveTaskRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).archiveTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Assigns a task to a user.
     * @param {TasksApiAssignTaskRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public assignTask(requestParameters: TasksApiAssignTaskRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).assignTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.userId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Constraints: - Maximum 50 task IDs per request - All tasks must belong to the same board (enforced by the route) - Already archived tasks are treated as no-ops (idempotent) - The entire batch succeeds or fails together — partial archives are not supported - A task with a non-archived child outside this request blocks the entire batch
     * @summary Bulk archives a set of Tasks by their IDs.
     * @param {TasksApiBulkArchiveTasksRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public bulkArchiveTasks(requestParameters: TasksApiBulkArchiveTasksRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).bulkArchiveTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.bulkArchiveTasksRequest, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Each task item must specify its TaskId, ETag, and update details.              Constraints: - Maximum 50 task items per request - All tasks must belong to the same board (enforced by the route) - Each task\'s ETag must match the current version for optimistic concurrency - The entire batch succeeds or fails together — partial updates are not supported
     * @summary Bulk updates multiple tasks, each with their own update properties.
     * @param {TasksApiBulkUpdateIndividualTasksRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public bulkUpdateIndividualTasks(requestParameters: TasksApiBulkUpdateIndividualTasksRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).bulkUpdateIndividualTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.bulkUpdateIndividualTasksRequest, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * This endpoint provides a simpler alternative to the query-based bulk update for the common use case of updating a specific set of selected tasks.              Constraints: - Maximum 50 task IDs per request - All tasks must belong to the same board - All tasks must belong to the same workspace (enforced by route) - Updates are applied transactionally (all succeed or all fail)
     * @summary Bulk updates multiple tasks by their IDs.
     * @param {TasksApiBulkUpdateTasksRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public bulkUpdateTasks(requestParameters: TasksApiBulkUpdateTasksRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).bulkUpdateTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.bulkUpdateTasksRequest, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Searches for Tasks count in a board.
     * @param {TasksApiCountBoardTasksRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public countBoardTasks(requestParameters: TasksApiCountBoardTasksRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).countBoardTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.boardTasksCountQuery, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Searches for Tasks count across boards.
     * @param {TasksApiCountTasksRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public countTasks(requestParameters: TasksApiCountTasksRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).countTasks(requestParameters.orgId, requestParameters.apiVersion, requestParameters.workspaceTasksCountQuery, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Creates a new task.
     * @param {TasksApiCreateTaskRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createTask(requestParameters: TasksApiCreateTaskRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).createTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.createTaskRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Associates an attachment to a task.
     * @param {TasksApiCreateTaskAttachmentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createTaskAttachment(requestParameters: TasksApiCreateTaskAttachmentRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).createTaskAttachment(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, requestParameters.createTaskAttachmentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Removes an attachment association from a task.
     * @param {TasksApiDeleteTaskAttachmentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteTaskAttachment(requestParameters: TasksApiDeleteTaskAttachmentRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).deleteTaskAttachment(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.attachmentId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {TasksApiDownloadTaskAttachmentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadTaskAttachment(requestParameters: TasksApiDownloadTaskAttachmentRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).downloadTaskAttachment(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.attachmentId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {TasksApiDownloadTaskAttachmentLinkRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadTaskAttachmentLink(requestParameters: TasksApiDownloadTaskAttachmentLinkRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).downloadTaskAttachmentLink(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.attachmentId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Ensures a Comm Hub conversation exists for the given task, creating one if absent. Idempotent — safe to call repeatedly. Both Desktop and Mobile FE clients call this before rendering the Comm Hub MFE.
     * @param {TasksApiEnsureConversationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public ensureConversation(requestParameters: TasksApiEnsureConversationRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).ensureConversation(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get a task by its ID.
     * @param {TasksApiGetTaskRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getTask(requestParameters: TasksApiGetTaskRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).getTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get a task by its ID without requiring a board key. Uses a workspace-scoped query instead of a point read.
     * @param {TasksApiGetTaskInWorkspaceRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getTaskInWorkspace(requestParameters: TasksApiGetTaskInWorkspaceRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).getTaskInWorkspace(requestParameters.orgId, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Adds or updates a user as mentioned.
     * @param {TasksApiMentionUserRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public mentionUser(requestParameters: TasksApiMentionUserRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).mentionUser(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.userId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Previews a task\'s field values so that field value resolution can occur without side effects.
     * @param {TasksApiPreviewTaskFieldsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewTaskFields(requestParameters: TasksApiPreviewTaskFieldsRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).previewTaskFields(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.taskFieldPreviewRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Removes a relation from a task.
     * @param {TasksApiRemoveTaskRelationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public removeTaskRelation(requestParameters: TasksApiRemoveTaskRelationRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).removeTaskRelation(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.relatedTaskId, requestParameters.relationType, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Renames an attachment associated with a task.
     * @param {TasksApiRenameTaskAttachmentRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public renameTaskAttachment(requestParameters: TasksApiRenameTaskAttachmentRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).renameTaskAttachment(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.attachmentId, requestParameters.apiVersion, requestParameters.renameTaskAttachmentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Returns a page of roll-up tasks (e.g. `jpm.job` / `jpm.project`) whose member tasks satisfy the request\'s member filters, additionally narrowed by roll-up-level filters. The matching member->roll-up id set is resolved and consumed entirely server-side.
     * @param {TasksApiSearchRollUpsByMemberFilterRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchRollUpsByMemberFilter(requestParameters: TasksApiSearchRollUpsByMemberFilterRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).searchRollUpsByMemberFilter(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.rollUpMemberFilterQuery, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Gets a paginated collection of existing audit logs for a specified task.
     * @param {TasksApiSearchTaskAuditLogRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchTaskAuditLog(requestParameters: TasksApiSearchTaskAuditLogRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).searchTaskAuditLog(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, requestParameters.searchTaskAuditLogRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Searches for distinct values on Tasks for a given FieldId.
     * @param {TasksApiSearchTaskFieldValuesRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchTaskFieldValues(requestParameters: TasksApiSearchTaskFieldValuesRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).searchTaskFieldValues(requestParameters.orgId, requestParameters.boardKey, requestParameters.fieldId, requestParameters.apiVersion, requestParameters.searchTaskFieldValuesQuery, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Searches for distinct values on Tasks for a given FieldId across multiple Boards, unioning each board\'s field definition so board-override-only options are preserved.
     * @param {TasksApiSearchTaskFieldValuesAcrossBoardsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchTaskFieldValuesAcrossBoards(requestParameters: TasksApiSearchTaskFieldValuesAcrossBoardsRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).searchTaskFieldValuesAcrossBoards(requestParameters.orgId, requestParameters.fieldId, requestParameters.apiVersion, requestParameters.searchWorkspaceTaskFieldValuesQuery, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Searches for tasks in a board.
     * @param {TasksApiSearchTasksRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchTasks(requestParameters: TasksApiSearchTasksRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).searchTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.boardTasksQuery, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Searches for tasks across boards.
     * @param {TasksApiSearchTasks2Request} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchTasks2(requestParameters: TasksApiSearchTasks2Request, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).searchTasks2(requestParameters.orgId, requestParameters.apiVersion, requestParameters.workspaceTasksQuery, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Searches for tasks in a board, paginated by continuation token. Every page but the last contains exactly the requested page size. Pass the returned token back unchanged, with the same filters, sort, and search term, to get the next page; a token presented with a different query is rejected with 400.
     * @param {TasksApiSearchTasksByContinuationRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchTasksByContinuation(requestParameters: TasksApiSearchTasksByContinuationRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).searchTasksByContinuation(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.boardTasksContinuationQuery, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Searches for tasks across boards, paginated by continuation token. Every page but the last contains exactly the requested page size. Pass the returned token back unchanged, with the same filters, boards, sort, and search term, to get the next page; a token presented with a different query is rejected with 400.
     * @param {TasksApiSearchTasksByContinuation2Request} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchTasksByContinuation2(requestParameters: TasksApiSearchTasksByContinuation2Request, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).searchTasksByContinuation2(requestParameters.orgId, requestParameters.apiVersion, requestParameters.workspaceTasksContinuationQuery, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Returns the top task per supplied group value, where groups are defined by the value of an external-entity field on the task. One result per input group value, in input order, with `Task` = `null` when no document matched.
     * @param {TasksApiSearchTopTaskPerGroupRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchTopTaskPerGroup(requestParameters: TasksApiSearchTopTaskPerGroupRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).searchTopTaskPerGroup(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.boardTasksTopPerGroupQuery, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Unarchives a task.
     * @param {TasksApiUnarchiveTaskRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unarchiveTask(requestParameters: TasksApiUnarchiveTaskRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).unarchiveTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Unassigns a task from a user.
     * @param {TasksApiUnassignTaskRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unassignTask(requestParameters: TasksApiUnassignTaskRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).unassignTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Updates a task.
     * @param {TasksApiUpdateTaskRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateTask(requestParameters: TasksApiUpdateTaskRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).updateTask(requestParameters.orgId, requestParameters.boardKey, requestParameters.taskId, requestParameters.apiVersion, requestParameters.updateTaskRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Page through `POST /orgs/{orgId}/boards/{boardKey}/tasks/search` to collect the task IDs, then send them to `POST /orgs/{orgId}/boards/{boardKey}/tasks/bulk-update` in batches of up to 50. Unlike this endpoint, each batch is applied all-or-nothing and returns the updated tasks instead of a count and warnings, so exclude tasks that should not change (for example, archived ones) in the search.
     * @summary Deprecated: updates every task that matches the filters.
     * @param {TasksApiUpdateTasksRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @deprecated
     * @throws {RequiredError}
     */
    public updateTasks(requestParameters: TasksApiUpdateTasksRequest, options?: RawAxiosRequestConfig) {
        return TasksApiFp(this.configuration).updateTasks(requestParameters.orgId, requestParameters.boardKey, requestParameters.apiVersion, requestParameters.updateTasksRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * UsersApi - axios parameter creator
 */
export const UsersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {FindUsersRequest} [findUsersRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findUsers: async (orgId: string, apiVersion?: string, findUsersRequest?: FindUsersRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('findUsers', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/users/fetch`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(findUsersRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentUser: async (orgId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('getCurrentUser', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/users/me`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} key 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrgSetting: async (orgId: string, key: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('getOrgSetting', 'orgId', orgId)
            // verify required parameter 'key' is not null or undefined
            assertParamExists('getOrgSetting', 'key', key)
            const localVarPath = `/orgs/{orgId}/settings/{key}`
                .replace('{orgId}', encodeURIComponent(String(orgId)))
                .replace('{key}', encodeURIComponent(String(key)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} [keys] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrgSettings: async (orgId: string, keys?: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('getOrgSettings', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/settings`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (keys !== undefined) {
                localVarQueryParameter['keys'] = keys;
            }

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchUsersRequest} [searchUsersRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchUsers: async (orgId: string, apiVersion?: string, searchUsersRequest?: SearchUsersRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'orgId' is not null or undefined
            assertParamExists('searchUsers', 'orgId', orgId)
            const localVarPath = `/orgs/{orgId}/users/search`
                .replace('{orgId}', encodeURIComponent(String(orgId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(searchUsersRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * UsersApi - functional programming interface
 */
export const UsersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = UsersApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {FindUsersRequest} [findUsersRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async findUsers(orgId: string, apiVersion?: string, findUsersRequest?: FindUsersRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserIPaginatedResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.findUsers(orgId, apiVersion, findUsersRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.findUsers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCurrentUser(orgId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<User>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCurrentUser(orgId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getCurrentUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} key 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOrgSetting(orgId: string, key: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrgSettingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOrgSetting(orgId, key, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getOrgSetting']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} [keys] 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOrgSettings(orgId: string, keys?: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrgSettingsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOrgSettings(orgId, keys, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getOrgSettings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} orgId 
         * @param {string} [apiVersion] The requested API version
         * @param {SearchUsersRequest} [searchUsersRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchUsers(orgId: string, apiVersion?: string, searchUsersRequest?: SearchUsersRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserIContinuationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchUsers(orgId, apiVersion, searchUsersRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.searchUsers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * UsersApi - factory interface
 */
export const UsersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = UsersApiFp(configuration)
    return {
        /**
         * 
         * @param {UsersApiFindUsersRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        findUsers(requestParameters: UsersApiFindUsersRequest, options?: RawAxiosRequestConfig): AxiosPromise<UserIPaginatedResponse> {
            return localVarFp.findUsers(requestParameters.orgId, requestParameters.apiVersion, requestParameters.findUsersRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {UsersApiGetCurrentUserRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentUser(requestParameters: UsersApiGetCurrentUserRequest, options?: RawAxiosRequestConfig): AxiosPromise<User> {
            return localVarFp.getCurrentUser(requestParameters.orgId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {UsersApiGetOrgSettingRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrgSetting(requestParameters: UsersApiGetOrgSettingRequest, options?: RawAxiosRequestConfig): AxiosPromise<OrgSettingResponse> {
            return localVarFp.getOrgSetting(requestParameters.orgId, requestParameters.key, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {UsersApiGetOrgSettingsRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrgSettings(requestParameters: UsersApiGetOrgSettingsRequest, options?: RawAxiosRequestConfig): AxiosPromise<OrgSettingsResponse> {
            return localVarFp.getOrgSettings(requestParameters.orgId, requestParameters.keys, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {UsersApiSearchUsersRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchUsers(requestParameters: UsersApiSearchUsersRequest, options?: RawAxiosRequestConfig): AxiosPromise<UserIContinuationResponse> {
            return localVarFp.searchUsers(requestParameters.orgId, requestParameters.apiVersion, requestParameters.searchUsersRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for findUsers operation in UsersApi.
 */
export interface UsersApiFindUsersRequest {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly findUsersRequest?: FindUsersRequest
}

/**
 * Request parameters for getCurrentUser operation in UsersApi.
 */
export interface UsersApiGetCurrentUserRequest {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for getOrgSetting operation in UsersApi.
 */
export interface UsersApiGetOrgSettingRequest {
    readonly orgId: string

    readonly key: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for getOrgSettings operation in UsersApi.
 */
export interface UsersApiGetOrgSettingsRequest {
    readonly orgId: string

    readonly keys?: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * Request parameters for searchUsers operation in UsersApi.
 */
export interface UsersApiSearchUsersRequest {
    readonly orgId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly searchUsersRequest?: SearchUsersRequest
}

/**
 * UsersApi - object-oriented interface
 */
export class UsersApi extends BaseAPI {
    /**
     * 
     * @param {UsersApiFindUsersRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public findUsers(requestParameters: UsersApiFindUsersRequest, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).findUsers(requestParameters.orgId, requestParameters.apiVersion, requestParameters.findUsersRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {UsersApiGetCurrentUserRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCurrentUser(requestParameters: UsersApiGetCurrentUserRequest, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getCurrentUser(requestParameters.orgId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {UsersApiGetOrgSettingRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOrgSetting(requestParameters: UsersApiGetOrgSettingRequest, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getOrgSetting(requestParameters.orgId, requestParameters.key, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {UsersApiGetOrgSettingsRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOrgSettings(requestParameters: UsersApiGetOrgSettingsRequest, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getOrgSettings(requestParameters.orgId, requestParameters.keys, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {UsersApiSearchUsersRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchUsers(requestParameters: UsersApiSearchUsersRequest, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).searchUsers(requestParameters.orgId, requestParameters.apiVersion, requestParameters.searchUsersRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * WorkspacesApi - axios parameter creator
 */
export const WorkspacesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @param {string} [apiVersion] The requested API version
         * @param {CreateWorkspaceRequest} [createWorkspaceRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createWorkspace: async (apiVersion?: string, createWorkspaceRequest?: CreateWorkspaceRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/workspaces`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createWorkspaceRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @param {string} workspaceId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getWorkSpace: async (workspaceId: string, apiVersion?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'workspaceId' is not null or undefined
            assertParamExists('getWorkSpace', 'workspaceId', workspaceId)
            const localVarPath = `/workspaces/{workspaceId}`
                .replace('{workspaceId}', encodeURIComponent(String(workspaceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication Bearer required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (apiVersion !== undefined) {
                localVarQueryParameter['api-version'] = apiVersion;
            }

            localVarHeaderParameter['Accept'] = 'text/plain,application/json,text/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * WorkspacesApi - functional programming interface
 */
export const WorkspacesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = WorkspacesApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @param {string} [apiVersion] The requested API version
         * @param {CreateWorkspaceRequest} [createWorkspaceRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createWorkspace(apiVersion?: string, createWorkspaceRequest?: CreateWorkspaceRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkspaceResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createWorkspace(apiVersion, createWorkspaceRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkspacesApi.createWorkspace']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @param {string} workspaceId 
         * @param {string} [apiVersion] The requested API version
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getWorkSpace(workspaceId: string, apiVersion?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkspaceResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getWorkSpace(workspaceId, apiVersion, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkspacesApi.getWorkSpace']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * WorkspacesApi - factory interface
 */
export const WorkspacesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = WorkspacesApiFp(configuration)
    return {
        /**
         * 
         * @param {WorkspacesApiCreateWorkspaceRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createWorkspace(requestParameters: WorkspacesApiCreateWorkspaceRequest = {}, options?: RawAxiosRequestConfig): AxiosPromise<WorkspaceResponse> {
            return localVarFp.createWorkspace(requestParameters.apiVersion, requestParameters.createWorkspaceRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @param {WorkspacesApiGetWorkSpaceRequest} requestParameters Request parameters.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getWorkSpace(requestParameters: WorkspacesApiGetWorkSpaceRequest, options?: RawAxiosRequestConfig): AxiosPromise<WorkspaceResponse> {
            return localVarFp.getWorkSpace(requestParameters.workspaceId, requestParameters.apiVersion, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * Request parameters for createWorkspace operation in WorkspacesApi.
 */
export interface WorkspacesApiCreateWorkspaceRequest {
    /**
     * The requested API version
     */
    readonly apiVersion?: string

    readonly createWorkspaceRequest?: CreateWorkspaceRequest
}

/**
 * Request parameters for getWorkSpace operation in WorkspacesApi.
 */
export interface WorkspacesApiGetWorkSpaceRequest {
    readonly workspaceId: string

    /**
     * The requested API version
     */
    readonly apiVersion?: string
}

/**
 * WorkspacesApi - object-oriented interface
 */
export class WorkspacesApi extends BaseAPI {
    /**
     * 
     * @param {WorkspacesApiCreateWorkspaceRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createWorkspace(requestParameters: WorkspacesApiCreateWorkspaceRequest = {}, options?: RawAxiosRequestConfig) {
        return WorkspacesApiFp(this.configuration).createWorkspace(requestParameters.apiVersion, requestParameters.createWorkspaceRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @param {WorkspacesApiGetWorkSpaceRequest} requestParameters Request parameters.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getWorkSpace(requestParameters: WorkspacesApiGetWorkSpaceRequest, options?: RawAxiosRequestConfig) {
        return WorkspacesApiFp(this.configuration).getWorkSpace(requestParameters.workspaceId, requestParameters.apiVersion, options).then((request) => request(this.axios, this.basePath));
    }
}



